import { db, ref, set, get, update } from "./firebase";
import { onDisconnect as fbOnDisconnect } from "firebase/database";
import { useState, useEffect, useRef, useCallback } from "react";

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const NUMS = [1, 2, 3, 4, 5, 6];

// ── Cricket tips shown on innings break screen (helps Google index real content) ──
const CRICKET_TIPS = [
  { title: "Did you know?", body: "In hand cricket, showing the same number as your opponent gets you out just like being caught in real cricket!" },
  { title: "Strategy tip", body: "Mix high and low numbers to keep your opponent guessing. Predictable patterns are easy to read and exploit." },
  { title: "Batting tip", body: "When chasing a small target, play aggressively early. Don't let wickets pile up while scoring slowly." },
  { title: "Bowling tip", body: "Watch your opponent's pattern. If they keep showing 6, wait for it then match it for the wicket!" },
  { title: "Did you know?", body: "Hand cricket originated in school playgrounds across India as a way to enjoy cricket without equipment." },
  { title: "Tactics", body: "In the power play, doubling runs means even a single 6 scores 12. Pick your shots carefully to maximise it." },
  { title: "Mental game", body: "Confidence matters players who hesitate on their pick tend to fall into predictable rhythms faster." },
  { title: "Did you know?", body: "The Last Stand mode with only one wicket tests your nerves like a real Test match tail-end batting situation." },
];
const EMOJI = { 1: "☝️", 2: "✌️", 3: "🤟", 4: "🖖", 5: "🖐️", 6: "👊" };
const TURN_SECS = 60;
const POLL_MS = 1000;
const MY_PID = Math.random().toString(36).slice(2, 10);
const genCode = () => [...Array(4)].map(() => "ABCDEFGHJKLMNPQRSTUVWXYZ"[Math.floor(Math.random() * 23)]).join("");

// Public web URL for share links (Android WebView uses appassets origin — not openable by friends)
const PUBLIC_WEB_URL = "https://hand-cricket-striveon-games.vercel.app";
const isAndroidApp = () => typeof window.AndroidBridge !== "undefined";
const getShareBaseUrl = () => isAndroidApp() ? PUBLIC_WEB_URL : `${window.location.origin}${window.location.pathname}`.replace(/\/$/, "");
const copyText = async (text, successMsg) => {
  if (isAndroidApp()) { window.AndroidBridge.copyText(text); return; }
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      alert(successMsg || "Copied!");
      return;
    }
  } catch (_) { /* fall through */ }
  alert(text);
};
const shareJoinLink = async (type, code, extraLine) => {
  const param = type === "team" ? "teamjoin" : "join";
  const joinUrl = `${getShareBaseUrl()}?${param}=${code}`;
  const title = type === "team" ? "Team Cricket" : "Hand Cricket";
  const msg = extraLine
    ? `${extraLine}\nTap to join: ${joinUrl}`
    : `🏏 Join my game!\nRoom code: ${code}\nTap to join: ${joinUrl}`;
  if (isAndroidApp()) { window.AndroidBridge.shareText(msg); return; }
  try {
    if (navigator.share) {
      await navigator.share({ title, text: msg, url: joinUrl });
      return;
    }
  } catch (e) {
    if (e?.name === "AbortError") return;
  }
  await copyText(msg, `Link copied! Room code: ${code}`);
};
const shareScorecardBlob = (blob, filename, title) => {
  if (isAndroidApp()) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = String(reader.result).split(",")[1];
      window.AndroidBridge.shareImage(base64, filename);
    };
    reader.readAsDataURL(blob);
    return;
  }
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    navigator.share({ files: [file], title }).catch(() => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    });
  } else {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
};

// Fair toss: mix CSPRNG with a per-match salt so each START is independent (A=RED, B=BLUE).
const tossHash32 = (s) => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
};
const coinFlipAB = (salt) => {
  const nonce = Date.now() + "|" + (globalThis.performance?.now?.() ?? 0) + "|" + Math.random().toString(36).slice(2);
  const localMix = tossHash32(`${salt}|${nonce}`);
  try {
    if (globalThis?.crypto?.getRandomValues) {
      const buf = new Uint32Array(2);
      globalThis.crypto.getRandomValues(buf);
      const bit = (buf[0] ^ buf[1] ^ localMix) & 1;
      return bit === 0 ? "A" : "B";
    }
  } catch (e) { }
  return (localMix ^ Math.floor(Math.random() * 0xffffffff)) & 1 ? "A" : "B";
};

const CLR = {
  bgDeep: "#0f2d1a", bgMid: "#163d24",
  textPrimary: "#ffffff", textSecond: "rgba(255,255,255,0.65)",
  textDim: "rgba(255,255,255,0.38)", textFaint: "rgba(255,255,255,0.18)",
  gold: "#f5c518", green: "#4cff6e", coral: "#ff5f57",
  sky: "#38d4f5", orange: "#ff8c42", cream: "#fff8e1",
  win: "#4cff6e", lose: "#ff5f57", draw: "#f5c518",
};

// Team match colors — always RED vs BLUE
const TM = {
  red: { id: "red", label: "RED", emoji: "🔴", color: "#ef4444", bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.45)" },
  blue: { id: "blue", label: "BLUE", emoji: "🔵", color: "#3b82f6", bg: "rgba(59,130,246,0.15)", border: "rgba(59,130,246,0.45)" },
};

const CHALLENGES = [
  { id: "classic", name: "Classic", icon: "🏏", desc: "3 overs · 3 wickets · Full match", color: CLR.gold, overs: 3, wickets: 3 },
  { id: "blitz", name: "T6 Blitz", icon: "⚡", desc: "1 over only — score big or bust", color: CLR.coral, overs: 1, wickets: 2, tag: "FAST" },
  { id: "power", name: "Power Play", icon: "💥", desc: "1st over all runs doubled!", color: CLR.sky, overs: 3, wickets: 3, tag: "BONUS", powerover: 1 },
  { id: "stand", name: "Last Stand", icon: "💀", desc: "5 overs · ONE wicket only. Survive.", color: CLR.orange, overs: 5, wickets: 1, tag: "HARD" },
  { id: "chase", name: "Chase!", icon: "🎯", desc: "Batting only · Beat 45 in 2 overs", color: CLR.green, overs: 2, wickets: 3, tag: "BAT", chaseTarget: 45, battingOnly: true },
  { id: "blind", name: "Blind", icon: "🙈", desc: "Ball history hidden — pure instinct!", color: "#c084fc", overs: 3, wickets: 3, tag: "WILD", blind: true },
];

// ═══════════════════════════════════════════════════════
// PURE GAME LOGIC
// ═══════════════════════════════════════════════════════
function initGS(batFirst) {
  return { p1score: 0, p2score: 0, wkts: 0, balls: 0, inn: 1, target: null, hist: [], inn1hist: [], batFirst, phase: "picking", p1pick: null, p2pick: null, winner: null, ver: 0 };
}
function processBall(gs, ch, p1num, p2num) {
  // FIREBASE FIX: Firebase returns null for empty arrays — normalize to []
  gs = {
    ...gs,
    hist: Array.isArray(gs.hist) ? gs.hist : [],
    inn1hist: Array.isArray(gs.inn1hist) ? gs.inn1hist : [],
  };

  const MAX_BALLS = ch.overs * 6, MAX_WKTS = ch.wickets;
  const batterTeam = gs.inn === 1 ? (gs.batFirst ? "p1" : "p2") : (gs.batFirst ? "p2" : "p1");
  const bnum = batterTeam === "p1" ? p1num : p2num;
  const isOut = bnum === (batterTeam === "p1" ? p2num : p1num);
  const isPow = !!(ch.powerover && Math.floor(gs.balls / 6) < ch.powerover);
  const rawNum = isOut ? 0 : bnum;
  const runs = isPow && !isOut ? rawNum * 2 : rawNum;
  const next = {
    ...gs, p1pick: null, p2pick: null, balls: gs.balls + 1, wkts: isOut ? gs.wkts + 1 : gs.wkts,
    p1score: batterTeam === "p1" && !isOut ? gs.p1score + runs : gs.p1score,
    p2score: batterTeam === "p2" && !isOut ? gs.p2score + runs : gs.p2score,
    hist: [...gs.hist, { isOut, rawNum, runs, batterTeam }], inn1hist: gs.inn1hist, ver: gs.ver + 1
  };
  const chaserScore = batterTeam === "p1" ? next.p1score : next.p2score;
  const chased = gs.inn === 2 && !isOut && gs.target !== null && chaserScore >= gs.target;
  const inningsOver = next.wkts >= MAX_WKTS || next.balls >= MAX_BALLS;
  if (ch.battingOnly) {
    if (next.p1score >= (ch.chaseTarget || 45)) { next.phase = "done"; next.winner = "p1"; }
    else if (inningsOver) { next.phase = "done"; next.winner = "p2"; }
    else next.phase = "picking";
  } else if (chased) { next.phase = "done"; next.winner = batterTeam; }
  else if (inningsOver) {
    if (gs.inn === 1) {
      const ss = batterTeam === "p1" ? next.p1score : next.p2score;
      next.inn1hist = [...next.hist]; next.target = ss + 1; next.inn = 2; next.balls = 0; next.wkts = 0; next.hist = []; next.phase = "break";
    } else {
      next.winner = next.p1score > next.p2score ? "p1" : next.p2score > next.p1score ? "p2" : "draw";
      next.phase = "done";
    }
  } else next.phase = "picking";
  return { next, isOut, rawNum, runs, batterTeam, isPow };
}

// ═══════════════════════════════════════════════════════
// AUDIO
// ═══════════════════════════════════════════════════════
let _ac = null;
const getAC = () => { try { if (!_ac) _ac = new (window.AudioContext || window.webkitAudioContext)(); return _ac; } catch (e) { return null; } };
const tone = (f, d, t = "sine", v = 0.18, dl = 0) => {
  try {
    const c = getAC(); if (!c) return;
    // H1 FIX: iOS Safari suspends AudioContext even after a gesture — must resume explicitly
    if (c.state === "suspended") c.resume();
    const o = c.createOscillator(), g = c.createGain(); o.connect(g); g.connect(c.destination); o.frequency.value = f; o.type = t; const s = c.currentTime + dl; g.gain.setValueAtTime(v, s); g.gain.exponentialRampToValueAtTime(0.001, s + d); o.start(s); o.stop(s + d);
  } catch (e) { }
};
const SFX = {
  tick: () => tone(1100, 0.03, "square", 0.05),
  warn: () => { tone(900, 0.06, "square", 0.12); tone(700, 0.06, "square", 0.08, 0.08); },
  timeout: () => [300, 250, 200, 160].forEach((f, i) => tone(f, 0.25, "sawtooth", 0.22, i * 0.12)),
  pick: () => tone(880, 0.05, "square", 0.1),
  hit: () => { tone(220, 0.08, "square", 0.25); tone(360, 0.06, "sine", 0.12, 0.05); },
  four: () => [440, 550, 660].forEach((f, i) => tone(f, 0.18, "sine", 0.2, i * 0.09)),
  six: () => [440, 550, 660, 880, 1100].forEach((f, i) => tone(f, 0.22, "sine", 0.22, i * 0.07)),
  out: () => [400, 300, 220, 160].forEach((f, i) => tone(f, 0.22, "sine", 0.2, i * 0.1)),
  wicket: () => [600, 500, 380, 260].forEach((f, i) => tone(f, 0.18, "sine", 0.2, i * 0.08)),
  win: () => [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.28, "sine", 0.22, i * 0.1)),
  lose: () => [400, 340, 270, 200].forEach((f, i) => tone(f, 0.25, "sine", 0.2, i * 0.12)),
  join: () => [600, 800, 1000].forEach((f, i) => tone(f, 0.15, "sine", 0.15, i * 0.08)),
};

// ═══════════════════════════════════════════════════════
// STORAGE
// ═══════════════════════════════════════════════════════
const updateRoom = async (code, fields) => { try { await update(ref(db, `rooms/${code}`), fields); return true; } catch (e) { return false; } };
const saveRoom = async (code, data) => {
  try { await set(ref(db, `rooms/${code}`), data); return true; }
  catch (e) { return false; }
};
const loadRoom = async (code) => {
  try {
    const snap = await get(ref(db, `rooms/${code}`));
    if (!snap.exists()) return null;
    const data = snap.val();
    // FIREBASE NULL FIX: Firebase drops any field set to null on save.
    // They come back as undefined, which breaks all !== null checks.
    // Restore every nullable field explicitly.
    if (data.gs) {
      data.gs.hist = Array.isArray(data.gs.hist) ? data.gs.hist : [];
      data.gs.inn1hist = Array.isArray(data.gs.inn1hist) ? data.gs.inn1hist : [];
      data.gs.p1pick = data.gs.p1pick ?? null;
      data.gs.p2pick = data.gs.p2pick ?? null;
      data.gs.target = data.gs.target ?? null;
      data.gs.winner = data.gs.winner ?? null;
    }
    // Also restore room-level picks
    data.p1pick = data.p1pick ?? null;
    data.p2pick = data.p2pick ?? null;
    data.p2id = data.p2id ?? null;
    data.p2name = data.p2name ?? "";
    data.lastBall = data.lastBall ?? null;
    data.reactions = Array.isArray(data.reactions) ? data.reactions : [];
    data.forfeit = data.forfeit ?? null;
    return data;
  } catch (e) { return null; }
};
// ═══════════════════════════════════════════════════════
// TEAM MATCH — STORAGE
// ═══════════════════════════════════════════════════════
const saveTeam = async (code, data) => { try { await set(ref(db, `tr/${code}`), data); return true; } catch (e) { return false; } };
// Atomic partial update — only writes specific fields, never overwrites other player's pick
const updateTeam = async (code, fields) => { try { await update(ref(db, `tr/${code}`), fields); return true; } catch (e) { return false; } };
const loadTeam = async (code) => {
  try {
    const snap = await get(ref(db, `tr/${code}`));
    if (!snap.exists()) return null;
    const d = snap.val();
    if (d.gs) {
      d.gs.hist = Array.isArray(d.gs.hist) ? d.gs.hist : [];
      d.gs.inn1hist = Array.isArray(d.gs.inn1hist) ? d.gs.inn1hist : [];
      ['batterPick', 'bowlerPick', 'target', 'winner'].forEach(k => { d.gs[k] = d.gs[k] ?? null; });
    }
    d.players = d.players ?? {};
    d.teamA = Array.isArray(d.teamA) ? d.teamA : [];
    d.teamB = Array.isArray(d.teamB) ? d.teamB : [];
    d.lastBall = d.lastBall ?? null;
    return d;
  } catch (e) { return null; }
};

// ═══════════════════════════════════════════════════════
// TEAM MATCH — GAME LOGIC
// ═══════════════════════════════════════════════════════
function initTGS(overs, nA, nB) {
  return {
    inn: 1, battingTeam: "A", batterIdx: 0, bowlerIdx: 0,
    teamAScore: 0, teamBScore: 0, wkts: 0, maxWkts: Math.max(nA, nB),
    balls: 0, ballsInOver: 0, overs, target: null,
    phase: "picking", batterPick: null, bowlerPick: null,
    winner: null, hist: [], inn1hist: [], ver: 0,
    startedAt: Date.now() // grace period for heartbeat detection
  };
}
function processTBall(gs, teamA, teamB) {
  const isOut = gs.batterPick === gs.bowlerPick;
  const runs = isOut ? 0 : gs.batterPick;
  const battArr = gs.battingTeam === "A" ? teamA : teamB;
  const bowlArr = gs.battingTeam === "A" ? teamB : teamA;
  const newBIO = (gs.ballsInOver + 1) % 6;
  const next = {
    ...gs,
    batterPick: null, bowlerPick: null,
    balls: gs.balls + 1, ballsInOver: newBIO,
    wkts: isOut ? gs.wkts + 1 : gs.wkts,
    batterIdx: isOut ? gs.batterIdx + 1 : gs.batterIdx,
    bowlerIdx: newBIO === 0 ? gs.bowlerIdx + 1 : gs.bowlerIdx,
    teamAScore: gs.battingTeam === "A" && !isOut ? gs.teamAScore + runs : gs.teamAScore,
    teamBScore: gs.battingTeam === "B" && !isOut ? gs.teamBScore + runs : gs.teamBScore,
    hist: [...(Array.isArray(gs.hist) ? gs.hist : []), {
      isOut, rawNum: isOut ? 0 : gs.batterPick, runs,
      batterPid: battArr[gs.batterIdx % battArr.length],
      bowlerPid: bowlArr[gs.bowlerIdx % bowlArr.length]
    }],
    inn1hist: Array.isArray(gs.inn1hist) ? gs.inn1hist : [],
    ver: gs.ver + 1
  };
  // chased win
  if (gs.inn === 2 && gs.target !== null && !isOut) {
    const cs = gs.battingTeam === "A" ? next.teamAScore : next.teamBScore;
    if (cs >= gs.target) { next.phase = "done"; next.winner = gs.battingTeam; return { next, isOut, rawNum: isOut ? 0 : gs.batterPick, runs }; }
  }
  const over = next.wkts >= gs.maxWkts || next.balls >= gs.overs * 6;
  if (over) {
    if (gs.inn === 1) {
      const sc = gs.battingTeam === "A" ? next.teamAScore : next.teamBScore;
      next.target = sc + 1; next.inn = 2;
      next.battingTeam = gs.battingTeam === "A" ? "B" : "A";
      next.batterIdx = 0; next.bowlerIdx = 0; next.wkts = 0; next.balls = 0; next.ballsInOver = 0;
      next.inn1hist = [...next.hist]; next.hist = []; next.phase = "break";
    } else {
      next.winner = next.teamAScore > next.teamBScore ? "A" : next.teamBScore > next.teamAScore ? "B" : "draw";
      next.phase = "done";
    }
  } else next.phase = "picking";
  return { next, isOut, rawNum: isOut ? 0 : gs.batterPick, runs };
}

// ═══════════════════════════════════════════════════════
// BACKGROUND
// ═══════════════════════════════════════════════════════
function CricketBg({ accent = CLR.gold }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg,#1a4a28 0%,#163d24 35%,#0f2d1a 70%,#122b1c 100%)" }} />
      <div style={{ position: "absolute", top: "-5%", left: "-5%", width: "45%", height: "45%", background: "radial-gradient(ellipse,rgba(255,245,180,0.18) 0%,transparent 65%)", filter: "blur(40px)" }} />
      <div style={{ position: "absolute", top: "-5%", right: "-5%", width: "45%", height: "45%", background: "radial-gradient(ellipse,rgba(255,245,180,0.16) 0%,transparent 65%)", filter: "blur(40px)" }} />
      <div style={{ position: "absolute", bottom: "-10%", right: "10%", width: "40%", height: "40%", background: `radial-gradient(ellipse,${accent}22 0%,transparent 60%)`, filter: "blur(50px)", transition: "background 0.5s" }} />
      {[...Array(8)].map((_, i) => <div key={i} style={{ position: "absolute", left: 0, right: 0, top: `${i * 14}%`, height: "7%", background: i % 2 === 0 ? "rgba(255,255,255,0.018)" : "rgba(0,0,0,0.04)" }} />)}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,transparent,${accent}60,transparent)`, transition: "background 0.5s" }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// TIMER RING
// ═══════════════════════════════════════════════════════
function TimerRing({ time, total, color = CLR.gold, size = 54 }) {
  const R = size / 2 - 4, C = 2 * Math.PI * R, dash = (time / total) * C;
  const tc = time <= 10 ? CLR.coral : time <= 20 ? CLR.orange : color;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3.5" />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={tc} strokeWidth="3.5"
          strokeDasharray={`${Math.max(0, dash)} ${C}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.9s linear,stroke 0.4s", filter: `drop-shadow(0 0 4px ${tc})` }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0 }}>
        <span style={{ color: tc, fontSize: time <= 10 ? 17 : 14, fontWeight: 900, fontFamily: "'Bebas Neue',serif", lineHeight: 1, animation: time <= 10 ? "tPulse 0.45s infinite" : "none" }}>{time}</span>
        <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 5, letterSpacing: 1 }}>SEC</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PARTICLES
// ═══════════════════════════════════════════════════════
function Burst({ type }) {
  const cols = type === "six" ? [CLR.gold, "#fff", CLR.green, CLR.sky, CLR.orange] : type === "four" ? [CLR.sky, CLR.gold, "#fff"] : [CLR.coral, "#ff8888", "#ffaaaa"];
  const n = type === "six" ? 24 : type === "four" ? 14 : 10;
  const pts = Array.from({ length: n }, (_, i) => ({ id: i, x: 28 + Math.random() * 44, dx: (Math.random() - 0.5) * 200, dy: -(50 + Math.random() * 140), c: cols[i % cols.length], sz: 3 + Math.random() * 9, dl: Math.random() * 0.25, br: Math.random() > 0.5 ? "50%" : "2px" }));
  return (
    <div style={{ position: "fixed", top: "28%", left: 0, width: "100%", pointerEvents: "none", zIndex: 900 }}>
      {pts.map(p => <div key={p.id} style={{ position: "absolute", left: `${p.x}%`, width: p.sz, height: p.sz, borderRadius: p.br, background: p.c, animation: `bstA 1s ${p.dl}s ease-out forwards`, "--dx": `${p.dx}px`, "--dy": `${p.dy}px` }} />)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// BALL DOT
// ═══════════════════════════════════════════════════════
function BallDot({ entry }) {
  return (
    <div style={{
      width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 900,
      background: entry.isOut ? "rgba(255,95,87,0.22)" : "rgba(76,255,110,0.15)",
      color: entry.isOut ? CLR.coral : CLR.green,
      border: `1.5px solid ${entry.isOut ? "rgba(255,95,87,0.45)" : "rgba(76,255,110,0.4)"}`
    }}>
      {entry.isOut ? "W" : entry.runs}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// FOOTER — branding shown on all screens
// ═══════════════════════════════════════════════════════
// ── useMenuLock ──────────────────────────────────────────
// Returns true for `secs` seconds after first render, then false.
// Used to disable "Main Menu" button so players have time to request a rematch.
function useMenuLock(secs = 10) {
  const [locked, setLocked] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLocked(false), secs * 1000);
    return () => clearTimeout(t);
  }, [secs]);
  return locked;
}

function Footer() {
  return (
    <div style={{ textAlign: "center", marginTop: 18, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "clamp(9px,2.5vw,11px)", fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, lineHeight: 1.8 }}>
        Built by <span style={{ color: "rgba(255,200,80,0.85)", fontWeight: 700 }}>Striveon</span>
        <span style={{ margin: "0 8px", opacity: 0.5 }}>·</span>
        Made with <span style={{ color: "#ff6b6b" }}>❤️</span> in India
      </div>
      <div style={{ marginTop: 6 }}>
        <a href="/privacy.html" target="_blank" rel="noopener"
          style={{ color: "rgba(255,255,255,0.28)", fontSize: "clamp(8px,2vw,10px)", textDecoration: "none", letterSpacing: 1, fontFamily: "'Rajdhani',sans-serif" }}>
          Privacy Policy
        </a>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// NUMPAD — fully responsive, touch-optimized
// ═══════════════════════════════════════════════════════
function NumPad({ onPick, disabled, myPick, acc, urgent }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(5px,1.8vw,9px)" }}>
      {NUMS.map(n => {
        const sel = myPick === n;
        return (
          <button key={n} onClick={() => !disabled && onPick(n)} disabled={disabled}
            style={{
              /* height scales: 68px on 320px phone → 80px on 390px → 90px on 430px+ */
              height: "clamp(68px,17vw,88px)",
              borderRadius: 12,
              border: `2px solid ${sel ? acc : urgent ? "rgba(255,95,87,0.5)" : "rgba(255,255,255,0.16)"}`,
              background: sel ? `linear-gradient(135deg,${acc}40,${acc}22)` : urgent ? "rgba(255,95,87,0.10)" : "rgba(255,255,255,0.09)",
              backdropFilter: "blur(6px)",
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
              transform: sel ? "scale(1.05)" : "scale(1)",
              opacity: disabled && !sel ? 0.32 : 1,
              transition: "all 0.13s cubic-bezier(0.34,1.56,0.64,1)",
              boxShadow: sel ? `0 4px 18px ${acc}50,0 0 0 1px ${acc}40` : urgent ? "0 0 10px rgba(255,95,87,0.18)" : "0 2px 6px rgba(0,0,0,0.2)",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              userSelect: "none",
            }}>
            <span style={{ fontSize: "clamp(22px,6.5vw,30px)", lineHeight: 1 }}>{EMOJI[n]}</span>
            <span style={{ color: sel ? acc : "#fff", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(16px,4.5vw,20px)", letterSpacing: 1, fontWeight: 900 }}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PROMO POPUP — full-screen overlay shown for 5s, closeable
// Sponsors DM for placements.
// ═══════════════════════════════════════════════════════
function PromoPopup({ onClose }) {
  const TOTAL = 15;
  const [secs, setSecs] = useState(TOTAL);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    const t = setInterval(() => {
      setSecs(s => {
        if (s <= 1) { clearInterval(t); onCloseRef.current(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const pct = ((TOTAL - secs) / TOTAL) * 100;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1200,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "16px",
      background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
      animation: "fadeIn 0.3s ease"
    }}>
      <div style={{
        width: "100%", maxWidth: 380, borderRadius: 20,
        background: "linear-gradient(160deg,#1a4a28,#0f2d1a)",
        border: "2px solid rgba(245,197,24,0.45)",
        boxShadow: "0 0 60px rgba(245,197,24,0.18), 0 8px 40px rgba(0,0,0,0.6)",
        overflow: "hidden", position: "relative",
        animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1)"
      }}>
        {/* Gold progress bar — drains as timer counts down */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "rgba(255,255,255,0.08)" }}>
          <div style={{
            height: "100%", width: `${pct}%`,
            background: "linear-gradient(90deg,#f5c518,#ffdd57)",
            transition: "width 1s linear", borderRadius: "0 2px 2px 0"
          }} />
        </div>

        {/* Close button with countdown */}
        <button onClick={onClose} style={{
          position: "absolute", top: 10, right: 10,
          background: "rgba(255,255,255,0.10)", border: "1.5px solid rgba(255,255,255,0.20)",
          borderRadius: 20, padding: "4px 12px", cursor: "pointer",
          color: "rgba(255,255,255,0.70)", fontFamily: "'Bebas Neue',serif",
          fontSize: 13, letterSpacing: 1, display: "flex", alignItems: "center", gap: 5,
          touchAction: "manipulation"
        }}>
          {secs > 0 ? `SKIP ${secs}` : "✕ CLOSE"}
        </button>

        <div style={{ padding: "44px 24px 28px", textAlign: "center" }}>
          {/* Badge */}
          <div style={{
            display: "inline-block", padding: "3px 12px", borderRadius: 20,
            background: "rgba(245,197,24,0.14)", border: "1px solid rgba(245,197,24,0.30)",
            color: "rgba(245,197,24,0.70)", fontFamily: "'Bebas Neue',serif",
            fontSize: 9, letterSpacing: 4, marginBottom: 16
          }}>ADVERTISEMENT</div>

          {/* Megaphone icon */}
          <div style={{ fontSize: 52, marginBottom: 12, filter: "drop-shadow(0 0 12px rgba(245,197,24,0.4))" }}>📢</div>

          <div style={{
            color: "#f5c518", fontFamily: "'Bebas Neue',serif",
            fontSize: "clamp(22px,7vw,28px)", letterSpacing: 3, marginBottom: 10,
            textShadow: "0 0 20px rgba(245,197,24,0.4)"
          }}>
            YOUR BRAND HERE
          </div>

          <div style={{
            color: "rgba(255,255,255,0.65)", fontSize: "clamp(12px,3.5vw,14px)",
            lineHeight: 1.65, marginBottom: 22
          }}>
            Want your brand in front of thousands of cricket fans?<br />
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "clamp(10px,3vw,12px)" }}>
              DM us to promote your brand in this spot.
            </span>
          </div>

          {/* CTA pill */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 22px", borderRadius: 30,
            background: "linear-gradient(135deg,rgba(245,197,24,0.22),rgba(245,197,24,0.10))",
            border: "1.5px solid rgba(245,197,24,0.50)",
            color: "#f5c518", fontFamily: "'Bebas Neue',serif",
            fontSize: "clamp(11px,3.2vw,13px)", letterSpacing: 2,
            boxShadow: "0 0 20px rgba(245,197,24,0.15)"
          }}>
            📩 CONTACT US FOR A QUOTE
          </div>

          {/* Divider */}
          <div style={{ margin: "20px 0 0", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 14 }}>
            <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>
              STRIVEON GAMES · HAND CRICKET
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Small inline placeholder shown where the popup was triggered from
function PromoBanner() {
  return null; // Popup handles visibility — no inline banner needed
}

// ═══════════════════════════════════════════════════════
// REMATCH BANNER — non-blocking top notification
// Shows to all players when someone requests a rematch.
// ═══════════════════════════════════════════════════════
function RematchBanner({ requesterName, onYes, onNo, totalSecs = 10 }) {
  const [secs, setSecs] = useState(totalSecs);
  const onNoRef = useRef(onNo);
  const onYesRef = useRef(onYes);
  useEffect(() => { onNoRef.current = onNo; }, [onNo]);
  useEffect(() => { onYesRef.current = onYes; }, [onYes]);
  useEffect(() => {
    const t = setInterval(() => {
      setSecs(s => {
        if (s <= 1) { clearInterval(t); onNoRef.current(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []); // empty deps — interval starts once and runs cleanly to zero

  const pct = (secs / totalSecs) * 100;

  return (
    <div style={{
      position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
      zIndex: 1100, width: "calc(100% - 32px)", maxWidth: 420,
      borderRadius: 16, overflow: "hidden",
      background: "linear-gradient(135deg,#1a4a28,#163d24)",
      border: "2px solid rgba(76,255,110,0.45)",
      boxShadow: "0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(76,255,110,0.12)",
      animation: "slideDown 0.35s cubic-bezier(0.34,1.56,0.64,1)"
    }}>
      {/* Countdown bar — drains from full to empty */}
      <div style={{ height: 3, background: "rgba(255,255,255,0.08)" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: pct > 50 ? "#4cff6e" : pct > 25 ? "#f5c518" : "#ff5f57",
          transition: "width 1s linear, background 0.5s"
        }} />
      </div>

      <div style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Icon + text */}
          <div style={{ fontSize: 28, flexShrink: 0 }}>🔁</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: "#fff", fontFamily: "'Bebas Neue',serif",
              fontSize: "clamp(13px,4vw,15px)", letterSpacing: 1, marginBottom: 2
            }}>
              {requesterName} wants a rematch!
            </div>
            <div style={{ color: "rgba(255,255,255,0.50)", fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 1 }}>
              Auto-declining in {secs}s…
            </div>
          </div>
          {/* Countdown ring */}
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: `conic-gradient(${pct > 50 ? "#4cff6e" : pct > 25 ? "#f5c518" : "#ff5f57"} ${pct * 3.6}deg, rgba(255,255,255,0.1) 0deg)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 900, color: "#fff", fontFamily: "'Bebas Neue',serif"
          }}>
            {secs}
          </div>
        </div>

        {/* YES / NO buttons */}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={onYes} style={{
            flex: 1, minHeight: 42, borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,rgba(76,255,110,0.28),rgba(76,255,110,0.14))",
            borderTop: "1.5px solid rgba(76,255,110,0.55)",
            color: "#4cff6e", fontFamily: "'Bebas Neue',serif",
            fontSize: "clamp(13px,4vw,15px)", letterSpacing: 2, cursor: "pointer",
            touchAction: "manipulation"
          }}>
            ✅ YES, PLAY AGAIN
          </button>
          <button onClick={onNo} style={{
            flex: 1, minHeight: 42, borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,rgba(255,95,87,0.22),rgba(255,95,87,0.10))",
            borderTop: "1.5px solid rgba(255,95,87,0.45)",
            color: "#ff5f57", fontFamily: "'Bebas Neue',serif",
            fontSize: "clamp(13px,4vw,15px)", letterSpacing: 2, cursor: "pointer",
            touchAction: "manipulation"
          }}>
            ✕ NO THANKS
          </button>
        </div>
      </div>
    </div>
  );
}

// Requester's status card — shown to the person who sent the rematch request
function RematchWaiting({ responses, players, onCancel }) {
  const entries = Object.entries(responses || {});
  const yesCount = entries.filter(([, v]) => v === "yes").length;
  const noCount  = entries.filter(([, v]) => v === "no").length;
  const total    = entries.length;

  return (
    <div style={{
      position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
      zIndex: 1100, width: "calc(100% - 32px)", maxWidth: 420,
      borderRadius: 16, padding: "14px 16px",
      background: "linear-gradient(135deg,#1a4a28,#163d24)",
      border: "2px solid rgba(245,197,24,0.40)",
      boxShadow: "0 8px 40px rgba(0,0,0,0.55)",
      animation: "slideDown 0.35s cubic-bezier(0.34,1.56,0.64,1)"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 24 }}>🔁</div>
        <div>
          <div style={{ color: "#f5c518", fontFamily: "'Bebas Neue',serif", fontSize: 14, letterSpacing: 1 }}>
            REMATCH REQUEST SENT
          </div>
          <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, letterSpacing: 1 }}>
            Waiting for others to respond…
          </div>
        </div>
        <button onClick={onCancel} style={{
          marginLeft: "auto", background: "none", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8, color: "rgba(255,255,255,0.45)", fontSize: 10,
          cursor: "pointer", padding: "4px 8px", fontFamily: "'Bebas Neue',serif",
          letterSpacing: 1, touchAction: "manipulation"
        }}>CANCEL</button>
      </div>
      {/* Response pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {entries.map(([pid, resp]) => {
          const name = players?.[pid]?.name || "Player";
          const col = resp === "yes" ? "#4cff6e" : resp === "no" ? "#ff5f57" : "rgba(255,255,255,0.3)";
          const icon = resp === "yes" ? "✅" : resp === "no" ? "✕" : "⏳";
          return (
            <div key={pid} style={{
              padding: "4px 10px", borderRadius: 20,
              background: `${col}18`, border: `1px solid ${col}40`,
              color: col, fontSize: 10, fontFamily: "'Bebas Neue',serif", letterSpacing: 1
            }}>
              {icon} {name}
            </div>
          );
        })}
      </div>
    </div>
  );
}
export default function HandCricket() {
  const [screen, setScreen] = useState("menu");
  const [gs, setGs] = useState(null);
  const [ch, setCh] = useState(CHALLENGES[0]);
  const [mode, setMode] = useState(null); // "ai", "multi", "team"
  const [myPick, setMyPick] = useState(null);
  const [revealInfo, setRevealInfo] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [burst, setBurst] = useState(null);
  const [flash, setFlash] = useState(null);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TURN_SECS);
  const [timerOn, setTimerOn] = useState(false);
  const [quitConfirm, setQuitConfirm] = useState(false);
  const [myRole, setMyRole] = useState("p1");
  const [myName, setMyName] = useState(() => localStorage.getItem("hc_playerName") || "");
  const [p1name, setP1name] = useState("");
  const [p2name, setP2name] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [joinInput, setJoinInput] = useState("");
  const [joinErr, setJoinErr] = useState("");
  // ── TEAM MATCH STATE ──
  const [tmOvers, setTmOvers] = useState(3);
  const [tmPlayers, setTmPlayers] = useState(4);
  const [tmJoinInput, setTmJoinInput] = useState("");
  const [tmJoinErr, setTmJoinErr] = useState("");
  const [tmRoom, setTmRoom] = useState(null);
  const [tmCode, setTmCode] = useState("");
  const [tmMyTeam, setTmMyTeam] = useState("red"); // which team this player is on
  const [lobbyToast, setLobbyToast] = useState(null); // {msg, color} shown for 3s when player joins/leaves lobby
  const [tmReactions, setTmReactions] = useState([]); // incoming floating emoji reactions from opponent
  const [tossReveal, setTossReveal] = useState(null); // {battingTm, bowlingTm} shown briefly after toss
  const [inn2Starting, setInn2Starting] = useState(false); // guard against result flash during inn2 start
  const [adDone, setAdDone] = useState(false); // true once promo popup closed on break screens
  const [inn2ReadyCount, setInn2ReadyCount] = useState(0); // players ready for inn2
  const [menuLocked, setMenuLocked] = useState(false); // true for 10s on result screens to give time for rematch
  const [room1v1, setRoom1v1] = useState(null); // SINGLE SOURCE OF TRUTH FOR 1V1
  const [lastBallReveal, setLastBallReveal] = useState(null); // {info, nextGs, room} — shown on last ball before result
  const [playerLeftModal, setPlayerLeftModal] = useState(null); // {pid, name, team, roomSnapshot} — shown when teammate quits
  const [opponentLeftBanner, setOpponentLeftBanner] = useState(null); // {name, team} — persistent info shown to opponents while teammates decide
  // ── PROMO POPUP ──
  const [showPromo, setShowPromo] = useState(false); // true = popup overlay is visible
  // ── REMATCH REQUEST STATE ──
  const [rematchReq, setRematchReq] = useState(null); // the live rematchRequest object from Firebase
  const [rematchStatus, setRematchStatus] = useState(null); // "waiting"|"declined"|"starting" for requester
  const [nameErr, setNameErr] = useState(""); // menu validation error
  const [nameConfirmed, setNameConfirmed] = useState(false); // local gate for setup screens


  const timerIntRef = useRef(null), timeLeftRef = useRef(TURN_SECS), pollRef = useRef(null);
  const heartbeatRef = useRef(null); // writes lastSeen to Firebase every 5s
  const pollTokenRef = useRef(0);
  const gsRef = useRef(gs), chRef = useRef(ch), modeRef = useRef(mode);
  const myRoleRef = useRef(myRole), roomCodeRef = useRef(roomCode);
  const animRef = useRef(animating), streakRef = useRef(streak);
  const p2nameRef = useRef(p2name);
  const p1nameRef = useRef(p1name);
  const quitConfirmRef = useRef(quitConfirm);
  // C1 FIX: screenRef so poll callback always knows current screen without stale closure
  const screenRef = useRef(screen);
  // keep screenRef in sync so callbacks can read the latest screen
  // (explicit cleanup for select screen was removed after it caused a blank UI)
  const myPickRef = useRef(null);
  myPickRef.current = myPick;
  const executePickRef = useRef(null), executeMultiPickRef = useRef(null), executeTeamPickRef = useRef(null);
  // ── TEAM MATCH REFS ──
  const tmPollRef = useRef(null);
  const tmPollTokenRef = useRef(0);
  const tmRoomRef = useRef(null);
  const room1v1Ref = useRef(null);
  const tmCodeRef = useRef("");
  const tmMyTeamRef = useRef("red");
  const tmLastReactionTsRef = useRef(0);
  const playerLeftModalRef = useRef(null);
  // Sync refs for interval callbacks
  room1v1Ref.current = room1v1;
  tmRoomRef.current = tmRoom;
  tmCodeRef.current = tmCode;
  tmMyTeamRef.current = tmMyTeam;
  playerLeftModalRef.current = playerLeftModal;
  gsRef.current = gs; chRef.current = ch; modeRef.current = mode;
  myRoleRef.current = myRole; roomCodeRef.current = roomCode;
  animRef.current = animating; streakRef.current = streak;
  p2nameRef.current = p2name; p1nameRef.current = p1name; quitConfirmRef.current = quitConfirm;
  screenRef.current = screen;

  const acc = ch?.color || CLR.gold;

  // Debug: trace key state changes when hunting lingering score display
  useEffect(() => {
    try {
      console.debug("HC: screen ->", screen);
      console.debug("HC: ch ->", ch?.id || null, "acc ->", acc);
      console.debug("HC: gs ->", gs ? { p1: gs.p1score, p2: gs.p2score, phase: gs.phase, ver: gs.ver } : null);
      console.debug("HC: room1v1 ->", room1v1 ? { p1id: room1v1.p1id, p2id: room1v1.p2id, status: room1v1.status } : null);
      console.debug("HC: roomCode ->", roomCode);
    } catch (e) {
      /* ignore */
    }
  }, [screen, ch, acc, gs, room1v1, roomCode]);

  // Clear all stale game visuals whenever we land on menu
  useEffect(() => {
    if (screen === "menu") {
      setBurst(null); setFlash(null); setRevealInfo(null); setMyPick(null); setAnimating(false);
      setTmRoom(null); setTmCode(""); setPlayerLeftModal(null); setOpponentLeftBanner(null);  // prevent teamResult flash when returning to menu
    }
    // Sync nameConfirmed based on myName whenever screen changes to a setup screen
    if (screen === "multiSetup" || screen === "teamSetup") {
      setNameConfirmed(!!myName.trim());
    }
    // Show promo popup whenever a result or break screen is reached
    if (screen === "result" || screen === "break" || screen === "teamResult" || screen === "teamBreak") {
      setShowPromo(true);
      setAdDone(false);
      setInn2ReadyCount(0);
    }



  }, [screen]);



  // ── TIMER ──────────────────────────────────────────────
  const stopTimer = useCallback(() => { if (timerIntRef.current) { clearInterval(timerIntRef.current); timerIntRef.current = null; } setTimerOn(false); }, []);
  const onTimeout = useCallback(() => {
    // BUG-6 FIX: don't auto-pick while quit confirm is open
    if (animRef.current || quitConfirmRef.current) return;
    stopTimer(); SFX.timeout();
    const auto = NUMS[Math.floor(Math.random() * NUMS.length)];
    // Team mode: must write into `tr/<code>/gs/*Pick` or the ball will hang forever.
    if (screenRef.current === "teamGame") executeTeamPickRef.current?.(auto, true);
    else if (modeRef.current === "ai") executePickRef.current?.(auto, true);
    else executeMultiPickRef.current?.(auto, true);
  }, [stopTimer]);
  const startTimer = useCallback(() => {
    stopTimer(); setTimeLeft(TURN_SECS); timeLeftRef.current = TURN_SECS; setTimerOn(true);
    timerIntRef.current = setInterval(() => {
      timeLeftRef.current -= 1; const t = timeLeftRef.current; setTimeLeft(t);
      if (t <= 10 && t > 0) SFX.warn(); else if (t > 10) SFX.tick();
      if (t <= 0) { clearInterval(timerIntRef.current); timerIntRef.current = null; onTimeout(); }
    }, 1000);
  }, [stopTimer, onTimeout]);
  useEffect(() => () => stopTimer(), []);

  // menuLocked is now started via startMenuLockAfterAd() — called when the ad closes,
  // NOT when the screen changes. This ensures the 10s countdown begins only after
  // the ad has fully finished, giving players proper time to request a rematch.
  const menuLockTimerRef = useRef(null);
  const startMenuLockAfterAd = useCallback(() => {
    setMenuLocked(true);
    if (menuLockTimerRef.current) clearTimeout(menuLockTimerRef.current);
    menuLockTimerRef.current = setTimeout(() => setMenuLocked(false), 10000);
  }, []);
  // Clear the lock if we navigate away from result screens before the timer fires
  useEffect(() => {
    if (screen !== "result" && screen !== "teamResult") {
      setMenuLocked(false);
      if (menuLockTimerRef.current) { clearTimeout(menuLockTimerRef.current); menuLockTimerRef.current = null; }
    }
  }, [screen]);

  // ── VISIBILITY CHANGE — restart poll when user switches back to the tab ──
  // On mobile, setInterval pauses in background. Poll token gets stale.
  // When user comes back from sharing screenshot, rematch stops working.
  useEffect(() => {
    const handleVisible = () => {
      if (document.hidden) return;
      const s = screenRef.current;
      const code = roomCodeRef.current;
      const tmCode = tmCodeRef.current;
      // Restart 1v1 poll if in game/result/break
      if (code && (s === "game" || s === "result" || s === "break" || s === "lobby")) {
        startPoll(code, myRoleRef.current);
      }
      // Restart team poll if in any team screen
      if (tmCode && (s === "teamGame" || s === "teamResult" || s === "teamBreak" || s === "teamLobby" || s === "tossReveal" || s === "teamToss")) {
        startTmPoll(tmCode);
      }
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  // eslint-disable-next-line
  }, []);

  // ── AUTO-JOIN FROM LINK ──────────────────────────────
  // Handles ?join=ABCD (1v1) and ?teamjoin=ABCD (team match) URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get("join");
    const teamJoinCode = params.get("teamjoin");
    if (joinCode && joinCode.length === 4) {
      // Pre-fill join input and go straight to multiSetup — they just tap JOIN
      setJoinInput(joinCode.toUpperCase());
      setMode("multi");
      // Pick classic as default mode so multiSetup shows correctly
      const defaultCh = CHALLENGES[0];
      setCh(defaultCh);
      setScreen("multiSetup");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (teamJoinCode && teamJoinCode.length === 4) {
      setTmJoinInput(teamJoinCode.toUpperCase());
      setScreen("teamSetup");
      window.history.replaceState({}, "", window.location.pathname);
    }
  // eslint-disable-next-line
  }, []);

  const showFlash = useCallback((msg, color, dur = 1300) => { setFlash({ msg, color }); setTimeout(() => setFlash(null), dur); }, []);

  const clearMatchUiState = useCallback(() => {
    setMyPick(null);
    setRevealInfo(null);
    setAnimating(false);
    setBurst(null);
    setFlash(null);
    setStreak(0);
    setInn2Starting(false);
    setTossReveal(null);
    setRematchReq(null);
    setRematchStatus(null);
    setPlayerLeftModal(null);
    setOpponentLeftBanner(null);
  }, []);

  const playSoundAndBurst = useCallback((isOut, rawNum, batterTeam) => {
    try {
      const iAm = (modeRef.current === "ai" && batterTeam === "p1") || (modeRef.current === "multi" && myRoleRef.current === batterTeam);
      if (isOut) { iAm ? SFX.out() : SFX.wicket(); setBurst("out"); setStreak(0); showFlash(iAm ? "💀 OUT!" : "🎯 WICKET!", iAm ? CLR.coral : CLR.green); }
      else if (rawNum === 6) { SFX.six(); setBurst("six"); const ns = streakRef.current + 1; setStreak(ns); showFlash(ns >= 3 ? `🔥 ${ns}× STREAK!` : "💥 SIX!", CLR.gold, 1600); }
      else if (rawNum === 4) { SFX.four(); setBurst("four"); showFlash("🔥 FOUR!", CLR.sky); }
      else { SFX.hit(); setStreak(s => s + 1); }
    } catch (e) { console.warn("SFX", e); }
  }, [showFlash]);

  // ── AI PICK ────────────────────────────────────────────
  const executePick = useCallback((num, isTimeout = false) => {
    if (animRef.current) return;
    stopTimer();
    if (!isTimeout) SFX.pick(); else showFlash("⏱️ TIME'S UP!", CLR.coral, 900);
    setMyPick(num); setAnimating(true);
    const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setMyPick(null); setBurst(null); }, 4000);
    setTimeout(() => {
      const cur = gsRef.current, curCh = chRef.current;
      if (!cur || !curCh) { clearTimeout(safe); setAnimating(false); setMyPick(null); return; }
      const aiNum = NUMS[Math.floor(Math.random() * NUMS.length)];
      const result = processBall(cur, curCh, num, aiNum);
      setRevealInfo({ p1num: num, p2num: aiNum, ...result, isTimeout });
      playSoundAndBurst(result.isOut, result.rawNum, result.batterTeam);
      setTimeout(() => {
        if (screenRef.current !== "game") return;
        clearTimeout(safe); setBurst(null); setGs(result.next); setMyPick(null); setRevealInfo(null); setAnimating(false);
        if (result.next.phase === "done") { result.next.winner === "p1" ? SFX.win() : SFX.lose(); setScreen("result"); }
        else if (result.next.phase === "break") { setScreen("break"); }
      }, 1600);
    }, 650);
  }, [stopTimer, showFlash, playSoundAndBurst]);
  executePickRef.current = executePick;

  // ── MULTI PICK ─────────────────────────────────────────
  const executeMultiPick = useCallback(async (num, isTimeout = false) => {
    if (animRef.current) return;
    if (myPickRef.current !== null) return;
    stopTimer();
    if (!isTimeout) SFX.pick(); else showFlash("⏱️ TIME'S UP!", CLR.coral, 900);
    setMyPick(num);
    const code = roomCodeRef.current;
    const myR = myRoleRef.current;
    const pF = myR === "p1" ? "p1pick" : "p2pick";
    const oF = myR === "p1" ? "p2pick" : "p1pick";

    // RACE FIX: write only MY pick atomically — won't overwrite opponent's pick
    await updateRoom(code, { [pF]: num });

    // Re-read fresh to see if both picks are now set
    const room = await loadRoom(code);
    if (!room) { setMyPick(null); startTimer(); return; }

    if (room[oF] !== null && room[pF] !== null) {
      // Both picks present — process ball
      const p1n = myR === "p1" ? num : room[oF], p2n = myR === "p2" ? num : room[oF];
      const result = processBall(room.gs, chRef.current, p1n, p2n);
      setAnimating(true);
      const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setMyPick(null); setBurst(null); }, 4000);
      setRevealInfo({ p1num: p1n, p2num: p2n, ...result, isTimeout });
      playSoundAndBurst(result.isOut, result.rawNum, result.batterTeam);
      await saveRoom(code, {
        ...room, gs: result.next, p1pick: null, p2pick: null,
        lastBall: { p1num: p1n, p2num: p2n, isOut: result.isOut, rawNum: result.rawNum, runs: result.runs, batterTeam: result.batterTeam, isPow: result.isPow, isTimeout, ver: result.next.ver }
      });
      setTimeout(() => {
        if (screenRef.current !== "game") return;
        clearTimeout(safe); setBurst(null); setGs(result.next); setMyPick(null); setRevealInfo(null); setAnimating(false);
        if (result.next.phase === "done") { const iW = result.next.winner === myR || result.next.winner === "draw"; iW ? SFX.win() : SFX.lose(); setScreen("result"); }
        else if (result.next.phase === "break") { setScreen("break"); }
        else startTimer();
      }, 1600);
    }
    // else: I'm the first picker — wait for opponent's poll to process
  }, [stopTimer, showFlash, playSoundAndBurst, startTimer]);
  executeMultiPickRef.current = executeMultiPick;

  // ── POLL ───────────────────────────────────────────────
  const stopPoll = useCallback(() => {
    pollTokenRef.current += 1; // cancel in-flight async poll callbacks
    clearInterval(pollRef.current); pollRef.current = null;
  }, []);

  // ── HEARTBEAT ─────────────────────────────────────────
  // Writes MY_PID lastSeen every 5s so the opponent can detect disconnects
  const stopHeartbeat = useCallback(() => {
    clearInterval(heartbeatRef.current); heartbeatRef.current = null;
  }, []);

  const startHeartbeat1v1 = useCallback((code) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      if (screenRef.current !== "game") { stopHeartbeat(); return; }
      // Write lastSeen for my role
      await updateRoom(code, { [`${myRoleRef.current}LastSeen`]: Date.now() });
    }, 5000);
  }, [stopHeartbeat]);

  const startHeartbeatTeam = useCallback((code) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      const s = screenRef.current;
      // Write heartbeat on ALL team-related screens — stopping only when fully back at menu
      const isTeamScreen = s === "teamGame" || s === "teamLobby" || s === "teamBreak"
        || s === "teamToss" || s === "teamResult" || s === "tossReveal";
      if (!isTeamScreen) { stopHeartbeat(); return; }
      // Reduced from 5000ms → 2000ms so heartbeat gaps are detected faster.
      // With a 20s stale threshold this means ~10 missed beats before flagging —
      // plenty of margin against false positives from network hiccups.
      await updateTeam(code, { [`presence/${MY_PID}`]: Date.now() });
    }, 2000);
  }, [stopHeartbeat]);

  // ── FIREBASE onDisconnect PRESENCE ────────────────────────
  // This is the reliable way to detect disconnects regardless of how the client
  // disconnects (phone back button, app swipe, network drop, tab close, crash).
  // Firebase server executes these writes when the WebSocket connection drops —
  // no reliance on `beforeunload` which is unreliable on mobile.
  const onDisconnectCancelRef = useRef(null);

  const setupOnDisconnect = useCallback(async (code, isGame = false) => {
    // onDisconnect is ONLY set during active gameplay (isGame=true).
    // In lobby we do NOT set onDisconnect because Firebase fires it on any
    // brief WebSocket drop (switching apps, tab switching) — causing false
    // player removals. Lobby disconnect is handled by the explicit quit button.
    try {
      const presRef = ref(db, `tr/${code}/presence/${MY_PID}`);
      const activeRef = ref(db, `tr/${code}/players/${MY_PID}/active`);

      // Always cancel any previous onDisconnect first
      try { await fbOnDisconnect(presRef).cancel(); } catch (_) {}
      try { await fbOnDisconnect(activeRef).cancel(); } catch (_) {}

      // Confirm I'm active right now
      await updateTeam(code, {
        [`presence/${MY_PID}`]: Date.now(),
        [`players/${MY_PID}/active`]: true,
      });

      if (isGame) {
        // During game: set server-side writes for true disconnect
        await fbOnDisconnect(presRef).set(0);
        await fbOnDisconnect(activeRef).set(false);
      }

      onDisconnectCancelRef.current = async () => {
        try {
          await fbOnDisconnect(presRef).cancel();
          await fbOnDisconnect(activeRef).cancel();
        } catch (_) {}
        onDisconnectCancelRef.current = null;
      };
    } catch (e) {
      console.warn("[Presence] onDisconnect setup failed:", e);
    }
  }, []);

  const cancelOnDisconnect = useCallback(async () => {
    if (onDisconnectCancelRef.current) {
      await onDisconnectCancelRef.current();
    }
  }, []);

  // Single function to go back to menu — wipes ALL state so nothing leaks
  const stopTmPollRef = useRef(null);
  const navBusyRef = useRef(false);
  const runNavOnce = useCallback((fn) => {
    if (navBusyRef.current) return;
    navBusyRef.current = true;
    fn();
    setTimeout(() => { navBusyRef.current = false; }, 450);
  }, []);
  const resetToMenu = useCallback(() => {
    stopTimer(); stopPoll();
    if (stopTmPollRef.current) stopTmPollRef.current();
    // Clear ALL state first to prevent scorecard flash
    setTmRoom(null); setTmCode(""); setTmJoinInput(""); setTmJoinErr("");
    setGs(null); setCh(null); setMode(null);
    setMyPick(null); setRevealInfo(null); setAnimating(false);
    setBurst(null); setFlash(null); setStreak(0); setQuitConfirm(false); setPlayerLeftModal(null); setOpponentLeftBanner(null);
    setTimeLeft(TURN_SECS); setTimerOn(false); setRoomCode("");
    setP1name(""); setP2name(""); setJoinInput(""); setJoinErr("");
    setRoom1v1(null);
    // Change screen LAST to prevent flash
    setTimeout(() => setScreen("menu"), 0);
  }, [stopTimer, stopPoll]);
  const startPoll = useCallback((code, myR) => {
    stopPoll();
    startHeartbeat1v1(code); // write my lastSeen every 5s
    const token = ++pollTokenRef.current;
    const STALE_MS = 20000; // 20s without heartbeat = disconnected
    pollRef.current = setInterval(async () => {
      if (token !== pollTokenRef.current) return;
      const room = await loadRoom(code);
      if (!room || animRef.current) return;
      if (token !== pollTokenRef.current) return;

      const myF = myR === "p1" ? "p1pick" : "p2pick";
      setRoom1v1(room);

      // 1. FORFEIT CHECK
      if (room.forfeit && room.forfeit !== myR) {
        stopTimer(); SFX.win();
        const winGs = { ...gsRef.current, phase: "done", winner: myR };
        setGs(winGs); setScreen("result"); stopPoll(); stopHeartbeat(); return;
      }

      // 1b. Heartbeat disconnect check removed — caused false positives.
      // Disconnect handled by forfeit field (quit button / beforeunload / onDisconnect).

      // 2. STATUS ROUTING
      if (room.status === "toss" && (screenRef.current === "lobby" || screenRef.current === "game" || screenRef.current === "break" || screenRef.current === "result")) {
        setGs(null);
        setMyPick(null); setRevealInfo(null); setAnimating(false); setBurst(null);
        setScreen("multiToss"); return;
      }
      if (room.status === "playing" && (screenRef.current === "lobby" || screenRef.current === "multiToss")) {
        if (room.gs && room.toss?.choice) {
          const bt = (room.toss.choice === "bat" && room.toss.winner === myR) || (room.toss.choice === "bowl" && room.toss.winner !== myR);
          const iAmBat = bt;
          const battingTmId = iAmBat ? room[myR + "team"] : room[(myR === "p1" ? "p2" : "p1") + "team"];
          const bowlingTmId = iAmBat ? room[(myR === "p1" ? "p2" : "p1") + "team"] : room[myR + "team"];
          if (TM[battingTmId] && TM[bowlingTmId]) {
            setTossReveal({ battingTm: TM[battingTmId], bowlingTm: TM[bowlingTmId] });
            setTimeout(() => {
              setTossReveal(null); setGs(room.gs); setScreen("game"); startTimer();
            }, 1500); // Reduced from 3000ms to 1500ms for faster transition
          } else { setGs(room.gs); setScreen("game"); }
        } else { setGs(room.gs); setScreen("game"); }
        return;
      }

      // 3. GAME STATE SYNC
      // Race condition: both players picked simultaneously — neither was "second" so neither processed
      // Detect: ver unchanged but both picks are set → p1 processes (always, deterministically)
      if (room.gs && room.gs.ver === gsRef.current?.ver &&
          room.p1pick !== null && room.p2pick !== null && !animRef.current) {
        // Both p1 AND p2 can detect this, but only p1 processes to avoid double-write
        if (myR === "p1") {
          const fresh = await loadRoom(code);
          if (token !== pollTokenRef.current) return;
          if (fresh?.p1pick !== null && fresh?.p2pick !== null && fresh?.gs?.ver === room.gs.ver) {
            const p1n = fresh.p1pick, p2n = fresh.p2pick;
            const result = processBall(fresh.gs, chRef.current, p1n, p2n);
            stopTimer(); setAnimating(true); setMyPick(null);
            setRevealInfo({ p1num: p1n, p2num: p2n, ...result });
            playSoundAndBurst(result.isOut, result.rawNum, result.batterTeam);
            await saveRoom(code, {
              ...fresh, gs: result.next, p1pick: null, p2pick: null,
              lastBall: { p1num: p1n, p2num: p2n, isOut: result.isOut, rawNum: result.rawNum, runs: result.runs, batterTeam: result.batterTeam, isPow: result.isPow, ver: result.next.ver }
            });
            const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setMyPick(null); setBurst(null); }, 4000);
            setTimeout(() => {
              if (token !== pollTokenRef.current) return;
              clearTimeout(safe); setBurst(null); setGs(result.next); setMyPick(null); setRevealInfo(null); setAnimating(false);
              if (result.next.phase === "done") { const iW = result.next.winner === myR || result.next.winner === "draw"; iW ? SFX.win() : SFX.lose(); setScreen("result"); return; }
              else if (result.next.phase === "break") { setScreen("break"); return; }
              else startTimer();
            }, 1600);
            return;
          }
        } else {
          // I'm p2 — p1 will process. Just clear my stale pick so pad re-enables after reveal.
          // setMyPick(null) is intentionally NOT called here — the lastBall reveal will clear it.
        }
      }

      if (room.gs && room.gs.ver !== gsRef.current?.ver) {
        if (room.lastBall && room.lastBall.ver === room.gs.ver && !animRef.current) {
          const lb = room.lastBall;
          stopTimer(); setAnimating(true); setMyPick(null);
          setRevealInfo({ ...lb, next: room.gs });
          playSoundAndBurst(lb.isOut, lb.rawNum, lb.batterTeam);
          setGs(room.gs);
          const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setBurst(null); }, 4000);
          setTimeout(() => {
            if (token !== pollTokenRef.current) return;
            clearTimeout(safe); setBurst(null); setRevealInfo(null); setAnimating(false);
            if (room.gs?.phase === "done") { const iW = room.gs.winner === myR || room.gs.winner === "draw"; iW ? SFX.win() : SFX.lose(); setScreen("result"); return; }
            if (room.gs?.phase === "break") { setScreen("break"); return; }
            if (room.gs?.phase === "picking" && screenRef.current === "break") { setScreen("game"); return; }
            startTimer();
          }, 1600);
          return;
        }
        setGs(room.gs); setMyPick(null);
        if (room.gs?.phase === "done") { const iW = room.gs.winner === myR || room.gs.winner === "draw"; iW ? SFX.win() : SFX.lose(); setScreen("result"); return; }
        if (room.gs?.phase === "break") { setScreen("break"); return; }
        if (room.gs?.phase === "picking" && screenRef.current === "break") { setScreen("game"); }
      }

      // 3b. INN2 READY GATE — detect when both players have signalled ready after their ad
      if (screenRef.current === "break" && room.inn2ReadyP1 && room.inn2ReadyP2 && !animRef.current && gsRef.current?.phase === "break") {
        const myReadyField = myR === "p1" ? "inn2ReadyP1" : "inn2ReadyP2";
        if (room[myReadyField]) {
          // I already clicked ready — now other player is also ready. Start innings.
          stopTimer();
          setInn2Starting(true);
          setMyPick(null); setRevealInfo(null); setAnimating(false);
          setBurst(null); setFlash(null); setStreak(0);
          const curGs = gsRef.current;
          const newGs = { ...curGs, phase: "picking", ver: curGs.ver + 1 };
          // Only p1 writes to avoid double-write
          if (myR === "p1") {
            const fresh = await loadRoom(code);
            if (fresh && fresh.gs?.phase === "break") {
              await saveRoom(code, { ...fresh, gs: newGs, inn2ReadyP1: null, inn2ReadyP2: null });
            }
          }
          setGs(newGs); setScreen("game");
          setTimeout(() => setInn2Starting(false), 500);
          return;
        }
      }

      // 4. MISC SYNC (REACTIONS, NAMES, TIMER)
      if (room.p2name && room.p2name !== p2nameRef.current) setP2name(room.p2name);
      if (room.p1name && room.p1name !== p1nameRef.current) setP1name(room.p1name);

      if (Array.isArray(room.reactions)) {
        const lastSeen = tmLastReactionTsRef.current || 0;
        const newOnes = room.reactions.filter(r => r.ts > lastSeen && r.pid !== MY_PID);
        if (newOnes.length > 0) {
          newOnes.forEach(r => {
            const id = Math.random().toString(36).slice(2, 8);
            setTmReactions(prev => [...prev, { ...r, id }]);
            setTimeout(() => setTmReactions(prev => prev.filter(x => x.id !== id)), 2500);
          });
          tmLastReactionTsRef.current = newOnes[newOnes.length - 1].ts;
        }
      }

      if (room[myF] === null && !animRef.current && !timerIntRef.current && room.gs?.phase === "picking") startTimer();
    }, POLL_MS);
  }, [stopPoll, playSoundAndBurst, showFlash, startTimer]);
  useEffect(() => () => stopPoll(), []);

  // Debug error catcher
  useEffect(() => {
    const handleError = (msg, url, line, col, error) => {
      console.error("[CRASH DETECTED]", msg, "at", url, line, ":", col, error);
    };
    window.onerror = handleError;
    window.onunhandledrejection = (e) => console.error("[ASYNC CRASH]", e.reason);
    return () => { window.onerror = null; window.onunhandledrejection = null; };
  }, []);

  // FORFEIT FIX: if player closes the tab mid-game, write forfeit so opponent wins
  // Also marks player as inactive in lobby if they close tab there
  useEffect(() => {
    const handleUnload = () => {
      // 1v1 forfeit
      if (modeRef.current === "multi" && roomCodeRef.current && screenRef.current === "game") {
        const code = roomCodeRef.current;
        loadRoom(code).then(room => {
          if (room && !room.forfeit) saveRoom(code, { ...room, forfeit: myRoleRef.current });
        });
      }
      // Lobby presence: mark as inactive when tab closes in lobby
      if (screenRef.current === "teamLobby" && tmCodeRef.current) {
        const code = tmCodeRef.current;
        loadTeam(code).then(room => {
          if (!room?.players?.[MY_PID]) return;
          const updated = { ...room, players: { ...room.players, [MY_PID]: { ...room.players[MY_PID], active: false, leftAt: Date.now() } } };
          saveTeam(code, updated);
        });
      }
      // Team match forfeit on tab close during game, toss, or innings break
      const isActiveGameScreen = screenRef.current === "teamGame"
        || screenRef.current === "teamToss"
        || screenRef.current === "teamBreak";
      if (isActiveGameScreen && tmCodeRef.current) {
        const code = tmCodeRef.current;
        loadTeam(code).then(room => {
          if (!room) return;
          // During toss: no gs yet, just write teamForfeit
          if (room.status === "toss") {
            if (!room.teamForfeit) saveTeam(code, { ...room, teamForfeit: MY_PID });
            return;
          }
          if (!room.gs || room.gs.phase === "done") return;
          const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
          const myTeamArr = myTeamSide === "A" ? (room.teamA || []) : (room.teamB || []);
          const remainingTeammates = myTeamArr.filter(p => p !== MY_PID);
          if (remainingTeammates.length === 0) {
            if (!room.teamForfeit) saveTeam(code, { ...room, teamForfeit: MY_PID });
          } else {
            const myName = room.players?.[MY_PID]?.name || "A player";
            if (!room.playerLeft) saveTeam(code, { ...room, playerLeft: { pid: MY_PID, name: myName, team: myTeamSide, at: Date.now() } });
          }
        });
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  useEffect(() => {
    if (screen === "game" && mode === "ai" && !animating && !revealInfo) startTimer();
    if (screen !== "game" && screen !== "teamGame") stopTimer();
  }, [screen, mode, animating, revealInfo, startTimer, stopTimer]);

  const resetAll = useCallback((newCh) => {
    stopTimer(); stopPoll();
    const bf = newCh?.battingOnly ? true : Math.random() > 0.5;
    setGs(initGS(bf)); setMyPick(null); setRevealInfo(null); setAnimating(false);
    setBurst(null); setFlash(null); setStreak(0); setQuitConfirm(false);
  }, [stopTimer, stopPoll]);

  const createRoom = useCallback(async () => {
    if (!myName.trim()) { setNameErr("Enter your name first!"); return; }
    const code = genCode();
    const room = {
      status: "lobby",
      p1id: MY_PID, p1name: myName || "Player 1", p1team: "red",
      p2id: null, p2name: "", p2team: null,
      chId: ch?.id || "classic",
      gs: null, lastBall: null,
      createdAt: Date.now()
    };
    setTmMyTeam("red");
    await saveRoom(code, room);
    setRoomCode(code); setMyRole("p1"); setRoom1v1(room);
    setScreen("lobby"); startPoll(code, "p1");
  }, [ch, myName, startPoll]);

  const startMultiMatch = useCallback(async () => {
    const code = roomCodeRef.current;
    const room = await loadRoom(code);
    if (!room) return;
    const tossSalt = `${code}|P1:${room.p1id}|P2:${room.p2id}`;
    const tossWinner = (coinFlipAB(tossSalt) === "A") ? "p1" : "p2";
    const tossBy = tossWinner === "p1" ? room.p1id : room.p2id;
    const updated = { ...room, status: "toss", toss: { winner: tossWinner, tossBy, choice: null, at: Date.now() } };
    await saveRoom(code, updated);
  }, []);

  const submitMultiTossChoice = useCallback(async (choice) => {
    const code = roomCodeRef.current;
    const room = await loadRoom(code);
    if (!room?.toss || room.status !== "toss") return;
    if (room.toss.tossBy !== MY_PID) return;
    const winnerId = room.toss.winner; // "p1" or "p2"
    const isP1Winner = winnerId === "p1";
    const winnerTeam = isP1Winner ? room.p1team : room.p2team;
    const battingTeam = choice === "bat" ? winnerTeam : (winnerTeam === "red" ? "blue" : "red");
    const battingFirst = (battingTeam === room.p1team) ? "p1" : "p2";
    const gs = initGS(battingFirst === "p1");
    const updated = { ...room, status: "playing", gs, toss: { ...room.toss, choice } };
    await saveRoom(code, updated);
    setGs(gs); setScreen("game");
  }, []);

  const joinRoom = useCallback(async () => {
    setJoinErr("");
    if (!myName.trim()) { setNameErr("Enter your name first!"); return; }
    const code = joinInput.toUpperCase().trim();
    if (code.length !== 4) { setJoinErr("Enter a 4-letter code"); return; }
    const room = await loadRoom(code);
    if (!room) { setJoinErr("Room not found!"); return; }
    if (room.p2id) { setJoinErr("Room is full!"); return; }
    if (room.createdAt && Date.now() - room.createdAt > 10 * 60 * 1000) { setJoinErr("Room expired!"); return; }
    const matchCh = CHALLENGES.find(c => c.id === room.chId) || CHALLENGES[0];
    setCh(matchCh);
    const pname = myName || "Player 2";
    const p2team = room.p1team === "red" ? "blue" : "red";
    setTmMyTeam(p2team);
    const updated = { ...room, p2id: MY_PID, p2name: pname, p2team };
    await saveRoom(code, updated);
    setRoomCode(code); setMyRole("p2"); setRoom1v1(updated);
    setScreen("lobby"); startPoll(code, "p2");
  }, [joinInput, myName, startPoll]);

  // ══════════════════════════════════════════════════════
  // TEAM MATCH CALLBACKS
  // ══════════════════════════════════════════════════════
  const stopTmPoll = useCallback(() => {
    tmPollTokenRef.current += 1;
    clearInterval(tmPollRef.current); tmPollRef.current = null;
    stopHeartbeat();
    // Cancel the server-side onDisconnect handler — player is leaving intentionally,
    // so we'll do our own explicit writes rather than relying on the disconnect trigger.
    cancelOnDisconnect();
  }, [stopHeartbeat, cancelOnDisconnect]);
  stopTmPollRef.current = stopTmPoll;

  const startTmPoll = useCallback((code) => {
    stopTmPoll();
    startHeartbeatTeam(code); // write my presence every 5s
    setupOnDisconnect(code, false);  // lobby only — no onDisconnect writes (would fire on app-switch)
    const token = ++tmPollTokenRef.current;
    const STALE_MS = 60000; // 60s without ping = disconnected (conservative to prevent false positives)
    tmLastReactionTsRef.current = 0;
    tmPollRef.current = setInterval(async () => {
      if (token !== tmPollTokenRef.current) return;
      const room = await loadTeam(code);
      if (!room) return;
      if (token !== tmPollTokenRef.current) return;

      // STALE READ GUARD: if we locally know about a newer rematch than Firebase returned,
      // this is a delayed read of the OLD game — discard it entirely to prevent
      // stale gs.phase="done" or old forfeit fields from firing incorrectly.
      const localRematchId = tmRoomRef.current?.rematchId ?? 0;
      const remoteRematchId = room.rematchId ?? 0;
      if (localRematchId > remoteRematchId) return;

      // ── PRESENCE STALE CHECK DISABLED ──────────────────
      // Heartbeat-based disconnect detection caused too many false positives:
      // - Players switching to spectator mode incorrectly
      // - Players being kicked from lobby
      // - False winner declarations
      // Disconnect handling is done via:
      // 1. Firebase onDisconnect (server-side, fires on true disconnect)
      // 2. Explicit quit button writes playerLeft/teamForfeit
      // 3. beforeunload event writes forfeit on tab close

      // Team forfeit — covers toss phase (gs=null) and all active game phases
      // IMPORTANT: skip entirely in lobby — stale forfeit/playerLeft from previous game must not fire
      if (room.status !== "lobby" && room.teamForfeit && (room.status === "toss" || (room.gs && room.gs.phase !== "done")) && !animRef.current) {
        const forfeiterTeam = (room.teamA || []).includes(room.teamForfeit) ? "A" : "B";
        const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
        if (forfeiterTeam !== myTeamSide) {
          stopTimer(); SFX.win();
          const winGs = { ...room.gs, phase: "done", winner: myTeamSide };
          setTmRoom({ ...room, gs: winGs });
          setScreen("teamResult");
          return;
        }
      }
      // playerLeft: if the leaving player's team is now empty → auto-forfeit
      // Covers toss phase (gs=null) and all active game phases.
      if (room.status !== "lobby" && room.playerLeft && !room.teamForfeit && (room.status === "toss" || (room.gs && room.gs.phase !== "done"))) {
        const pl = room.playerLeft;
        const leftTeamArr = pl.team === "A" ? (room.teamA || []) : (room.teamB || []);
        const remainingOnLeftTeam = leftTeamArr.filter(p => p !== pl.pid);
        if (remainingOnLeftTeam.length === 0 && !animRef.current) {
          // Whole team quit — auto forfeit, their opponent wins
          const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
          const oppSide = pl.team;
          if (myTeamSide !== oppSide) {
            stopTimer(); SFX.win();
            const winGs = { ...room.gs, phase: "done", winner: myTeamSide };
            setTmRoom({ ...room, gs: winGs });
            setScreen("teamResult");
          }
          return;
        }
      }
      const prev = tmRoomRef.current;
      setTmRoom(room);

      // ── LOBBY FEATURES ─────────────────────────────────
      // Hard rematch routing: if a new rematchId appears, force everyone back to lobby.
      // This covers edge cases where a client is on an unexpected screen (or missed the exact
      // status transition) and would otherwise stay stuck on the result page.
      const prevRematchId = prev?.rematchId ?? 0;
      const currRematchId = room.rematchId ?? 0;
      const rematchJustHappened = room.status === "lobby" && currRematchId && currRematchId !== prevRematchId;
      if (rematchJustHappened) {
        stopTimer();
        clearMatchUiState();
        setGs(null);
        if (room.players?.[MY_PID]?.rematchPending) {
          updateTeam(code, { [`players/${MY_PID}/rematchPending`]: false });
        }
        setTimeout(() => setScreen("teamLobby"), 0);
        return;
      }

      // Route back to lobby if room was reset (Play Again) — covers ALL game screens
      const isAnyTeamGameScreen = screenRef.current === "teamResult"
        || screenRef.current === "teamBreak"
        || screenRef.current === "teamGame"
        || screenRef.current === "teamToss"
        || screenRef.current === "tossReveal";
      if (room.status === "lobby" && isAnyTeamGameScreen) {
        stopTimer();
        clearMatchUiState();
        setGs(null);
        // Clear rematchPending for this player — they've arrived back in lobby
        if (room.players?.[MY_PID]?.rematchPending) {
          updateTeam(code, { [`players/${MY_PID}/rematchPending`]: false });
        }
        setTimeout(() => setScreen("teamLobby"), 0); return;
      }
      if (room.status === "lobby" && screenRef.current === "teamLobby") {
        const prevPlayers = prev?.players || {};
        const currPlayers = room.players || {};

        // Clear our own rematchPending if still set (covers host + anyone already on lobby)
        if (currPlayers[MY_PID]?.rematchPending) {
          updateTeam(code, { [`players/${MY_PID}/rematchPending`]: false });
        }

        // ── CLEAN UP INACTIVE SLOTS ────────────────────
        // Only clean up players explicitly marked active:false (via onDisconnect or back button)
        // No heartbeat-based stale detection — caused false positives
        const inactivePids = Object.entries(currPlayers)
          .filter(([pid, p]) => p.active === false && pid !== MY_PID)
          .map(([pid]) => pid);

        if (inactivePids.length > 0 && room.hostPid === MY_PID) {
          const fresh = await loadTeam(code);
          if (fresh?.status === "lobby") {
            let changed = false;
            const updatedPlayers = { ...fresh.players };
            const updatedPresence = { ...(fresh.presence || {}) };
            inactivePids.forEach(pid => {
              if (updatedPlayers[pid]?.active === false) {
                delete updatedPlayers[pid];
                delete updatedPresence[pid];
                changed = true;
              }
            });
            if (changed) await saveTeam(code, { ...fresh, players: updatedPlayers, presence: updatedPresence });
          }
        }

        // Detect newly inactive players — show toast (no setTimeout removal needed — host cleans above)
        Object.entries(currPlayers).forEach(([pid, p]) => {
          if (pid === MY_PID) return;
          const prevP = prevPlayers[pid];
          if (!prevP) return; // first render — skip
          const wasActive = prevP.active !== false;
          const nowInactive = p.active === false;
          if (wasActive && nowInactive) {
            setLobbyToast({ msg: `${p.name || "A player"} left the room`, color: CLR.coral });
            setTimeout(() => setLobbyToast(null), 3000);
          }
          // Detect rejoin
          if (prevP.active === false && p.active !== false) {
            setLobbyToast({ msg: `${p.name || "A player"} rejoined! 🎉`, color: CLR.green });
            setTimeout(() => setLobbyToast(null), 2500);
          }
        });
        // New player joining
        Object.entries(currPlayers).forEach(([pid, p]) => {
          if (pid === MY_PID) return;
          if (!prevPlayers[pid] && prev && p.active !== false) {
            setLobbyToast({ msg: `${p.name || "A player"} joined! 🎉`, color: CLR.green });
            setTimeout(() => setLobbyToast(null), 2500);
          }
        });

        // ── AUTO HOST REASSIGNMENT ──────────────────────
        // Host is gone ONLY if explicitly marked inactive (active:false).
        // We do NOT use presence stale in lobby — too many false positives from
        // app-switching which temporarily drops the WebSocket connection.
        const presenceNow = Date.now();
        const hostPresent = currPlayers[room.hostPid]?.active !== false && !!currPlayers[room.hostPid];
        if (!hostPresent) {
          // Find the earliest-joined active player
          const activePids = Object.entries(currPlayers)
            .filter(([pid, p]) => {
              if (p.active === false) return false;
              return true;
            })
            .sort((a, b) => (a[1].joinedAt || 0) - (b[1].joinedAt || 0))
            .map(([pid]) => pid);
          const newHost = activePids[0];
          if (newHost && newHost === MY_PID) {
            // I am the new host — write to Firebase
            const fresh = await loadTeam(code);
            if (fresh && fresh.hostPid !== MY_PID && fresh.status === "lobby") {
              await saveTeam(code, { ...fresh, hostPid: MY_PID });
              setLobbyToast({ msg: "You are now the host! 👑", color: CLR.gold });
              setTimeout(() => setLobbyToast(null), 3000);
            }
          } else if (newHost && prev?.hostPid === room.hostPid) {
            // Host changed — show notification to everyone
            const newHostName = currPlayers[newHost]?.name || "Someone";
            setLobbyToast({ msg: `${newHostName} is now the host 👑`, color: CLR.gold });
            setTimeout(() => setLobbyToast(null), 3000);
          }
        }
      }

      // ── PLAYER LEFT DETECTION ──────────────────────────
      // Runs on ANY active game screen including during toss (room.gs may be null).
      // Check: either game hasn't started (toss) OR game is active (not done).
      const isActiveMatch = room.status === "toss" || (room.gs && room.gs.phase !== "done");
      if (room.playerLeft && !room.teamForfeit && isActiveMatch) {
        const pl = room.playerLeft;
        const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
        const isMyTeam = pl.team === myTeamSide;
        const iAmTheLeavingPlayer = pl.pid === MY_PID;

        if (!iAmTheLeavingPlayer) {
          if (isMyTeam) {
            // Teammate left — show the continue/forfeit modal (only if not already showing)
            if (!playerLeftModalRef.current) {
              stopTimer();
              setPlayerLeftModal({ pid: pl.pid, name: pl.name, team: pl.team, room });
            }
          } else {
            // Opponent left — show a brief flash to inform us, don't block anything
            // Show persistent info banner (not just a flash) so opponents always know
            if (!prev?.playerLeft || prev.playerLeft.pid !== pl.pid) {
              setOpponentLeftBanner({ name: pl.name, team: pl.team });
            }
          }
        }
      }

      // Clear opponentLeftBanner when teammates have decided (playerLeft cleared)
      if (!room.playerLeft && prev?.playerLeft) {
        setOpponentLeftBanner(null);
        // If no forfeit was written → they chose to continue, inform opponent team
        if (!room.teamForfeit) {
          showFlash("✅ Match continues!", CLR.green, 2500);
        }
      }

      // Android WebView: onDisconnect often sets active:false without playerLeft — notify remaining players
      if (isActiveMatch && !room.playerLeft && prev?.players) {
        Object.entries(room.players || {}).forEach(([pid, p]) => {
          if (pid === MY_PID) return;
          const wasActive = prev.players[pid]?.active !== false;
          if (!wasActive || p.active !== false) return;
          const leftTeam = (room.teamA || []).includes(pid) ? "A" : "B";
          const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
          const leftName = p.name || "A player";
          if (leftTeam === myTeamSide) {
            if (!playerLeftModalRef.current) {
              stopTimer();
              setPlayerLeftModal({ pid, name: leftName, team: leftTeam, room });
            }
          } else if (!prev.playerLeft || prev.playerLeft.pid !== pid) {
            setOpponentLeftBanner({ name: leftName, team: leftTeam });
            showFlash(`${leftName} left the match`, CLR.coral, 3500);
          }
        });
      }

      const syncOpponentReactions = (roomData) => {
        if (!Array.isArray(roomData?.reactions)) return;
        const lastSeen = tmLastReactionTsRef.current || 0;
        const myTeamColor =
          (roomData.players?.[MY_PID]?.team) ||
          ((roomData.teamA || []).includes(MY_PID) ? "red" : (roomData.teamB || []).includes(MY_PID) ? "blue" : null) ||
          tmMyTeamRef.current ||
          null; // "red"/"blue"
        const newOnes = roomData.reactions.filter(r => {
          if (r.ts <= lastSeen || r.pid === MY_PID) return false; // never self
          if (!r.team || !myTeamColor) return false;
          return r.team !== myTeamColor;
        });
        if (newOnes.length > 0) {
          newOnes.forEach(r => {
            const id = Math.random().toString(36).slice(2, 8);
            setTmReactions(prev => [...prev, { ...r, id }]);
            setTimeout(() => setTmReactions(prev => prev.filter(x => x.id !== id)), 2500);
          });
          tmLastReactionTsRef.current = newOnes[newOnes.length - 1].ts;
        }
      };

      // ── REMATCH REQUEST DETECTION ──────────────────────
      // Show banner to all players except the requester when a rematch request exists
      if (room.rematchRequest && room.status !== "lobby") {
        const req = room.rematchRequest;
        const iAmRequester = req.requestedBy === MY_PID;
        const myResponse = req.responses?.[MY_PID];
        const allPids = Object.keys(room.players || {}).filter(p => room.players[p].active !== false);
        const allResponded = allPids.every(pid => req.responses?.[pid]);
        const yesPlayers = allPids.filter(pid => req.responses?.[pid] === "yes");
        const isResultScreen = screenRef.current === "teamResult" || screenRef.current === "teamBreak";

        if (iAmRequester) {
          // Update requester's live view
          setRematchReq(req);
          setRematchStatus("waiting");

          // Resolve as soon as at least one OTHER player says yes.
          // Waiting for ALL responses can deadlock if a stale/extra active player slot exists
          // (or a client never responds). We still respect timedOut and the allResponded path.
          // Never use Date.now() comparison here — poll timing causes false positives.
          const timedOut = req.expiredAt === true;
          const enoughYesToStart = yesPlayers.length > 1;
          if (enoughYesToStart || allResponded || timedOut) {
            // All responded or timed out — decide outcome
            if (yesPlayers.length <= 1) {
              // Nobody else said yes
              setRematchStatus("declined");
              setRematchReq(null);
              updateTeam(code, { rematchRequest: null });
            } else {
              // Some said yes — reset room with only them
              setRematchStatus("starting");
              setRematchReq(null);
              const fresh = await loadTeam(code);
              if (fresh) {
                const newRematchId = Date.now();
                const resetPlayers = {};
                yesPlayers.forEach(pid => {
                  const p = fresh.players[pid];
                  if (p) resetPlayers[pid] = { ...p, active: true, rematchPending: pid !== MY_PID };
                });
                const resetRoom = {
                  hostPid: MY_PID,
                  status: "lobby",
                  overs: fresh.overs,
                  totalPlayers: yesPlayers.length,
                  players: resetPlayers,
                  teamA: [], teamB: [],
                  gs: null, lastBall: null, toss: null,
                  teamForfeit: null, playerLeft: null, forfeit: null,
                  rematchRequest: null,
                  reactions: [], presence: {},
                  createdAt: newRematchId, rematchId: newRematchId,
                };
                await saveTeam(code, resetRoom);
                setTmRoom(resetRoom);
                clearMatchUiState();
                setGs(null);
                startTmPoll(code);
                setTimeout(() => setScreen("teamLobby"), 0);
              }
            }
          }
        } else {
          // I'm NOT the requester
          setRematchReq(req);
          // Auto-decline only when Firebase expiredAt flag is set (written by RematchBanner's onNo)
          // NOT by comparing Date.now() — that causes false auto-declines from poll timing
          if (!myResponse && req.expiredAt === true) {
            updateTeam(code, { [`rematchRequest/responses/${MY_PID}`]: "no" });
            setRematchReq(null);
          }
        }
        // Block routing only while THIS player still needs to respond to the rematch banner
        if (room.rematchRequest && room.status !== "lobby" && room.rematchRequest.requestedBy !== MY_PID) {
          if (room.rematchRequest.responses?.[MY_PID] == null) return;
        }
      } else {
        // No active rematch request — clear any stale banner
        if (rematchReq) setRematchReq(null);
        if (rematchStatus === "waiting") setRematchStatus(null);
      }

      // Toss routing
      if (room.status === "toss" && (screenRef.current === "teamLobby" || screenRef.current === "teamGame" || screenRef.current === "teamBreak" || screenRef.current === "teamResult")) {
        setTmRoom(room); // ensure toss data is in state before screen switches
        setTimeout(() => setScreen("teamToss"), 0); // defer screen switch so tmRoom flushes first
        return;
      }
      // Start routing after toss choice
      if (room.status === "playing" && (screenRef.current === "teamLobby" || screenRef.current === "teamToss")) {
        // Game starting — upgrade onDisconnect for all players
        setupOnDisconnect(code, true);
        // Show batting/bowling reveal for 1.5 seconds before game screen
        if (room.gs && room.toss?.choice) {
          const battingTm = room.gs.battingTeam === "A" ? TM.red : TM.blue;
          const bowlingTm = room.gs.battingTeam === "A" ? TM.blue : TM.red;
          setTossReveal({ battingTm, bowlingTm });
          setTimeout(() => {
            setTossReveal(null);
            setScreen("teamGame");
            if (room.gs) {
              const battArr = room.gs.battingTeam === "A" ? room.teamA : room.teamB;
              const bowlArr = room.gs.battingTeam === "A" ? room.teamB : room.teamA;
              const cb = battArr[room.gs.batterIdx % battArr.length];
              const cbl = bowlArr[room.gs.bowlerIdx % bowlArr.length];
              if (MY_PID === cb || MY_PID === cbl) startTimer();
            }
          }, 1500); // Reduced from 3000ms to 1500ms for faster transition
        } else {
          setScreen("teamGame");
        }
        return;
      }

      if (!room.gs) return;
      // CRITICAL: only process game state when room is actually in a playing state.
      // Prevents stale gs from old game triggering result/break screens after Play Again reset.
      if (room.status !== "playing") return;
      const prevVer = prev?.gs?.ver ?? -1;
      if (room.gs.ver === prevVer) {
        // RACE CONDITION FIX: both players picked simultaneously — neither processed.
        // Detect stale state: both picks set but ver hasn't changed.
        // The BATTER always breaks the tie and processes the ball.
        if (room.gs.batterPick !== null && room.gs.bowlerPick !== null && !animRef.current) {
          const battArr = room.gs.battingTeam === "A" ? room.teamA : room.teamB;
          const bowlArr = room.gs.battingTeam === "A" ? room.teamB : room.teamA;
          const currentBatter = battArr[room.gs.batterIdx % battArr.length];
          const currentBowler = bowlArr[room.gs.bowlerIdx % bowlArr.length];
          if (MY_PID === currentBatter || MY_PID === currentBowler) {
            // I am active in this ball — process if still unprocessed.
            stopTimer();
            const latestRoom = await loadTeam(code);
            if (token !== tmPollTokenRef.current) return;
            if (!latestRoom?.gs) return;
            const latestGs = latestRoom.gs;
            if (
              latestGs.ver !== room.gs.ver ||
              latestGs.batterPick === null ||
              latestGs.bowlerPick === null
            ) {
              return;
            }
            const result = processTBall(latestGs, latestRoom.teamA, latestRoom.teamB);
            const battArr2 = latestGs.battingTeam === "A" ? latestRoom.teamA : latestRoom.teamB;
            const bowlArr2 = latestGs.battingTeam === "A" ? latestRoom.teamB : latestRoom.teamA;
            const cb = battArr2[latestGs.batterIdx % battArr2.length];
            const cbl = bowlArr2[latestGs.bowlerIdx % bowlArr2.length];
            const batterName = latestRoom.players[cb]?.name || "Batter";
            const bowlerName = latestRoom.players[cbl]?.name || "Bowler";
            setAnimating(true); setMyPick(null);
            const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setMyPick(null); setBurst(null); }, 4000);
            setRevealInfo({
              tmMode: true,
              batterNum: latestGs.batterPick, bowlerNum: latestGs.bowlerPick,
              batterName, bowlerName, isOut: result.isOut, rawNum: result.rawNum,
              runs: result.runs, iAmBatting: true
            });
            playSoundAndBurst(result.isOut, result.rawNum, result.isOut ? "p2" : "p1");
            const lb = {
              ver: result.next.ver, batterPick: latestGs.batterPick, bowlerPick: latestGs.bowlerPick,
              batterPid: cb, bowlerPid: cbl, batterName, bowlerName,
              isOut: result.isOut, rawNum: result.rawNum, runs: result.runs
            };
            await saveTeam(code, { ...latestRoom, gs: result.next, lastBall: lb });
            const revealDur2 = result.next.phase === "done" ? 2500 : 1600;
            setTimeout(() => {
              if (token !== tmPollTokenRef.current) return;
              clearTimeout(safe); setBurst(null); setRevealInfo(null); setAnimating(false);
              setTmRoom(r => r ? { ...r, gs: result.next } : r);
              if (result.next.phase === "done") { SFX.win(); setScreen("teamResult"); return; }
              if (result.next.phase === "break") { setScreen("teamBreak"); return; }
              const ba2 = result.next.battingTeam === "A" ? latestRoom.teamA : latestRoom.teamB;
              const bo2 = result.next.battingTeam === "A" ? latestRoom.teamB : latestRoom.teamA;
              const nb = ba2[result.next.batterIdx % ba2.length];
              const nbl = bo2[result.next.bowlerIdx % bo2.length];
              if (MY_PID === nb || MY_PID === nbl) startTimer();
            }, revealDur2);
          }
        }
        syncOpponentReactions(room);
        return;
      }
      // New ball result arrived — show reveal for first picker via lastBall
      // Only show lastBall reveal when we're actively in the game screen (not already on break/result)
      if (room.lastBall && !animRef.current && screenRef.current === "teamGame") {
        const lb = room.lastBall;
        if (lb.ver === room.gs.ver) {
          stopTimer();
          const myPid = MY_PID;
          // Use lb.batterPid to derive the inn1 batting team — NOT room.gs.battingTeam
          // because after inn1 ends, battingTeam has already flipped to inn2
          const lbBattingTeamIsA = lb.batterPid && (room.teamA || []).includes(lb.batterPid);
          const lbBattArr = lbBattingTeamIsA ? room.teamA : room.teamB;
          // figure out current roles at the TIME OF THE BALL (use lb data)
          const iWasBatter = lb.batterPid === myPid;
          const iWasBowler = lb.bowlerPid === myPid;
          // Show reveal for EVERYONE — spectators AND the first picker
          // (second picker already showed it in submitTeamPick)
          // Detect first picker: they have myPick still set OR they were batter/bowler but animating is false
          const amISecondPicker = iWasBatter && animRef.current || iWasBowler && animRef.current;
          if (!amISecondPicker) {
            setMyPick(null);
            setAnimating(true);
            setRevealInfo({
              tmMode: true, batterNum: lb.batterPick, bowlerNum: lb.bowlerPick,
              batterName: lb.batterName, bowlerName: lb.bowlerName,
              isOut: lb.isOut, rawNum: lb.rawNum, runs: lb.runs,
              iAmBatting: lbBattArr.includes(myPid)
            });
            playSoundAndBurst(lb.isOut, lb.rawNum, lb.isOut ? "p2" : "p1");
            const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setBurst(null); setMyPick(null); }, 4000);
            const revealDur3 = room.gs.phase === "done" ? 2500 : 1600;
            setTimeout(() => {
              if (token !== tmPollTokenRef.current) return;
              clearTimeout(safe); setBurst(null); setRevealInfo(null); setAnimating(false); setMyPick(null);
              if (room.gs.phase === "done") { SFX.win(); setScreen("teamResult"); return; }
              if (room.gs.phase === "break") { setScreen("teamBreak"); return; }
              if (room.gs.phase === "picking") {
                const ba2 = room.gs.battingTeam === "A" ? room.teamA : room.teamB;
                const bo2 = room.gs.battingTeam === "A" ? room.teamB : room.teamA;
                const nb = ba2[room.gs.batterIdx % ba2.length];
                const nbl = bo2[room.gs.bowlerIdx % bo2.length];
                if (MY_PID === nb || MY_PID === nbl) startTimer();
              }
            }, revealDur3);
            return;
          }
        }
      }
      // 3c. TEAM INN2 READY GATE — detect when all players are ready after their ad
      if (screenRef.current === "teamBreak" && room.gs?.phase === "break" && room.inn2Ready && !animRef.current) {
        const activePids = Object.keys(room.players || {}).filter(pid => room.players[pid].active !== false);
        const readyPids = Object.keys(room.inn2Ready).filter(pid => room.inn2Ready[pid]);
        const allReady = activePids.length > 0 && activePids.every(pid => readyPids.includes(pid));
        if (allReady) {
          // All players ready — the first one to detect writes the new gs
          if (!animRef.current) {
            const myReadyField = `inn2Ready/${MY_PID}`;
            if (room.inn2Ready?.[MY_PID]) {
              // I'm ready — start if not already starting (only write if hostPid or first alphabetically to avoid double-write)
              const sortedPids = [...activePids].sort();
              if (sortedPids[0] === MY_PID) {
                setInn2Starting(true);
                const fresh = await loadTeam(code);
                if (fresh?.gs?.phase === "break") {
                  const newGs = { ...fresh.gs, phase: "picking", ver: fresh.gs.ver + 1 };
                  await saveTeam(code, { ...fresh, gs: newGs, lastBall: null, inn2Ready: null });
                  setTmRoom({ ...fresh, gs: newGs });
                }
                setScreen("teamGame");
                setTimeout(() => setInn2Starting(false), 500);
                return;
              }
            }
          }
        }
      }

      // Phase transitions fallback
      setMyPick(null); // always clear stale pick on any gs change
      if (room.gs.phase === "done") { SFX.win(); setScreen("teamResult"); return; }
      if (room.gs.phase === "break") { setScreen("teamBreak"); return; }
      // Inn2 start: transition from teamBreak to teamGame
      if (room.gs.phase === "picking" && screenRef.current === "teamBreak") { setInn2Starting(true); setRevealInfo(null); setAnimating(false); setBurst(null); setScreen("teamGame"); setTimeout(() => setInn2Starting(false), 500); }
      // Restart timer if my turn
      if (!animRef.current && !timerIntRef.current && room.gs.phase === "picking") {
        const battArr = room.gs.battingTeam === "A" ? room.teamA : room.teamB;
        const bowlArr = room.gs.battingTeam === "A" ? room.teamB : room.teamA;
        const cb = battArr[room.gs.batterIdx % battArr.length];
        const cbl = bowlArr[room.gs.bowlerIdx % bowlArr.length];
        if (MY_PID === cb || MY_PID === cbl) startTimer();
      }
      // Reaction sync — only show reactions from the OPPONENT color team.
      syncOpponentReactions(room);
    }, POLL_MS);
  }, [stopTmPoll, playSoundAndBurst, startTimer, stopTimer, clearMatchUiState, showFlash]);
  useEffect(() => () => stopTmPoll(), []);

  // ── TEAM POLL WATCHDOG ────────────────────────────────
  // If the interval ever gets cleared (mobile suspend, navigation edge),
  // Team screens must restart polling or the UI can get stuck on result
  // while others already moved to lobby (rematch reset).
  useEffect(() => {
    const s = screen;
    const isTeamScreen = s === "teamGame" || s === "teamLobby" || s === "teamBreak"
      || s === "teamToss" || s === "teamResult" || s === "tossReveal";
    const code = tmCodeRef.current;
    if (isTeamScreen && code && !tmPollRef.current) {
      startTmPoll(code);
    }
  }, [screen, startTmPoll]);

  const createTeamRoom = useCallback(async () => {
    if (!myName.trim()) { setNameErr("Enter your name first!"); return; }
    const code = genCode();
    const room = {
      hostPid: MY_PID, status: "lobby",
      overs: tmOvers, totalPlayers: tmPlayers,
      players: { [MY_PID]: { name: myName.trim(), joinedAt: Date.now(), team: tmMyTeam } },
      teamA: [], teamB: [], gs: null, lastBall: null,
      createdAt: Date.now()
    };
    await saveTeam(code, room);
    setTmCode(code); setTmRoom(room);
    setScreen("teamLobby"); startTmPoll(code);
  }, [myName, tmOvers, tmPlayers, tmMyTeam, startTmPoll]);

  const joinTeamRoom = useCallback(async () => {
    setTmJoinErr("");
    if (!myName.trim()) { setTmJoinErr("Enter your name first!"); return; }
    const code = tmJoinInput.toUpperCase().trim();
    if (code.length !== 4) { setTmJoinErr("Enter a 4-letter code"); return; }
    const room = await loadTeam(code);
    if (!room) { setTmJoinErr("Room not found!"); return; }
    if (room.status !== "lobby") { setTmJoinErr("Game already started!"); return; }
    if (room.createdAt && Date.now() - room.createdAt > 30 * 60 * 1000) { setTmJoinErr("Room expired!"); return; }

    const wasHere = room.players?.[MY_PID];
    if (!wasHere) {
      // New player — only count truly active players toward the limit
      const activePlayers = Object.values(room.players || {}).filter(p => p.active !== false);
      if (activePlayers.length >= room.totalPlayers) {
        setTmJoinErr("Room is full!"); return;
      }
    }

    const pids = Object.keys(room.players || {}).filter(pid => room.players[pid].active !== false);
    const redCount = pids.filter(p => room.players[p].team === "red").length;
    const blueCount = pids.filter(p => room.players[p].team === "blue").length;
    const autoTeam = wasHere?.team || (blueCount <= redCount ? "blue" : "red");
    setTmMyTeam(autoTeam);

    // Write active:true explicitly — overrides any stale active:false from previous session
    const playerEntry = { name: myName.trim(), joinedAt: wasHere?.joinedAt || Date.now(), team: autoTeam, active: true };
    const updated = { ...room, players: { ...room.players, [MY_PID]: playerEntry } };
    await saveTeam(code, updated);

    // Also use atomic update to confirm active:true (prevents race with onDisconnect)
    await updateTeam(code, { [`players/${MY_PID}/active`]: true });

    setTmCode(code); setTmRoom(updated);
    setScreen("teamLobby"); startTmPoll(code);
  }, [myName, tmJoinInput, startTmPoll]);

  // Switch team in lobby — updates Firebase and local state
  const switchTeam = useCallback(async (newTeam) => {
    if (!tmCodeRef.current) return;
    setTmMyTeam(newTeam);
    const room = await loadTeam(tmCodeRef.current);
    if (!room?.players[MY_PID]) return;
    const updated = { ...room, players: { ...room.players, [MY_PID]: { ...room.players[MY_PID], team: newTeam } } };
    await saveTeam(tmCodeRef.current, updated);
    setTmRoom(updated);
  }, []);

  // Send emoji reaction — saves to Firebase with sender's team
  // Appears only on the OPPONENT team's screens
  const sendReaction = useCallback(async (emoji) => {
    const isTm = screenRef.current.startsWith("team");
    const code = isTm ? tmCodeRef.current : roomCodeRef.current;
    if (!code) return;
    const room = isTm ? await loadTeam(code) : await loadRoom(code);
    if (!room) return;
    const isP1 = myRoleRef.current === "p1";
    const myN = isTm ? room.players?.[MY_PID]?.name : (isP1 ? room.p1name : room.p2name);
    const myTmCol = isTm ? (room.players?.[MY_PID]?.team || tmMyTeamRef.current) : (isP1 ? "red" : "blue");
    const reaction = { pid: MY_PID, name: myN || "?", emoji, ts: Date.now(), team: myTmCol };
    const existing = Array.isArray(room.reactions) ? room.reactions : [];
    const updated = [...existing, reaction].slice(-20);
    if (isTm) await updateTeam(code, { reactions: updated });
    else await update(ref(db, `rooms/${code}`), { reactions: updated });

    // INSTANT LOCAL FEEDBACK: Show reaction on sender's screen immediately
    const id = Math.random().toString(36).slice(2, 8);
    setTmReactions(prev => [...prev, { ...reaction, id }]);
    setTimeout(() => setTmReactions(prev => prev.filter(x => x.id !== id)), 2500);
  }, []);

  const startTeamMatch = useCallback(async () => {
    const room = await loadTeam(tmCodeRef.current);
    if (!room) return;
    // Only include ACTIVE players — exclude anyone who left mid-game
    const allPids = Object.keys(room.players).filter(pid => room.players[pid].active !== false);
    allPids.sort((a, b) => (room.players[a].joinedAt || 0) - (room.players[b].joinedAt || 0));
    // Use player team choices
    let teamA = allPids.filter(pid => room.players[pid].team === "red");  // RED = teamA
    let teamB = allPids.filter(pid => room.players[pid].team === "blue"); // BLUE = teamB
    // Handle players who haven't picked a team — fill into smaller team
    const unpicked = allPids.filter(pid => !room.players[pid].team);
    unpicked.forEach(pid => {
      if (teamA.length <= teamB.length) teamA.push(pid);
      else teamB.push(pid);
    });
    // Edge case: all picked same team — auto split
    if (teamA.length === 0 || teamB.length === 0) {
      const half = Math.ceil(allPids.length / 2);
      teamA = allPids.slice(0, half);
      teamB = allPids.slice(half);
    }
    const tossSalt = `${tmCodeRef.current}|A:${teamA.join(",")}|B:${teamB.join(",")}`;
    const tossWinner = coinFlipAB(tossSalt);
    const tossBy = tossWinner === "A" ? teamA[0] : teamB[0];
    const updated = { ...room, status: "toss", teamA, teamB, gs: null, toss: { winner: tossWinner, tossBy, choice: null, at: Date.now() } };
    await saveTeam(tmCodeRef.current, updated);
    setTmRoom(updated);
    setScreen("teamToss");
  }, []);

  const submitTossChoice = useCallback(async (choice) => {
    const code = tmCodeRef.current;
    if (!code) return;
    const room = await loadTeam(code);
    if (!room?.toss || room.status !== "toss") return;
    if (room.toss.tossBy !== MY_PID) return;
    const winner = room.toss.winner;
    const battingTeam = choice === "bat" ? winner : (winner === "A" ? "B" : "A");
    const gs = initTGS(room.overs, room.teamA?.length || 0, room.teamB?.length || 0);
    gs.battingTeam = battingTeam;
    const updated = { ...room, status: "playing", gs, toss: { ...room.toss, choice } };
    await saveTeam(code, updated);
    setTmRoom(updated);
    // Game is now starting — upgrade onDisconnect to write playerLeft on server-side disconnect
    setupOnDisconnect(code, true);
    // Show the batting/bowling reveal for the toss winner too — same as poll path
    const battingTm = battingTeam === "A" ? TM.red : TM.blue;
    const bowlingTm = battingTeam === "A" ? TM.blue : TM.red;
    setTossReveal({ battingTm, bowlingTm });
    const battArr = gs.battingTeam === "A" ? room.teamA : room.teamB;
    const bowlArr = gs.battingTeam === "A" ? room.teamB : room.teamA;
    setTimeout(() => {
      setTossReveal(null);
      setScreen("teamGame");
      if (MY_PID === battArr[0] || MY_PID === bowlArr[0]) startTimer();
    }, 1500);
  }, [startTimer]);

  const submitTeamPick = useCallback(async (num, isTimeout = false) => {
    if (myPickRef.current !== null || animRef.current) return;
    setMyPick(num);
    if (!isTimeout) SFX.pick(); else showFlash("⏱️ TIME'S UP!", CLR.coral, 900);
    const code = tmCodeRef.current;
    const room = await loadTeam(code);
    if (!room?.gs) { setMyPick(null); return; }
    const gs = room.gs;
    stopTimer();
    const battArr = gs.battingTeam === "A" ? room.teamA : room.teamB;
    const bowlArr = gs.battingTeam === "A" ? room.teamB : room.teamA;
    const iAmBatter = MY_PID === battArr[gs.batterIdx % battArr.length];
    const iAmBowler = MY_PID === bowlArr[gs.bowlerIdx % bowlArr.length];
    if (!iAmBatter && !iAmBowler) { setMyPick(null); return; }
    const field = iAmBatter ? "batterPick" : "bowlerPick";
    const other = iAmBatter ? "bowlerPick" : "batterPick";

    // RACE FIX: update ONLY our pick field atomically — doesn't overwrite other player's pick
    await updateTeam(code, { [`gs/${field}`]: num });

    // Re-read fresh room to see if both picks are now set
    const freshRoom = await loadTeam(code);
    if (!freshRoom?.gs) { setMyPick(null); return; }
    const freshGs = freshRoom.gs;

    if (freshGs[other] !== null && freshGs[field] !== null) {
      // Both picks present — allow either active side to process, with a final version guard.
      const latestRoom = await loadTeam(code);
      if (!latestRoom?.gs) { setMyPick(null); return; }
      const latestGs = latestRoom.gs;
      if (
        latestGs.ver !== freshGs.ver ||
        latestGs.batterPick === null ||
        latestGs.bowlerPick === null
      ) {
        return;
      }
      const battArr2 = latestGs.battingTeam === "A" ? latestRoom.teamA : latestRoom.teamB;
      const bowlArr2 = latestGs.battingTeam === "A" ? latestRoom.teamB : latestRoom.teamA;
      const result = processTBall(latestGs, latestRoom.teamA, latestRoom.teamB);
      const cb = battArr2[latestGs.batterIdx % battArr2.length];
      const cbl = bowlArr2[latestGs.bowlerIdx % bowlArr2.length];
      const batterName = latestRoom.players[cb]?.name || "Batter";
      const bowlerName = latestRoom.players[cbl]?.name || "Bowler";
      setAnimating(true);
      const safe = setTimeout(() => { setAnimating(false); setRevealInfo(null); setMyPick(null); setBurst(null); }, 4000);
      setRevealInfo({
        tmMode: true,
        batterNum: latestGs.batterPick, bowlerNum: latestGs.bowlerPick,
        batterName, bowlerName,
        isOut: result.isOut, rawNum: result.rawNum, runs: result.runs, iAmBatting: true
      });
      playSoundAndBurst(result.isOut, result.rawNum, result.isOut ? "p2" : "p1");
      const lb = {
        ver: result.next.ver, batterPick: latestGs.batterPick, bowlerPick: latestGs.bowlerPick,
        batterPid: cb, bowlerPid: cbl, batterName, bowlerName,
        isOut: result.isOut, rawNum: result.rawNum, runs: result.runs
      };
      await saveTeam(code, { ...latestRoom, gs: result.next, lastBall: lb });
      // For the LAST ball (phase=done), show reveal for 2.5s so everyone sees it, THEN go to result
      const revealDur = result.next.phase === "done" ? 2500 : 1600;
      setTimeout(() => {
        if (screenRef.current !== "teamGame") return;
        clearTimeout(safe); setBurst(null); setRevealInfo(null); setAnimating(false); setMyPick(null);
        setTmRoom(r => r ? { ...r, gs: result.next } : r);
        if (result.next.phase === "done") { SFX.win(); setScreen("teamResult"); return; }
        if (result.next.phase === "break") { setScreen("teamBreak"); return; }
        const ba2 = result.next.battingTeam === "A" ? latestRoom.teamA : latestRoom.teamB;
        const bo2 = result.next.battingTeam === "A" ? latestRoom.teamB : latestRoom.teamA;
        const nb = ba2[result.next.batterIdx % ba2.length];
        const nbl = bo2[result.next.bowlerIdx % bo2.length];
        if (MY_PID === nb || MY_PID === nbl) startTimer();
      }, revealDur);
    }
    // else: genuinely first picker — pick saved atomically above, wait for other
  }, [stopTimer, startTimer, playSoundAndBurst, showFlash]);
  const handleQuitMatch = async () => {
    setQuitConfirm(false);
    stopTimer();
    const s = screenRef.current;
    
    // 1v1 / AI Cleanup
    if (s === "game") {
      stopPoll();
      if (mode === "multi" && roomCodeRef.current) {
        const room = await loadRoom(roomCodeRef.current);
        if (room) await saveRoom(roomCodeRef.current, { ...room, forfeit: myRoleRef.current });
      }
      resetToMenu();
      return;
    }

    // Team Match Cleanup
    if (s === "teamGame" || s === "teamToss" || s === "teamBreak") {
      await cancelOnDisconnect();
      const code = tmCodeRef.current;
      const room = await loadTeam(code);
      if (room) {
        const myTeamSide = (room.teamA || []).includes(MY_PID) ? "A" : "B";
        const myTeamArr = myTeamSide === "A" ? (room.teamA || []) : (room.teamB || []);
        const remainingTeammates = myTeamArr.filter(p => p !== MY_PID);
        if (remainingTeammates.length === 0) {
          await saveTeam(code, { ...room, teamForfeit: MY_PID });
        } else {
          const myN = room.players?.[MY_PID]?.name || "A player";
          await saveTeam(code, { ...room, playerLeft: { pid: MY_PID, name: myN, team: myTeamSide, at: Date.now() } });
        }
      }
      stopTmPoll(); resetToMenu();
    }
  };

  // ── QUIT MATCH OVERLAY RENDERER ──
  const renderQuitOverlay = () => {
    if (!quitConfirm) return null;
    const isMulti = mode === "multi" || screen.startsWith("team");
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(16px,5vw,28px)" }}>
        <div onClick={() => setQuitConfirm(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }} />
        <div style={{ position: "relative", zIndex: 1, background: "rgba(12,36,20,0.97)", border: `2px solid ${CLR.coral}50`, borderRadius: 18, padding: "clamp(18px,5vw,26px)", maxWidth: 320, width: "100%", textAlign: "center", animation: "popIn 0.3s ease" }}>
          <div style={{ fontSize: "clamp(36px,12vw,50px)", marginBottom: 8 }}>🚪</div>
          <h3 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(20px,6vw,26px)", letterSpacing: 4, marginBottom: 7 }}>QUIT MATCH?</h3>
          <p style={{ color: CLR.textDim, fontSize: "clamp(11px,3vw,12px)", marginBottom: 20, lineHeight: 1.6 }}>
            {isMulti ? "Your team will forfeit or your status will be set to away." : "Your current score will be lost."}
          </p>
          <div style={{ display: "flex", gap: 9 }}>
            <button onClick={() => setQuitConfirm(false)} style={{ flex: 1, minHeight: 46, background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.18)", borderRadius: 10, cursor: "pointer", color: CLR.textSecond, fontSize: "clamp(12px,3.5vw,14px)", fontWeight: 700, fontFamily: "'Bebas Neue',serif", letterSpacing: 2, touchAction: "manipulation" }}>RESUME</button>
            <button onClick={handleQuitMatch}
              style={{ flex: 1, minHeight: 46, background: `linear-gradient(135deg,${CLR.coral},${CLR.coral}bb)`, border: "none", borderRadius: 10, cursor: "pointer", color: "#fff", fontSize: "clamp(12px,3.5vw,14px)", fontWeight: 900, fontFamily: "'Bebas Neue',serif", letterSpacing: 2, touchAction: "manipulation" }}>QUIT</button>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════
  // GLOBAL MOBILE-FIRST CSS
  // ═══════════════════════════════════════════════════════
  const CSS = `
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@500;600;700&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{
      height:100%;
      /* prevent iOS text inflation */
      -webkit-text-size-adjust:100%;
      text-size-adjust:100%;
    }
    body{
      background:${CLR.bgDeep};
      min-height:100%;
      min-height:100dvh;
      overflow-x:hidden;
      -webkit-overflow-scrolling:touch;
    }
    /* Kill blue tap flash on Android and iOS */
    *{ -webkit-tap-highlight-color:transparent; }
    /* Remove scrollbars */
    ::-webkit-scrollbar{display:none;}
    /* All buttons/inputs: no tap delay, min 44px touch target */
    button,input{
      touch-action:manipulation;
      font-size:16px; /* prevents iOS zoom on input focus */
    }
    input{ -webkit-appearance:none; }
    @keyframes tPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes bstA{to{transform:translate(var(--dx),var(--dy));opacity:0}}
    @keyframes popIn{0%{transform:scale(0.5) translateY(8px);opacity:0}75%{transform:scale(1.05)}100%{transform:scale(1);opacity:1}}
    @keyframes bnc{0%{transform:translateY(-14px);opacity:0}70%{transform:translateY(2px)}100%{opacity:1;transform:translateY(0)}}
    @keyframes flashA{0%{transform:scale(0.3) translateY(-30px);opacity:0}18%{transform:scale(1.15)}60%{transform:scale(1);opacity:1}88%{opacity:1}100%{opacity:0;transform:translateY(-20px)}}
    @keyframes cardIn{from{transform:translateY(18px) scale(0.96);opacity:0}to{opacity:1;transform:none}}
    @keyframes wigBat{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(6deg)}}
    @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
    @keyframes urgVig{0%,100%{box-shadow:inset 0 0 60px rgba(255,95,87,0.22)}50%{box-shadow:inset 0 0 90px rgba(255,95,87,0.42)}}
    @keyframes headIn{from{opacity:0;letter-spacing:12px}to{opacity:1;letter-spacing:5px}}
    @keyframes scorePop{from{transform:scale(1.25);opacity:0}to{transform:scale(1);opacity:1}}
    @keyframes floatUp{0%{transform:translateY(0) scale(1);opacity:1}100%{transform:translateY(-120px) scale(1.4);opacity:0}}
    @keyframes floatSend{0%{transform:translateY(0) scale(1);opacity:0.9}100%{transform:translateY(-80px) scale(0.8) translateX(30px);opacity:0}}
    @keyframes slideDown{from{transform:translateY(-12px);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes pulseRing{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.08);opacity:0.75}}
    @keyframes revealFlip{0%{transform:scaleY(0.3);opacity:0}60%{transform:scaleY(1.05)}100%{transform:scaleY(1);opacity:1}}
    @keyframes thinkDot{0%,80%,100%{opacity:0.2;transform:scale(0.8)}40%{opacity:1;transform:scale(1)}}
  `;

  // ── SHARED STYLE TOKENS ─────────────────────────────
  // ── SHARED RENDER HELPERS ──
  const renderNameSection = (color = CLR.gold) => (
    <div style={{ marginBottom: 24, padding: 14, background: "rgba(255,255,255,0.05)", border: `1.5px solid ${color}40`, borderRadius: 14, animation: "popIn 0.3s ease" }}>
      <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 6, fontFamily: "'Bebas Neue',serif", textAlign: "left" }}>FIRST, ENTER YOUR NAME</label>
      <input
        value={myName}
        onChange={e => handleSetMyName(e.target.value.slice(0, 12))}
        placeholder="Enter your name"
        maxLength={12}
        style={{ ...inp, textAlign: "center", fontSize: 16, letterSpacing: 2, border: nameErr ? "1.5px solid rgba(255,95,87,0.5)" : inp.border }}
      />
      {nameErr && <p style={{ color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", marginTop: 6, fontWeight: 500, textAlign: "left" }}>{nameErr}</p>}
      <button onClick={() => { if (myName.trim()) setNameConfirmed(true); else setNameErr("Enter your name first!"); }} 
        style={{ ...solidBtn(color, false), minHeight: 38, marginTop: 14, fontSize: 12 }}>CONFIRM NAME & CONTINUE</button>
    </div>
  );

  // Safe-area-aware full-screen wrap
  // One consistent wrap for ALL screens — centered, scrollable when content overflows
  const wrap = {
    minHeight: "100dvh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "max(env(safe-area-inset-top,16px),16px)",
    paddingBottom: "max(env(safe-area-inset-bottom,16px),16px)",
    paddingLeft: "max(env(safe-area-inset-left,16px),16px)",
    paddingRight: "max(env(safe-area-inset-right,16px),16px)",
    position: "relative",
    fontFamily: "'Rajdhani',sans-serif",
    boxSizing: "border-box",
    maxWidth: 500,
    margin: "0 auto",
  };
  // scrollWrap: same — scrolling handled by #root/#page-game
  const scrollWrap = wrap;

  // Frosted glass panel — shrinks to fit screen, scrolls only when too tall
  const panel = (maxW = 430, borderC = acc) => ({
    width: "100%", maxWidth: Math.min(maxW, 460),
    position: "relative", zIndex: 10,
    background: "rgba(255,255,255,0.09)",
    backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
    border: `1.5px solid ${borderC}38`,
    borderRadius: "clamp(14px,4vw,20px)",
    padding: "clamp(10px,2.5vh,20px) clamp(12px,3.5vw,20px)",
    boxShadow: `0 8px 36px rgba(0,0,0,0.32),0 1px 0 rgba(255,255,255,0.10) inset,0 0 40px ${borderC}15`,
    boxSizing: "border-box",
  });

  // Persist player name across sessions
  const handleSetMyName = useCallback((val) => {
    setMyName(val); localStorage.setItem("hc_playerName", val);
    if (val.trim()) setNameErr("");
  }, []);

  // Solid accent button — 48px min touch target
  const solidBtn = (c = CLR.gold, full = true) => ({
    width: full ? "100%" : "auto",
    minHeight: 48,
    padding: "0 18px",
    background: `linear-gradient(135deg,${c},${c}cc)`,
    border: "none", borderRadius: 11, cursor: "pointer",
    color: (c === CLR.gold || c === CLR.green || c === CLR.sky) ? "#0f2d1a" : "#fff",
    fontSize: "clamp(13px,3.8vw,15px)",
    fontWeight: 900, fontFamily: "'Bebas Neue',serif",
    letterSpacing: "clamp(2px,0.6vw,3px)",
    boxShadow: `0 4px 14px ${c}45`,
    transition: "opacity 0.13s",
    touchAction: "manipulation",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  });

  // Ghost button
  const ghostBtn = (full = true) => ({
    width: full ? "100%" : "auto",
    minHeight: 44, padding: "0 18px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.20)",
    borderRadius: 11, cursor: "pointer",
    color: CLR.textSecond,
    fontSize: "clamp(11px,3.2vw,13px)",
    fontWeight: 600, fontFamily: "'Rajdhani',serif",
    letterSpacing: "clamp(1px,0.4vw,2px)",
    touchAction: "manipulation",
    display: "flex", alignItems: "center", justifyContent: "center",
  });

  // Text input — 16px prevents iOS auto-zoom
  const inp = {
    display: "block", width: "100%",
    background: "rgba(255,255,255,0.11)",
    border: "1.5px solid rgba(255,255,255,0.20)",
    borderRadius: 10, padding: "12px 13px",
    color: "#fff", fontSize: 16,
    fontFamily: "'Rajdhani',serif", outline: "none",
    WebkitAppearance: "none",
  };

  const tag = (c) => ({ background: `${c}22`, border: `1px solid ${c}50`, color: c, fontSize: "clamp(7px,2vw,9px)", fontWeight: 700, letterSpacing: 2, padding: "2px 6px", borderRadius: 20 });

  // ─────────────────────────────────────────────────────
  // SCREEN: MENU
  // ─────────────────────────────────────────────────────
  if (screen === "menu") return (
    <><style>{CSS}</style><CricketBg accent={CLR.gold} />
      <div style={wrap}>
        <div style={{ ...panel(), textAlign: "center" }}>

          <div style={{ textAlign: "left", marginBottom: "clamp(2px,1vh,8px)" }}>
            <button onClick={() => { if (window.goToLanding) window.goToLanding(); }} style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, fontFamily: "'Rajdhani',serif", minHeight: 32, touchAction: "manipulation" }}>← BACK</button>
          </div>

          <div style={{ fontSize: "clamp(36px,10vw,56px)", animation: "wigBat 2.2s ease-in-out infinite", display: "block", marginBottom: "clamp(2px,0.8vh,6px)" }}>🏏</div>

          <h1 style={{ fontFamily: "'Bebas Neue',serif", fontSize: "clamp(28px,8vw,46px)", color: CLR.cream, letterSpacing: "clamp(2px,1vw,5px)", lineHeight: 0.9, marginBottom: "clamp(4px,1vh,8px)", animation: "headIn 0.7s ease" }}>
            HAND<br /><span style={{ color: CLR.gold, textShadow: `0 0 24px ${CLR.gold}` }}>CRICKET</span>
          </h1>

          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginBottom: "clamp(4px,1vh,12px)" }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${CLR.gold}45)` }} />
            <span style={{ color: `${CLR.gold}aa`, fontSize: "clamp(7px,2vw,9px)", letterSpacing: 4, fontFamily: "'Bebas Neue',serif" }}>ARCADE EDITION</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${CLR.gold}45,transparent)` }} />
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: "clamp(4px,2vw,10px)", marginBottom: "clamp(6px,1.5vh,14px)" }}>
            {NUMS.map((n, i) => <span key={n} style={{ fontSize: "clamp(16px,5vw,24px)", animation: `bnc 0.5s ${i * 0.07}s ease both`, display: "inline-block" }}>{EMOJI[n]}</span>)}
          </div>

          {/* Player name input */}
          <div style={{ marginBottom: "clamp(6px,1.5vh,14px)", textAlign: "left" }}>
            <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 4, fontFamily: "'Bebas Neue',serif" }}>YOUR NAME</label>
            <input
              value={myName}
              onChange={e => handleSetMyName(e.target.value.slice(0, 12))}
              placeholder="Enter your name"
              maxLength={12}
              style={{ ...inp, textAlign: "center", fontSize: 16, letterSpacing: 2, border: nameErr ? "1.5px solid rgba(255,95,87,0.5)" : inp.border }}
            />
            {nameErr && <p style={{ color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", marginTop: 6, fontWeight: 500, animation: "popIn 0.3s ease" }}>{nameErr}</p>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "clamp(5px,1.2vh,9px)", marginBottom: "clamp(6px,1.5vh,14px)" }}>
            <button onClick={() => { if (!myName.trim()) { setNameErr("Enter your name first!"); return; } setMode("ai"); setScreen("select"); }} style={solidBtn(CLR.gold)}>🤖 VS COMPUTER</button>
            <button onClick={() => { if (!myName.trim()) { setNameErr("Enter your name first!"); return; } setMode("multi"); setScreen("select"); }} style={solidBtn(CLR.sky)}>👥 CHALLENGE A FRIEND <span style={{ fontSize: "clamp(7px,2vw,9px)", letterSpacing: 2, opacity: 0.85 }}>1 VS 1</span></button>
            <button onClick={() => { if (!myName.trim()) { setNameErr("Enter your name first!"); return; } setTmJoinInput(""); setTmJoinErr(""); setTmCode(""); setTmRoom(null); setScreen("teamSetup"); }} style={solidBtn(CLR.orange)}>🏟️ TEAM MATCH  <span style={{ fontSize: "clamp(7px,2vw,9px)", letterSpacing: 2, opacity: 0.8 }}>2–10 PLAYERS</span></button>
          </div>

          <div style={{ padding: "clamp(6px,1.2vh,9px) 12px", background: "rgba(255,95,87,0.10)", border: "1.5px solid rgba(255,95,87,0.25)", borderRadius: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>⏱️</span>
            <span style={{ color: "rgba(255,180,160,0.80)", fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 1 }}>60-SECOND TURNS · AUTO-PICK ON TIMEOUT</span>
          </div>

          <p style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,9px)", letterSpacing: 3, marginTop: "clamp(4px,1vh,10px)", fontFamily: "'Bebas Neue',serif" }}>SAME NUMBER = OUT · MORE RUNS = WIN</p>
          <Footer />
        </div>
      </div></>
  );

  // ─────────────────────────────────────────────────────
  // SCREEN: SELECT CHALLENGE
  // ─────────────────────────────────────────────────────
  if (screen === "select") return (
    <><style>{CSS}</style><CricketBg accent={CLR.gold} />
      <div style={scrollWrap}>
        <div style={{ ...panel(), "width": "100%" }}>
          <button onClick={() => resetToMenu()} style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, marginBottom: 14, fontFamily: "'Rajdhani',serif", minHeight: 36, touchAction: "manipulation" }}>← BACK</button>
          <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(22px,7vw,30px)", letterSpacing: 4, marginBottom: 3 }}>SELECT MODE</h2>
          <p style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,11px)", letterSpacing: 2, marginBottom: 14 }}>{mode === "multi" ? "FRIEND GETS SAME MODE" : "6 MODES · 60 SECS PER TURN"}</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {CHALLENGES.map((c, i) => (
              <button key={c.id}
                onClick={() => {
                  setCh(c); if (mode === "ai") { resetAll(c); setScreen("game"); } else {
                    // FIX: only clear room/join fields — keep myName from menu screen
                    setJoinInput(""); setJoinErr("");
                    setScreen("multiSetup");
                  }
                }}
                style={{ background: `linear-gradient(135deg,${c.color}15,${c.color}06)`, border: `1.5px solid ${c.color}30`, borderRadius: 12, padding: "clamp(10px,3vw,14px) 13px", cursor: "pointer", textAlign: "left", animation: `cardIn 0.3s ${i * 0.04}s ease both`, display: "flex", alignItems: "center", gap: 10, touchAction: "manipulation", minHeight: 60 }}>
                <span style={{ fontSize: "clamp(22px,7vw,28px)", flexShrink: 0 }}>{c.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2, flexWrap: "wrap" }}>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: "clamp(13px,3.8vw,15px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1 }}>{c.name}</span>
                    {c.tag && <span style={tag(c.color)}>{c.tag}</span>}
                  </div>
                  <div style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.desc}</div>
                </div>
                <span style={{ color: `${c.color}70`, fontSize: 20, fontFamily: "'Bebas Neue',serif", flexShrink: 0 }}>›</span>
              </button>
            ))}
          </div>
          <Footer />
        </div>
      </div></>
  );

  // ─────────────────────────────────────────────────────
  // SCREEN: MULTI SETUP
  // ─────────────────────────────────────────────────────
  if (screen === "multiSetup") return (
    <><style>{CSS}</style><CricketBg accent={acc} />
      <div style={wrap}>
        <div style={panel()}>
          <button onClick={() => setScreen("select")} style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, marginBottom: 14, fontFamily: "'Rajdhani',serif", minHeight: 36, touchAction: "manipulation" }}>← BACK</button>
          <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(20px,6.5vw,26px)", letterSpacing: 4, marginBottom: 3 }}>{ch?.icon} ONLINE MATCH</h2>
          <p style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", letterSpacing: 2, marginBottom: 16 }}>{ch?.name} · CHALLENGE A FRIEND ANYWHERE</p>

          {!nameConfirmed ? (
            renderNameSection(acc)
          ) : (
            <>
              <button onClick={createRoom} style={{ ...solidBtn(acc), marginBottom: 18 }}>🏠 CREATE ROOM</button>

              <div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", paddingTop: 16 }}>
                <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 7 }}>JOIN FRIEND'S ROOM</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={joinInput} onChange={e => setJoinInput(e.target.value.toUpperCase().slice(0, 4))} placeholder="ABCD" maxLength={4}
                    style={{ ...inp, flex: 1, fontSize: "clamp(20px,6vw,26px)", textAlign: "center", letterSpacing: "clamp(4px,2vw,8px)", padding: "10px 8px" }} />
                  <button onClick={joinRoom} style={{ ...solidBtn(acc, false), minWidth: 64 }}>JOIN</button>
                </div>
                {joinErr && <p style={{ color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", marginTop: 7 }}>{joinErr}</p>}
              </div>
              
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 12 }}>
                <span style={{ color: CLR.textFaint, fontSize: 10, letterSpacing: 1, fontFamily: "'Bebas Neue',serif" }}>PLAYING AS: <span style={{ color: CLR.textDim }}>{myName}</span></span>
                <span style={{ color: "rgba(255,255,255,0.1)", fontSize: 10 }}>·</span>
                <button onClick={() => setNameConfirmed(false)} style={{ background: "none", border: "none", color: CLR.textFaint, cursor: "pointer", fontSize: 10, letterSpacing: 1, fontFamily: "'Bebas Neue',serif", textDecoration: "underline", padding: 0 }}>✎ EDIT</button>
              </div>
            </>
          )}
          <Footer />
        </div>
      </div></>
  );

  // ─────────────────────────────────────────────────────
  // SCREEN: LOBBY (1V1)
  // ─────────────────────────────────────────────────────
  if (screen === "lobby" && room1v1) {
    const isHost = myRole === "p1";
    const p1n = room1v1.p1name || "P1";
    const p2n = room1v1.p2name || "Waiting...";
    return (
      <><style>{CSS}</style><CricketBg accent={acc} />
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={panel(430, acc)}>
            <button onClick={() => {
              stopTimer(); stopPoll();
              setRoomCode(""); setRoom1v1(null); setScreen("multiSetup");
            }} style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, marginBottom: 14, fontFamily: "'Rajdhani',serif", minHeight: 36, touchAction: "manipulation" }}>← BACK</button>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(20px,6vw,26px)", letterSpacing: 4, marginBottom: 3 }}>MATCH LOBBY</h2>
            <p style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", letterSpacing: 1, marginBottom: 12 }}>Invite your friend to join the match</p>

            <div style={{ background: `linear-gradient(135deg,${acc}18,${acc}08)`, border: `2px solid ${acc}45`, borderRadius: 16, padding: "12px", marginBottom: 14 }}>
              <div style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 4, marginBottom: 3, fontFamily: "'Bebas Neue',serif" }}>ROOM CODE</div>
              <div style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(32px,11vw,48px)", letterSpacing: "clamp(6px,3vw,12px)", textShadow: `0 0 18px ${acc}60` }}>{roomCode}</div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <button onClick={() => shareJoinLink("1v1", roomCode, `🏏 Hand Cricket — Challenge Me!`)} style={{ ...solidBtn(acc), flex: 1, minWidth: 140 }}>🔗 SHARE LINK</button>
              <button onClick={() => copyText(roomCode, `Room code ${roomCode} copied!`)} style={{ ...ghostBtn(), flex: 1, minWidth: 100 }}>📋 COPY CODE</button>
            </div>

            <div style={{ background: "rgba(0,0,0,0.15)", borderRadius: 12, padding: "12px", marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ color: TM.red.color, fontFamily: "'Bebas Neue',serif", fontSize: 13 }}>🔴 RED (HOST)</div>
                <div style={{ color: TM.blue.color, fontFamily: "'Bebas Neue',serif", fontSize: 13 }}>🔵 BLUE</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, padding: "8px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#fff", fontSize: 12 }}>{p1n}</span>
                </div>
                <div style={{ color: CLR.textDim, display: "flex", alignItems: "center" }}>VS</div>
                <div style={{ flex: 1, padding: "8px", borderRadius: 8, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#fff", fontSize: 12 }}>{p2n}</span>
                </div>
              </div>
            </div>

            {room1v1.p2id ? (
              isHost ? (
                <button onClick={startMultiMatch} style={solidBtn(acc)}>🏏 START TOSS</button>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: acc, animation: "blink 1s infinite" }} />
                  <span style={{ color: CLR.textDim, fontSize: 12 }}>Waiting for host to start...</span>
                </div>
              )
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: acc, animation: "blink 1s infinite" }} />
                <span style={{ color: CLR.textDim, fontSize: 12 }}>Waiting for opponent…</span>
              </div>
            )}
            <Footer />
          </div>
        </div></>
    );
  }

  // ─────────────────────────────────────────────────────
  // SCREEN: TOSS (1V1)
  // ─────────────────────────────────────────────────────
  if (screen === "multiToss" && room1v1) {
    const isWinner = room1v1.toss?.winner === myRole;
    const isP1 = room1v1.toss?.winner === "p1";
    const winnerName = isP1 ? room1v1.p1name : (room1v1.p2name || "P2");
    return (
      <><style>{CSS}</style><CricketBg accent={CLR.gold} />
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={panel(430, CLR.gold)}>
            <div style={{ fontSize: 50, marginBottom: 8 }}>🪙</div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: 28, letterSpacing: 5 }}>THE TOSS</h2>

            <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: 16, border: "1.5px solid rgba(255,255,255,0.1)", marginBottom: 20, textAlign: "center" }}>
              <div style={{ color: CLR.gold, fontSize: 18, fontFamily: "'Bebas Neue',serif", marginBottom: 7, textAlign: "center" }}>RESULTS</div>
              <div style={{ color: "#fff", fontSize: 22, fontFamily: "'Bebas Neue',serif", letterSpacing: 2, textAlign: "center" }}>{isWinner ? "YOU WON THE TOSS!" : `${winnerName.toUpperCase()} WON THE TOSS`}</div>
            </div>

            {isWinner ? (
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button onClick={() => submitMultiTossChoice("bat")} style={{ ...solidBtn(CLR.green, false), flex: 1 }}>🏏 BAT FIRST</button>
                <button onClick={() => submitMultiTossChoice("bowl")} style={{ ...solidBtn(CLR.coral, false), flex: 1 }}>⚾ BOWL FIRST</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", textAlign: "center" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: CLR.gold, animation: "blink 1s infinite" }} />
                <div style={{ color: CLR.textDim, textAlign: "center" }}>Waiting for choice…</div>
              </div>
            )}
            <Footer />
          </div>
        </div></>
    );
  }

  // ─────────────────────────────────────────────────────
  // SCREEN: INNINGS BREAK
  // ─────────────────────────────────────────────────────
  if (screen === "break" && gs) return (
    <><style>{CSS}</style><CricketBg accent={acc} />
      {showPromo && <PromoPopup onClose={() => { setShowPromo(false); setAdDone(true); }} />}
      <div style={{ ...wrap, textAlign: "center" }}>
        <div style={panel()}>
          <div style={{ fontSize: "clamp(44px,14vw,62px)", marginBottom: 10 }}>🔄</div>
          <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(22px,7vw,30px)", letterSpacing: 5, marginBottom: 16 }}>INNINGS BREAK</h2>
          <div style={{ background: `linear-gradient(135deg,${acc}18,${acc}08)`, border: `2px solid ${acc}40`, borderRadius: 14, padding: "clamp(14px,4vw,22px)", marginBottom: 14 }}>
            <div style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 4, marginBottom: 6, fontFamily: "'Bebas Neue',serif" }}>TARGET TO WIN</div>
            <div style={{ fontFamily: "'Bebas Neue',serif", color: acc, fontSize: "clamp(52px,18vw,72px)", lineHeight: 1, textShadow: `0 0 24px ${acc}50` }}>{gs.target}</div>
            <div style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,11px)", marginTop: 6, letterSpacing: 2 }}>NEXT BATTER CHASES THIS</div>
          </div>
          {/* Cricket tip */}
          {(() => {
            const tip = CRICKET_TIPS[gs.ver % CRICKET_TIPS.length];
            return (
              <div style={{ textAlign: "left", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ color: acc, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, marginBottom: 5 }}>🏏 {tip.title.toUpperCase()}</div>
                <p style={{ color: "rgba(255,255,255,0.70)", fontSize: "clamp(11px,3.2vw,13px)", lineHeight: 1.6, margin: 0 }}>{tip.body}</p>
              </div>
            );
          })()}
          {/* Promo Banner */}
          <PromoBanner />
          {/* Start 2nd Innings button — AI goes direct; multi uses ready-gate so both players finish their ad first */}
          {mode === "ai" ? (
            <button onClick={() => {
              setInn2Starting(true); stopTimer();
              setMyPick(null); setRevealInfo(null); setAnimating(false);
              setBurst(null); setFlash(null); setStreak(0); setQuitConfirm(false);
              const newGs = { ...gs, phase: "picking", ver: gs.ver + 1 };
              setGs(newGs); setScreen("game");
              setTimeout(() => setInn2Starting(false), 500);
            }} style={solidBtn(acc)}>🏏 START 2ND INNINGS →</button>
          ) : adDone ? (
            // Ad finished — show the ready button
            <button onClick={() => {
              (async () => {
                const code = roomCodeRef.current;
                const myR = myRoleRef.current;
                const readyField = myR === "p1" ? "inn2ReadyP1" : "inn2ReadyP2";
                const otherField = myR === "p1" ? "inn2ReadyP2" : "inn2ReadyP1";
                // Write my ready flag
                await updateRoom(code, { [readyField]: true });
                setInn2ReadyCount(1); // show "waiting for opponent"
                // Check if both are ready now — if so, start the innings
                const room = await loadRoom(code);
                if (room?.[otherField]) {
                  // Both ready — write new gs
                  setInn2Starting(true); stopTimer();
                  setMyPick(null); setRevealInfo(null); setAnimating(false);
                  setBurst(null); setFlash(null); setStreak(0); setQuitConfirm(false);
                  const newGs = { ...gs, phase: "picking", ver: gs.ver + 1 };
                  await saveRoom(code, { ...room, gs: newGs, inn2ReadyP1: null, inn2ReadyP2: null });
                  setGs(newGs); setScreen("game");
                  setTimeout(() => setInn2Starting(false), 500);
                }
                // else: poll will detect when other player sets their ready flag
              })();
            }} disabled={inn2ReadyCount > 0} style={{ ...solidBtn(acc), animation: "popIn 0.3s ease", opacity: inn2ReadyCount > 0 ? 0.6 : 1 }}>
              {inn2ReadyCount > 0 ? "⏳ Waiting for opponent…" : "🏏 READY FOR 2ND INNINGS →"}
            </button>
          ) : (
            // Ad still showing — button not yet available
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", padding: "12px 0", color: "rgba(255,255,255,0.40)", fontSize: "clamp(10px,2.8vw,12px)" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: acc, animation: "blink 1s infinite" }} />
              Please watch the ad, then 2nd innings button will appear…
            </div>
          )}
          <Footer />
        </div>
      </div></>
  );

  // ─────────────────────────────────────────────────────
  // SCREEN: GAME
  // ─────────────────────────────────────────────────────
  if (screen === "game" && gs && ch) {
    const MAX_BALLS = ch.overs * 6, MAX_WKTS = ch.wickets;
    const batterTeam = gs.inn === 1 ? (gs.batFirst ? "p1" : "p2") : (gs.batFirst ? "p2" : "p1");
    const iAmBatting = mode === "ai" ? batterTeam === "p1" : batterTeam === myRole;
    const myScore = mode === "ai" ? gs.p1score : myRole === "p1" ? gs.p1score : gs.p2score;
    const oppScore = mode === "ai" ? gs.p2score : myRole === "p1" ? gs.p2score : gs.p1score;
    const displayScore = iAmBatting ? myScore : oppScore;
    const isPow = !!(ch.powerover && Math.floor(gs.balls / 6) < ch.powerover);
    const need = gs.inn === 2 && gs.target !== null ? gs.target - (iAmBatting ? displayScore : oppScore) : null;
    const urgent = timerOn && timeLeft <= 10;
    // FIX: use myPick state (set instantly on tap) not gs.p1pick/p2pick (only synced from Firebase)
    // This prevents double-picks — numpad disables the moment finger lifts
    const myHasPicked = mode === "multi" && myPick !== null;
    const padDisabled = animating || !!revealInfo || myHasPicked;
    const oppName = mode === "multi" ? (myRole === "p1" ? p2name || "P2" : p1name || "P1") : "AI";
    // Use entered name everywhere instead of plain "YOU"
    const myDisplayName = myName.trim() || "YOU";

    return (
      <><style>{CSS}</style><CricketBg accent={acc} />
        {burst && <Burst type={burst} />}
        {flash && <div style={{ position: "fixed", top: "16%", left: 0, right: 0, textAlign: "center", zIndex: 800, pointerEvents: "none", color: flash.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(28px,9vw,42px)", letterSpacing: 4, textShadow: `0 0 24px ${flash.color}`, animation: "flashA 1.3s ease forwards", padding: "0 8px" }}>{flash.msg}</div>}

        {/* Incoming emoji reactions — float up dramatically */}
        {mode === "multi" && tmReactions.map(r => {
          const left = 15 + (parseInt(r.id, 36) % 70);
          return (
            <div key={r.id} style={{ position: "fixed", bottom: "22%", left: `${left}%`, zIndex: 950, pointerEvents: "none", animation: "floatUp 2.4s ease forwards", textAlign: "center" }}>
              <div style={{ fontSize: "clamp(28px,9vw,36px)" }}>{r.emoji}</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(8px,2.2vw,10px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1, marginTop: 2 }}>{r.name}</div>
            </div>
          );
        })}

        {/* Urgent vignette */}
        {urgent && <div style={{ position: "fixed", inset: 0, zIndex: 2, pointerEvents: "none", animation: "urgVig 0.5s infinite" }} />}

        {renderQuitOverlay()}

        <div style={wrap}>
          <div style={{ ...panel(430, urgent ? CLR.coral : acc), transition: "border-color 0.3s", width: "100%" }}>

            {/* ── TOP BAR ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <button onClick={() => setQuitConfirm(true)}
                  style={{ background: "rgba(255,95,87,0.12)", border: "1.5px solid rgba(255,95,87,0.28)", borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", fontWeight: 700, fontFamily: "'Bebas Neue',serif", letterSpacing: 1, minHeight: 30, touchAction: "manipulation" }}>✕ QUIT</button>
                <span style={{ fontSize: "clamp(12px,3.5vw,15px)" }}>{ch.icon}</span>
                <span style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>{ch.name}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {streak >= 3 && <div style={{ padding: "2px 7px", borderRadius: 20, background: "rgba(245,197,24,0.18)", border: `1px solid ${CLR.gold}40`, color: CLR.gold, fontSize: "clamp(8px,2.2vw,9px)", fontWeight: 700, animation: "blink 0.7s infinite" }}>🔥{streak}×</div>}
                {isPow && <div style={{ padding: "2px 7px", borderRadius: 20, background: "rgba(56,212,245,0.18)", border: `1px solid ${CLR.sky}40`, color: CLR.sky, fontSize: "clamp(8px,2.2vw,9px)", fontWeight: 700 }}>⚡2×</div>}
              </div>
            </div>

            {/* ── ROLE BANNER: who bats, who bowls ── */}
            <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
              {/* BATTER */}
              <div style={{
                flex: 1, padding: "8px 10px", borderRadius: 11,
                background: iAmBatting ? "rgba(76,255,110,0.13)" : "rgba(255,255,255,0.05)",
                border: `2px solid ${iAmBatting ? "rgba(76,255,110,0.50)" : "rgba(255,255,255,0.08)"}`,
                display: "flex", alignItems: "center", gap: 7
              }}>
                <span style={{ fontSize: "clamp(18px,5.5vw,24px)", flexShrink: 0 }}>🏏</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: iAmBatting ? CLR.green : "rgba(255,255,255,0.30)", fontSize: "clamp(7px,1.8vw,8px)", letterSpacing: 3, fontFamily: "'Bebas Neue',serif", marginBottom: 1 }}>BATTING</div>
                  <div style={{ color: iAmBatting ? "#fff" : "rgba(255,255,255,0.35)", fontSize: "clamp(11px,3.2vw,13px)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {iAmBatting ? myDisplayName : oppName}
                  </div>
                  {iAmBatting && <div style={{ color: CLR.green, fontSize: "clamp(7px,1.8vw,8px)", letterSpacing: 1, marginTop: 1 }}>◀ YOUR TURN</div>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", color: "rgba(255,255,255,0.22)", fontSize: "clamp(9px,2.5vw,11px)", fontWeight: 700, flexShrink: 0 }}>VS</div>
              {/* BOWLER */}
              <div style={{
                flex: 1, padding: "8px 10px", borderRadius: 11,
                background: !iAmBatting ? "rgba(255,95,87,0.13)" : "rgba(255,255,255,0.05)",
                border: `2px solid ${!iAmBatting ? "rgba(255,95,87,0.50)" : "rgba(255,255,255,0.08)"}`,
                display: "flex", alignItems: "center", gap: 7, flexDirection: "row-reverse", textAlign: "right"
              }}>
                <span style={{ fontSize: "clamp(18px,5.5vw,24px)", flexShrink: 0 }}>⚾</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: !iAmBatting ? CLR.coral : "rgba(255,255,255,0.30)", fontSize: "clamp(7px,1.8vw,8px)", letterSpacing: 3, fontFamily: "'Bebas Neue',serif", marginBottom: 1 }}>BOWLING</div>
                  <div style={{ color: !iAmBatting ? "#fff" : "rgba(255,255,255,0.35)", fontSize: "clamp(11px,3.2vw,13px)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {!iAmBatting ? myDisplayName : oppName}
                  </div>
                  {!iAmBatting && <div style={{ color: CLR.coral, fontSize: "clamp(7px,1.8vw,8px)", letterSpacing: 1, marginTop: 1 }}>◀ YOUR TURN</div>}
                </div>
              </div>
            </div>

            {/* ── SCOREBOARD ── */}
            <div style={{ background: "rgba(0,0,0,0.25)", border: "1.5px solid rgba(255,255,255,0.12)", borderRadius: 14, padding: "clamp(10px,3vw,15px) clamp(12px,3.5vw,16px)", marginBottom: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ color: `${acc}bb`, fontSize: "clamp(7px,2vw,9px)", letterSpacing: 3, marginBottom: 3, fontFamily: "'Bebas Neue',serif" }}>
                    {gs.inn === 2
                      ? (iAmBatting ? `CHASING ${gs.target}` : `DEFENDING ${gs.target}`)
                      : iAmBatting ? "YOUR SCORE" : `${oppName}'S SCORE`}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ color: "#fff", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(40px,12vw,54px)", lineHeight: 1, animation: revealInfo && !revealInfo.isOut ? "scorePop 0.3s ease" : "none" }}>{displayScore}</span>
                    <span style={{ color: CLR.coral, fontSize: "clamp(14px,4vw,18px)", fontWeight: 700 }}>/{gs.wkts}W</span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: CLR.textDim, fontSize: "clamp(7px,2vw,9px)", letterSpacing: 2, marginBottom: 3, fontFamily: "'Bebas Neue',serif" }}>OVERS</div>
                  <div style={{ color: acc, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(20px,6vw,26px)" }}>{Math.floor(gs.balls / 6)}.{gs.balls % 6}</div>
                  {need !== null && (
                    <div style={{ marginTop: 5, padding: "2px 8px", background: need <= 6 ? "rgba(76,255,110,0.14)" : "rgba(255,95,87,0.11)", border: `1.5px solid ${need <= 6 ? "rgba(76,255,110,0.38)" : "rgba(255,95,87,0.32)"}`, borderRadius: 20 }}>
                      <span style={{ color: need <= 6 ? CLR.green : CLR.coral, fontSize: "clamp(9px,2.5vw,10px)", fontWeight: 900, fontFamily: "'Bebas Neue',serif", letterSpacing: 1 }}>NEED {Math.max(0, need)}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Over progress bar */}
              <div style={{ marginTop: 9, display: "flex", gap: 2 }}>
                {Array.from({ length: MAX_BALLS }, (_, i) => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < gs.balls ? acc : "rgba(255,255,255,0.10)", transition: "background 0.25s" }} />
                ))}
              </div>
            </div>

            {/* ── TIMER + REVEAL ROW ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9, minHeight: 70 }}>
              {timerOn && !revealInfo && <TimerRing time={timeLeft} total={TURN_SECS} color={acc} size={54} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                {revealInfo ? (
                  <div style={{
                    borderRadius: 12, padding: "10px 12px",
                    background: revealInfo.isOut ? "rgba(255,95,87,0.11)" : "rgba(76,255,110,0.07)",
                    border: `1.5px solid ${revealInfo.isOut ? "rgba(255,95,87,0.32)" : "rgba(76,255,110,0.25)"}`,
                    animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1)"
                  }}>
                    <div style={{ display: "flex", justifyContent: "center", gap: 18, marginBottom: 6 }}>
                      {(() => {
                        // M2 FIX: always show YOUR pick under "YOU", opponent's under their name
                        // In multi mode p2 role: p2num is yours, p1num is theirs
                        const myNum = mode === "multi" && myRole === "p2" ? revealInfo.p2num : revealInfo.p1num;
                        const theirNum = mode === "multi" && myRole === "p2" ? revealInfo.p1num : revealInfo.p2num;
                        return [{ l: myDisplayName.slice(0, 6).toUpperCase(), n: myNum }, { l: oppName.toUpperCase().slice(0, 6), n: theirNum }];
                      })().map(({ l, n }, idx) => (
                        <div key={idx} style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "clamp(6px,1.8vw,8px)", color: CLR.textFaint, letterSpacing: 2, marginBottom: 2 }}>{l}</div>
                          <div style={{ fontSize: "clamp(26px,8vw,34px)", animation: `bnc 0.35s ${idx * 0.08}s ease both`, lineHeight: 1 }}>{EMOJI[n]}</div>
                          <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(15px,4.5vw,19px)" }}>{n}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ textAlign: "center", color: revealInfo.isOut ? CLR.coral : iAmBatting ? CLR.green : CLR.coral, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(16px,5vw,20px)", letterSpacing: 2 }}>
                      {revealInfo.isOut ? (iAmBatting ? (revealInfo.isTimeout ? "⏱️ TIMED OUT!" : "💀 CAUGHT OUT!") : "🎯 WICKET!")
                        : revealInfo.rawNum === 6 ? "💥 SIX! MAXIMUM!"
                          : revealInfo.rawNum === 4 ? "🔥 BOUNDARY FOUR!"
                            : iAmBatting ? `+${revealInfo.runs} RUNS` : `${oppName.slice(0, 6)} +${revealInfo.runs}`}
                    </div>
                    {revealInfo.isPow && !revealInfo.isOut && <div style={{ textAlign: "center", color: CLR.sky, fontSize: "clamp(8px,2.2vw,9px)", marginTop: 2, letterSpacing: 2 }}>⚡ POWER: {revealInfo.rawNum}×2={revealInfo.runs}</div>}
                  </div>
                ) : (
                  <div style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", letterSpacing: 0.5 }}>
                    {myHasPicked ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: acc, animation: "blink 0.85s infinite", flexShrink: 0 }} />
                        <span>Waiting for {oppName}…</span>
                      </div>
                    ) : iAmBatting ? "PICK A NUMBER TO SCORE" : "MATCH THEIR NUMBER FOR WICKET"}
                  </div>
                )}
              </div>
            </div>

            {/* ── NUMBER PAD ── */}
            <NumPad onPick={mode === "ai" ? executePick : executeMultiPick} disabled={padDisabled} myPick={myPick} acc={acc} urgent={urgent} />

            {/* ── BALL HISTORY ── */}
            <div style={{ marginTop: 10, minHeight: 26 }}>
              {ch.blind && gs.hist.length > 0 ? (
                <div style={{ textAlign: "center", color: CLR.textFaint, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>🙈 HISTORY HIDDEN — TRUST YOUR GUT</div>
              ) : (
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
                  {gs.hist.slice(-16).map((e, i) => <BallDot key={i} entry={e} />)}
                </div>
              )}
            </div>

            {/* ── WICKET PIPS ── */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 9 }}>
              {Array.from({ length: MAX_WKTS }, (_, i) => (
                <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < gs.wkts ? CLR.coral : "rgba(255,255,255,0.16)", boxShadow: i < gs.wkts ? `0 0 7px ${CLR.coral}` : "none", transition: "all 0.28s" }} />
              ))}
              <span style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", marginLeft: 4, letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>WICKETS</span>
            </div>

            {/* Emoji reactions for 1v1 */}
            {mode === "multi" && (
              <div style={{ marginTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
                <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3, textAlign: "center", marginBottom: 6, fontFamily: "'Bebas Neue',serif" }}>REACT</div>
                <div style={{ display: "flex", justifyContent: "center", gap: "clamp(6px,2vw,10px)" }}>
                  {["👏", "🔥", "💀", "😂", "😱", "🎯"].map(emoji => (
                    <button key={emoji} onClick={() => sendReaction(emoji)}
                      style={{
                        width: "clamp(38px,10vw,46px)", height: "clamp(38px,10vw,46px)", borderRadius: 10,
                        border: "1.5px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)",
                        fontSize: "clamp(18px,5.5vw,22px)", cursor: "pointer", touchAction: "manipulation",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "transform 0.1s", WebkitTapHighlightColor: "transparent"
                      }}
                      onTouchStart={e => e.currentTarget.style.transform = "scale(0.88)"}
                      onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Footer />
          </div>
        </div></>
    );
  }

  // ─────────────────────────────────────────────────────
  // SCREEN: RESULT
  // ─────────────────────────────────────────────────────
  if (screen === "result" && gs && ch && !inn2Starting) {
    const myTeam = mode === "ai" ? "p1" : myRole;
    const oppTeam = myTeam === "p1" ? "p2" : "p1";
    const oppDisplayName = mode === "multi" ? (myTeam === "p1" ? p2name || "P2" : p1name || "P1") : "AI";
    let myF, oppF, won, isDraw;
    if (ch.battingOnly) { myF = gs.p1score; oppF = ch.chaseTarget || 45; won = myF >= oppF; isDraw = false; }
    else { myF = myTeam === "p1" ? gs.p1score : gs.p2score; oppF = oppTeam === "p1" ? gs.p1score : gs.p2score; won = gs.winner === myTeam; isDraw = gs.winner === "draw"; }
    const rc = isDraw ? CLR.draw : won ? CLR.win : CLR.lose;
    const fullHist = [...(gs.inn1hist || []), ...gs.hist];
    // H2 FIX: run rate must use only MY batting balls, not all balls in both innings
    const myBalls = fullHist.filter(e => e.batterTeam === myTeam).length;
    const rr = myBalls > 0 ? ((myF / myBalls) * 6).toFixed(1) : "0.0";
    const sixes = fullHist.filter(e => e.batterTeam === myTeam && !e.isOut && e.rawNum === 6).length;
    const fours = fullHist.filter(e => e.batterTeam === myTeam && !e.isOut && e.rawNum === 4).length;

    return (
      <><style>{CSS}</style><CricketBg accent={rc} />
        {showPromo && <PromoPopup onClose={() => { setShowPromo(false); startMenuLockAfterAd(); }} />}
        <div style={scrollWrap}>
          <div style={{ ...panel(430, rc), textAlign: "center", width: "100%" }}>

            <div style={{ fontSize: "clamp(52px,16vw,72px)", marginBottom: 8, animation: "popIn 0.6s cubic-bezier(0.34,1.56,0.64,1)", display: "block" }}>
              {isDraw ? "🤝" : won ? "🏆" : "💀"}
            </div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", fontSize: "clamp(32px,10vw,46px)", color: rc, letterSpacing: "clamp(3px,1.2vw,6px)", marginBottom: 5, textShadow: `0 0 24px ${rc}50` }}>
              {isDraw ? "TIE GAME!" : won ? "VICTORY!" : "ELIMINATED"}
            </h2>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 16, padding: "3px 12px", borderRadius: 20, background: `${ch.color}1a`, border: `1.5px solid ${ch.color}38`, color: ch.color, fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, fontFamily: "'Bebas Neue',serif" }}>
              {ch.icon} {ch.name} · {mode === "multi" ? "ONLINE" : "VS AI"}
            </div>

            {/* Scores */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[{ l: myName.trim() || "YOU", s: myF, c: won && !isDraw ? CLR.win : "#fff" }, { l: oppDisplayName.slice(0, 8), s: oppF, c: !won && !isDraw ? CLR.lose : CLR.textDim }].map(({ l, s, c }) => (
                <div key={l} style={{ flex: 1, padding: "clamp(10px,3vw,14px) 8px", background: "rgba(0,0,0,0.20)", border: "1.5px solid rgba(255,255,255,0.12)", borderRadius: 12 }}>
                  <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3, marginBottom: 3, fontFamily: "'Bebas Neue',serif" }}>{l}</div>
                  <div style={{ color: c, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(38px,11vw,50px)", lineHeight: 1 }}>{s}</div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: 5, marginBottom: 12, padding: "10px clamp(8px,2.5vw,12px)", background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 11 }}>
              {[{ l: "R/OVER", v: rr }, { l: "SIXES", v: sixes }, { l: "FOURS", v: fours }, { l: "BALLS", v: myBalls }].map(({ l, v }) => (
                <div key={l} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ color: CLR.textFaint, fontSize: "clamp(6px,1.8vw,7px)", letterSpacing: 1, marginBottom: 2, fontFamily: "'Bebas Neue',serif" }}>{l}</div>
                  <div style={{ color: acc, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(18px,5.5vw,22px)" }}>{v}</div>
                </div>
              ))}
            </div>
            {/* Match summary — real paragraph text for Google to index */}
            <div style={{ textAlign: "left", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,9px)", letterSpacing: 3, marginBottom: 6 }}>MATCH SUMMARY</div>
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "clamp(11px,3vw,13px)", lineHeight: 1.6, margin: 0 }}>
                {isDraw
                  ? `An incredible tie! Both players finished level after ${myBalls} balls played. ${myName.trim() || "You"} scored ${myF} runs — a result that could have gone either way.`
                  : won
                    ? `${myName.trim() || "You"} won this ${ch.name} match with ${myF} runs from ${myBalls} balls, hitting ${sixes} sixes and ${fours} fours at a run rate of ${rr} per over. Well played!`
                    : `${oppDisplayName} won this ${ch.name} match. ${myName.trim() || "You"} scored ${myF} runs from ${myBalls} balls with ${sixes} sixes and ${fours} fours. A good effort — challenge them again!`
                }
              </p>
            </div>

            {/* Ball dots */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center", marginBottom: 12 }}>
              {fullHist.map((e, i) => <BallDot key={i} entry={e} />)}
            </div>

            {/* Promo Banner */}
            <PromoBanner />

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => runNavOnce(async () => {
                stopTimer();
                if (stopTmPollRef.current) stopTmPollRef.current();
                if (mode === "ai") {
                  stopPoll();
                  resetAll(ch); setScreen("game");
                } else {
                  // Stay in same room — reset game state and re-trigger toss
                  const code = roomCodeRef.current;
                  clearMatchUiState();
                  setGs(null);
                  const room = await loadRoom(code);
                  if (room) {
                    const tossWinner = Math.random() > 0.5 ? "p1" : "p2";
                    const tossById = tossWinner === "p1" ? room.p1id : room.p2id;
                    // Clean reset — don't spread stale room fields
                    const reset = {
                      status: "toss",
                      p1id: room.p1id, p1name: room.p1name, p1team: room.p1team,
                      p2id: room.p2id, p2name: room.p2name, p2team: room.p2team,
                      chId: room.chId,
                      gs: null, lastBall: null,
                      p1pick: null, p2pick: null,
                      forfeit: null, reactions: [],
                      toss: { winner: tossWinner, tossBy: tossById, choice: null, at: Date.now() },
                      createdAt: Date.now(),
                    };
                    await saveRoom(code, reset);
                    // Ensure the next screen has data immediately (prevents blank UI until poll tick).
                    setRoom1v1(reset);
                    // Use ref to avoid any stale closure value.
                    startPoll(code, myRoleRef.current);
                    setTimeout(() => setScreen("multiToss"), 0);
                  } else {
                    // Room gone — fall back to setup
                    setTimeout(() => setScreen("multiSetup"), 0);
                  }
                }
              })} style={solidBtn(rc)}>🔁 PLAY AGAIN</button>
              <button onClick={() => runNavOnce(() => {
                stopTimer(); stopPoll();
                if (stopTmPollRef.current) stopTmPollRef.current();
                // Clear ALL state before changing screen to prevent scorecard flash
                setGs(null); setRevealInfo(null); setMyPick(null); setBurst(null); setFlash(null); setStreak(0); setAnimating(false);
                setRoom1v1(null); setRoomCode(""); setMyRole("p1"); setP1name(""); setP2name(""); setJoinInput(""); setJoinErr("");
                setCh(CHALLENGES[0]);
                // Change screen LAST to prevent flash
                setTimeout(() => setScreen("select"), 0);
              })} style={solidBtn(CLR.gold)}>TRY ANOTHER MODE</button>

              <button onClick={() => {
                // PREMIUM 1v1 SCORECARD — cleaner, vertically stacked
                const inn1 = gs.inn1hist || [];
                const inn2 = gs.hist || [];
                const batFirst = gs.batFirst; // p1 batted first?
                const p1Name = myTeam === "p1" ? (myName.trim() || "Me") : oppDisplayName;
                const p2Name = myTeam === "p2" ? (myName.trim() || "Me") : oppDisplayName;

                const ballStr = (hist) => hist.map(e => e.isOut ? "W" : String(e.rawNum)).join(" ");
                const inn1Balls = ballStr(inn1);
                const inn2Balls = ballStr(inn2);
                const inn1Batter = batFirst ? p1Name : p2Name;
                const inn2Batter = batFirst ? p2Name : p1Name;
                const inn1Score = batFirst ? gs.p1score : gs.p2score;
                const inn2Score = batFirst ? gs.p2score : gs.p1score;
                const inn1Wkts = inn1.filter(e => e.isOut).length;
                const inn2Wkts = inn2.filter(e => e.isOut).length;
                

                // Height calculation:
                // Header(180) + Scores(140) + Stats(60) + Inn1(80) + Inn2(80) + Footer(40)
                const c = document.createElement("canvas");
                const lineH = 22;
                const getRows = (str) => Math.max(1, Math.ceil(ctx.measureText(str).width / 520));
                
                c.width = 600; c.height = 800; // placeholder for height calc
                const ctx = c.getContext("2d");
                ctx.font = "13px monospace"; // for measurement
                const h1r = getRows(inn1Balls);
                const h2r = getRows(inn2Balls);
                const H = 480 + (h1r + h2r) * lineH + 60;
                c.height = H;

                // 1. BG
                const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
                bgGrad.addColorStop(0, "#1a4a28"); bgGrad.addColorStop(0.4, "#0f2d1a"); bgGrad.addColorStop(1, "#0d2415");
                ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, 600, H);

                let y = 65;
                // 2. HEADER
                ctx.shadowBlur = 30; ctx.shadowColor = rc;
                ctx.font = "80px serif"; ctx.textAlign = "center";
                ctx.fillText(isDraw ? "🤝" : won ? "🏆" : "💀", 300, y);
                ctx.shadowBlur = 0; y += 70;

                ctx.font = "900 62px Arial"; ctx.fillStyle = rc;
                ctx.fillText(isDraw ? "TIE GAME!" : won ? "VICTORY!" : "DEFEATED", 300, y); y += 35;
                ctx.font = "700 13px Arial"; ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.letterSpacing = "4px";
                ctx.fillText(`${ch.name.toUpperCase()} · ${mode === "ai" ? "VS COMPUTER" : "1v1 ONLINE"}`, 300, y); ctx.letterSpacing = "0px"; y += 45;

                // 3. SCORE BOXES
                const drawScoreBox = (x, y, w, h, name, score, isWin) => {
                  ctx.strokeStyle = isWin ? "rgba(76,255,110,0.5)" : "rgba(255,255,255,0.2)"; ctx.lineWidth = 2;
                  ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.beginPath(); ctx.roundRect(x, y, w, h, 16); ctx.fill(); ctx.stroke();
                  ctx.fillStyle = isWin ? CLR.green : "rgba(255,255,255,0.5)"; ctx.font = "bold 11px Arial"; ctx.textAlign = "center";
                  ctx.fillText(name.toUpperCase(), x + w / 2, y + 22);
                  ctx.fillStyle = "#fff"; ctx.font = "900 52px Arial";
                  ctx.fillText(String(score), x + w / 2, y + 74);
                };
                drawScoreBox(30, y, 260, 110, (myName.trim() || "Me") + " (YOU)", myF, won && !isDraw);
                drawScoreBox(310, y, 260, 110, oppDisplayName, oppF, !won && !isDraw);
                y += 140;

                // 4. STATS BAR
                ctx.fillStyle = "rgba(245,197,24,0.08)"; ctx.strokeStyle = "rgba(245,197,24,0.3)"; ctx.lineWidth = 1.5;
                ctx.beginPath(); ctx.roundRect(30, y, 540, 42, 10); ctx.fill(); ctx.stroke();
                ctx.font = "700 12px Arial"; ctx.fillStyle = CLR.gold; ctx.textAlign = "center";
                ctx.fillText(`R/OVER: ${rr}   •   SIXES: ${sixes}   •   FOURS: ${fours}   •   BALLS: ${myBalls}`, 300, y + 26);
                y += 70;

                // 5. SCORECARD INNINGS
                ctx.font = "bold 12px Arial"; ctx.fillStyle = "rgba(255,255,255,0.3)"; ctx.textAlign = "left"; ctx.letterSpacing = "3px";
                ctx.fillText("📊 SCORECARD", 30, y); ctx.letterSpacing = "0px"; y += 22;

                const drawInning1v1 = (label, batter, score, wkts, ballsStr) => {
                  ctx.fillStyle = "rgba(255,255,255,0.05)"; ctx.beginPath(); ctx.roundRect(30, y, 540, 30, 6); ctx.fill();
                  ctx.font = "bold 13px Arial"; ctx.fillStyle = "#fff";
                  ctx.fillText(`INN ${label}  ${batter.toUpperCase()}: ${score}/${wkts}`, 42, y + 20);
                  y += 40;
                  ctx.font = "13px monospace"; ctx.fillStyle = "rgba(255,255,255,0.6)";
                  const words = ballsStr || "—";
                  let cur = "";
                  for (const ch2 of words) {
                    if (ctx.measureText(cur + ch2).width > 510) { ctx.fillText(cur, 40, y); y += lineH; cur = ch2; }
                    else cur += ch2;
                  }
                  if (cur) ctx.fillText(cur, 40, y);
                  y += lineH + 15;
                };

                drawInning1v1("1", inn1Batter, inn1Score, inn1Wkts, inn1Balls);
                drawInning1v1("2", inn2Batter, inn2Score, inn2Wkts, inn2Balls);

                // FOOTER
                ctx.fillStyle = "rgba(255,255,255,0.15)"; ctx.font = "11px Arial"; ctx.textAlign = "center";
                ctx.fillText("hand-cricket-striveon-games.vercel.app · Built by Striveon", 300, H - 25);

                c.toBlob(blob => {
                  shareScorecardBlob(blob, "hand-cricket-scorecard.png", "Hand Cricket Scorecard");
                }, "image/png");
              }} style={ghostBtn()}>📸 SCREENSHOT RESULT</button>
              <button onClick={() => { if (!menuLocked) runNavOnce(() => resetToMenu()); }} disabled={menuLocked} style={{ ...ghostBtn(), opacity: menuLocked ? 0.4 : 1, cursor: menuLocked ? "not-allowed" : "pointer" }}>{menuLocked ? "← MAIN MENU (wait…)" : "← MAIN MENU"}</button>
            </div>
            <Footer />
          </div>
        </div></>
    );
  }

  // ═══════════════════════════════════════════════════════
  // TEAM MATCH SCREENS
  // ═══════════════════════════════════════════════════════

  // ── TEAM SETUP ─────────────────────────────────────────
  if (screen === "teamSetup") return (
    <><style>{CSS}</style><CricketBg accent={CLR.orange} />
      <div style={wrap}>
        <div style={panel(430, CLR.orange)}>
          <button onClick={() => setScreen("menu")} style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, marginBottom: 14, fontFamily: "'Rajdhani',serif", minHeight: 36, touchAction: "manipulation" }}>← BACK</button>
          <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(22px,7vw,28px)", letterSpacing: 4, marginBottom: 4 }}>🏟️ TEAM MATCH</h2>
          <p style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", letterSpacing: 1, marginBottom: 20 }}>Everyone joins one room · Auto split into teams</p>

          {/* Overs counter */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 10, fontFamily: "'Bebas Neue',serif" }}>OVERS PER INNINGS</label>
            <div style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(0,0,0,0.25)", borderRadius: 14, border: `1.5px solid ${CLR.orange}40`, overflow: "hidden" }}>
              <button onClick={() => setTmOvers(v => Math.max(1, v - 1))}
                style={{ width: 56, minHeight: 54, background: "rgba(255,140,66,0.15)", border: "none", color: CLR.orange, fontSize: 26, cursor: "pointer", touchAction: "manipulation", fontWeight: 900, flexShrink: 0 }}>−</button>
              <div style={{ flex: 1, textAlign: "center", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(22px,7vw,28px)", color: CLR.orange, letterSpacing: 2 }}>{tmOvers} <span style={{ fontSize: "clamp(10px,3vw,12px)", color: CLR.textDim, letterSpacing: 2 }}>{tmOvers === 1 ? "OVER" : "OVERS"}</span></div>
              <button onClick={() => setTmOvers(v => Math.min(20, v + 1))}
                style={{ width: 56, minHeight: 54, background: "rgba(255,140,66,0.15)", border: "none", color: CLR.orange, fontSize: 26, cursor: "pointer", touchAction: "manipulation", fontWeight: 900, flexShrink: 0 }}>+</button>
            </div>
          </div>

          {/* Players counter */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 10, fontFamily: "'Bebas Neue',serif" }}>TOTAL PLAYERS</label>
            <div style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(0,0,0,0.25)", borderRadius: 14, border: `1.5px solid ${CLR.orange}40`, overflow: "hidden" }}>
              <button onClick={() => setTmPlayers(v => Math.max(2, v - 1))}
                style={{ width: 56, minHeight: 54, background: "rgba(255,140,66,0.15)", border: "none", color: CLR.orange, fontSize: 26, cursor: "pointer", touchAction: "manipulation", fontWeight: 900, flexShrink: 0 }}>−</button>
              <div style={{ flex: 1, textAlign: "center", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(22px,7vw,28px)", color: CLR.orange, letterSpacing: 2 }}>{tmPlayers} <span style={{ fontSize: "clamp(10px,3vw,12px)", color: CLR.textDim, letterSpacing: 2 }}>PLAYERS</span></div>
              <button onClick={() => setTmPlayers(v => Math.min(20, v + 1))}
                style={{ width: 56, minHeight: 54, background: "rgba(255,140,66,0.15)", border: "none", color: CLR.orange, fontSize: 26, cursor: "pointer", touchAction: "manipulation", fontWeight: 900, flexShrink: 0 }}>+</button>
            </div>
            <p style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", marginTop: 7 }}>
              Teams: <span style={{ color: CLR.orange, fontWeight: 700 }}>{Math.ceil(tmPlayers / 2)} vs {Math.floor(tmPlayers / 2)}</span>
              {tmPlayers % 2 !== 0 && <span style={{ color: CLR.textFaint }}> · smaller team bats twice</span>}
            </p>
          </div>

          {/* Host team picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 8, fontFamily: "'Bebas Neue',serif" }}>YOUR TEAM</label>
            <div style={{ display: "flex", gap: 8 }}>
              {Object.values(TM).map(t => (
                <button key={t.id} onClick={() => setTmMyTeam(t.id)}
                  style={{ flex: 1, minHeight: 52, borderRadius: 12, border: `2px solid ${tmMyTeam === t.id ? t.color : "rgba(255,255,255,0.15)"}`, background: tmMyTeam === t.id ? t.bg : "rgba(255,255,255,0.06)", color: tmMyTeam === t.id ? t.color : "rgba(255,255,255,0.5)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(15px,4.5vw,18px)", letterSpacing: 2, cursor: "pointer", touchAction: "manipulation", transition: "all 0.15s" }}>
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
          </div>

          {!nameConfirmed ? (
            renderNameSection(CLR.orange)
          ) : (
            <>
              <button onClick={createTeamRoom} style={{ ...solidBtn(CLR.orange), marginBottom: 16 }}>🏠 CREATE ROOM</button>

              <div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", paddingTop: 16 }}>
                <label style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, display: "block", marginBottom: 7, fontFamily: "'Bebas Neue',serif" }}>JOIN EXISTING ROOM</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={tmJoinInput} onChange={e => setTmJoinInput(e.target.value.toUpperCase().slice(0, 4))} placeholder="ABCD" maxLength={4}
                    style={{ ...inp, flex: 1, fontSize: "clamp(20px,6vw,26px)", textAlign: "center", letterSpacing: "clamp(4px,2vw,8px)", padding: "10px 8px" }} />
                  <button onClick={joinTeamRoom} style={{ ...solidBtn(CLR.orange, false), minWidth: 64 }}>JOIN</button>
                </div>
                {tmJoinErr && <p style={{ color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", marginTop: 7 }}>{tmJoinErr}</p>}
              </div>

              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 12 }}>
                <span style={{ color: CLR.textFaint, fontSize: 10, letterSpacing: 1, fontFamily: "'Bebas Neue',serif" }}>PLAYING AS: <span style={{ color: CLR.textDim }}>{myName}</span></span>
                <span style={{ color: "rgba(255,255,255,0.1)", fontSize: 10 }}>·</span>
                <button onClick={() => setNameConfirmed(false)} style={{ background: "none", border: "none", color: CLR.textFaint, cursor: "pointer", fontSize: 10, letterSpacing: 1, fontFamily: "'Bebas Neue',serif", textDecoration: "underline", padding: 0 }}>✎ EDIT</button>
              </div>
            </>
          )}
          <Footer />
        </div>
      </div></>
  );

  // ── TEAM LOBBY ─────────────────────────────────────────
  if (screen === "teamLobby" && tmRoom) {
    const allPlayers = Object.entries(tmRoom.players || {});
    // Only show ACTIVE players everywhere — inactive slots are invisible
    const players = allPlayers.filter(([, p]) => p.active !== false);
    const isHost = tmRoom.hostPid === MY_PID;
    const expected = tmRoom.totalPlayers || 4;
    const joined = players.length;
    const redPlayers = players.filter(([, p]) => p.team === "red");
    const bluePlayers = players.filter(([, p]) => p.team === "blue");
    const myCurrentTeam = tmRoom.players?.[MY_PID]?.team || tmMyTeam;
    // Ready: enough active players AND both teams have at least 1
    const ready = joined >= expected && redPlayers.length > 0 && bluePlayers.length > 0;

    // Can only switch if the other team has >0 active players after switch
    const canSwitchTo = (targetTeam) => {
      if (targetTeam === myCurrentTeam) return false;
      const myCurrentCount = myCurrentTeam === "red" ? redPlayers.length : bluePlayers.length;
      return myCurrentCount > 1;
    };

    return (
      <><style>{CSS}</style><CricketBg accent={CLR.orange} />
        <div style={scrollWrap}>
          <div style={panel(430, CLR.orange)}>
            {/* Back button */}
            <button onClick={() => {
              // Cancel the onDisconnect handler first — we're about to do an explicit
              // active:false write ourselves, so we don't want a double-write later.
              cancelOnDisconnect();
              // Fire-and-forget: write active:false without awaiting so the UI
              // transitions INSTANTLY. Firebase still gets the update in the background.
              const code = tmCodeRef.current;
              if (code) {
                loadTeam(code).then(r => {
                  if (r?.players?.[MY_PID]) {
                    saveTeam(code, { ...r, players: { ...r.players, [MY_PID]: { ...r.players[MY_PID], active: false, leftAt: Date.now() } } });
                  }
                });
              }
              stopTmPoll(); setTmRoom(null); setTmCode(""); setScreen("teamSetup");
            }}
              style={{ background: "none", border: "none", color: CLR.textDim, cursor: "pointer", fontSize: "clamp(10px,2.8vw,12px)", letterSpacing: 2, padding: 0, marginBottom: 14, fontFamily: "'Rajdhani',serif", minHeight: 36, touchAction: "manipulation" }}>← BACK</button>

            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(20px,6vw,26px)", letterSpacing: 4, marginBottom: 3, textAlign: "center" }}>🏟️ TEAM LOBBY</h2>
            <p style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)", marginBottom: 12, textAlign: "center" }}>
              {tmRoom.overs} overs · waiting {joined}/{expected}
            </p>

            {/* Lobby toast — shows join/leave/host-change messages briefly */}
            {lobbyToast && (
              <div style={{ background: `${lobbyToast.color}18`, border: `1.5px solid ${lobbyToast.color}50`, borderRadius: 10, padding: "8px 12px", marginBottom: 10, textAlign: "center", animation: "popIn 0.3s ease" }}>
                <span style={{ color: lobbyToast.color, fontSize: "clamp(10px,2.8vw,12px)", fontWeight: 700 }}>{lobbyToast.msg}</span>
              </div>
            )}

            {/* Room code */}
            <div style={{ background: `${CLR.orange}15`, border: `2px solid ${CLR.orange}45`, borderRadius: 12, padding: "10px", marginBottom: 12, textAlign: "center" }}>
              <div style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 4, marginBottom: 3, fontFamily: "'Bebas Neue',serif" }}>ROOM CODE</div>
              <div style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(28px,9vw,42px)", letterSpacing: "clamp(5px,2.5vw,10px)" }}>{tmCode}</div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <button onClick={() => shareJoinLink("team", tmCode, `🏏 Team Cricket — Join My Room!\n${tmRoom.overs} overs · ${expected} players`)} style={{ ...solidBtn(CLR.orange), flex: 1, minWidth: 140 }}>🔗 SHARE LINK</button>
              <button onClick={() => copyText(tmCode, `Room code ${tmCode} copied!`)} style={{ ...ghostBtn(), flex: 1, minWidth: 100 }}>📋 COPY CODE</button>
            </div>

            {/* MY TEAM badge */}
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <span style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>YOU ARE ON </span>
              <span style={{ color: TM[myCurrentTeam]?.color || CLR.orange, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(14px,4vw,16px)", letterSpacing: 2 }}>{TM[myCurrentTeam]?.emoji} {TM[myCurrentTeam]?.label} TEAM</span>
            </div>

            {/* Switch team buttons — disabled if switch would leave team empty */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {Object.values(TM).map(t => {
                const isActive = myCurrentTeam === t.id;
                const canSwitch = canSwitchTo(t.id);
                const disabled = isActive || !canSwitch;
                return (
                  <button key={t.id} onClick={() => !disabled && switchTeam(t.id)}
                    disabled={disabled}
                    style={{
                      flex: 1, minHeight: 52, borderRadius: 11,
                      border: `2.5px solid ${isActive ? t.color : canSwitch ? t.color + "99" : "rgba(255,255,255,0.10)"}`,
                      background: isActive ? t.bg : canSwitch ? t.bg + "99" : "rgba(255,255,255,0.03)",
                      color: isActive ? t.color : canSwitch ? t.color : "rgba(255,255,255,0.18)",
                      fontFamily: "'Bebas Neue',serif", fontSize: "clamp(13px,4vw,15px)", letterSpacing: 2,
                      cursor: disabled ? "default" : "pointer", touchAction: "manipulation", transition: "all 0.15s",
                      boxShadow: isActive ? `0 0 14px ${t.color}40` : "none"
                    }}>
                    {t.emoji} {isActive ? "✓ ON " + t.label.toUpperCase() : canSwitch ? "JOIN " + t.label.toUpperCase() : "LOCKED"}
                  </button>
                );
              })}
            </div>

            {/* Two-column team view */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {Object.values(TM).map(t => {
                const teamPlayers = t.id === "red" ? redPlayers : bluePlayers;
                return (
                  <div key={t.id} style={{ flex: 1, borderRadius: 12, border: `1.5px solid ${t.border}`, background: t.bg, padding: "10px 8px" }}>
                    <div style={{ color: t.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(11px,3.2vw,13px)", letterSpacing: 3, marginBottom: 8, textAlign: "center" }}>{t.emoji} {t.label}</div>
                    {teamPlayers.length === 0 && (
                      <div style={{ color: "rgba(255,255,255,0.18)", fontSize: "clamp(9px,2.5vw,10px)", textAlign: "center", padding: "6px 0" }}>Empty</div>
                    )}
                    {teamPlayers.map(([pid, p]) => (
                      <div key={pid} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 6px", borderRadius: 7, background: p.active === false ? "rgba(255,95,87,0.10)" : "rgba(0,0,0,0.18)", marginBottom: 4, opacity: p.active === false ? 0.7 : 1 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: t.bg, border: `1.5px solid ${p.active === false ? CLR.coral : t.color}`, display: "flex", alignItems: "center", justifyContent: "center", color: p.active === false ? CLR.coral : t.color, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                          {p.active === false ? "✕" : p.name[0]?.toUpperCase()}
                        </div>
                        <span style={{ color: p.active === false ? CLR.coral : "#fff", fontSize: "clamp(10px,2.8vw,12px)", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.name}{pid === MY_PID ? " ✓" : ""}
                        </span>
                        {pid === tmRoom.hostPid && p.active !== false && <span style={{ color: CLR.gold, fontSize: 9, fontFamily: "'Bebas Neue',serif" }}>HOST</span>}
                        {isHost && pid !== MY_PID && pid !== tmRoom.hostPid && p.active !== false && (
                          <button onClick={async () => {
                            const fresh = await loadTeam(tmCodeRef.current);
                            if (!fresh) return;
                            await saveTeam(tmCodeRef.current, { ...fresh, hostPid: pid });
                            setLobbyToast({ msg: `${p.name} is now the host`, color: CLR.gold });
                            setTimeout(() => setLobbyToast(null), 2500);
                          }} style={{ background: `${CLR.gold}20`, border: `1px solid ${CLR.gold}50`, borderRadius: 6, color: CLR.gold, fontSize: 9, fontFamily: "'Bebas Neue',serif", cursor: "pointer", padding: "2px 5px", touchAction: "manipulation", letterSpacing: 1 }}>
                            MAKE HOST
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Start button */}
            {!ready && redPlayers.length > 0 && bluePlayers.length === 0 && (
              <p style={{ color: CLR.coral, fontSize: "clamp(9px,2.5vw,10px)", textAlign: "center", marginBottom: 8, letterSpacing: 1 }}>⚠️ BLUE team is empty — someone must join BLUE</p>
            )}
            {!ready && bluePlayers.length > 0 && redPlayers.length === 0 && (
              <p style={{ color: CLR.coral, fontSize: "clamp(9px,2.5vw,10px)", textAlign: "center", marginBottom: 8, letterSpacing: 1 }}>⚠️ RED team is empty — someone must join RED</p>
            )}

            {isHost ? (
              <button onClick={startTeamMatch} disabled={!ready}
                style={{ ...solidBtn(ready ? CLR.orange : "rgba(255,255,255,0.2)"), opacity: ready ? 1 : 0.5, marginBottom: 10 }}>
                {ready ? "🏏 START MATCH" : `Waiting (${joined}/${expected})…`}
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 10 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: CLR.orange, animation: "blink 1s infinite" }} />
                <span style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)" }}>Waiting for host to start…</span>
              </div>
            )}
            <Footer />
          </div>
        </div></>
    );
  }

  // ── TEAM TOSS ──────────────────────────────────────────
  // ── TOSS REVEAL OVERLAY ────────────────────────────────
  if (tossReveal) {
    const myActualTeamId = tmRoom?.players?.[MY_PID]?.team || tmMyTeam;
    const amIBatting = tossReveal.battingTm.id === myActualTeamId;
    const myTm = amIBatting ? tossReveal.battingTm : tossReveal.bowlingTm;

    return (
      <><style>{CSS}</style><CricketBg accent={myTm.color} />
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={{ ...panel(430, myTm.color), animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: "clamp(48px,14vw,64px)", marginBottom: 10 }}>{amIBatting ? "🏏" : "⚾"}</div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(24px,7vw,30px)", letterSpacing: 4, marginBottom: 20 }}>MATCH STARTS!</h2>

            <div style={{
              padding: "24px 16px", borderRadius: 16, background: myTm.bg, border: `2px solid ${myTm.border}`,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 26,
              boxShadow: `0 8px 32px ${myTm.color}25`
            }}>
              <div style={{ color: CLR.textDim, fontSize: "clamp(10px,3vw,12px)", letterSpacing: 3, fontFamily: "'Bebas Neue',serif", display: "flex", alignItems: "center", gap: 6 }}>
                {myTm.emoji} {myTm.label} TEAM
              </div>
              <div style={{ color: myTm.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(32px,9vw,40px)", letterSpacing: 2, lineHeight: 1, textShadow: `0 0 20px ${myTm.color}60` }}>
                YOU ARE {amIBatting ? "BATTING" : "BOWLING"}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: myTm.color, animation: "blink 0.8s infinite" }} />
              <span style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)" }}>Starting match in 3 seconds…</span>
            </div>
          </div>
        </div></>
    );
  }

  if (screen === "teamToss" && tmRoom?.toss) {
    const toss = tmRoom.toss;
    const winnerTeam = toss.winner === "A" ? "red" : "blue";
    const winnerTm = winnerTeam === "red" ? TM.red : TM.blue;
    const loserTm = winnerTeam === "red" ? TM.blue : TM.red;
    const tossByName = tmRoom.players?.[toss.tossBy]?.name || "Captain";
    const amChooser = toss.tossBy === MY_PID;

    return (
      <><style>{CSS}</style><CricketBg accent={winnerTm.color} />
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={panel(430, winnerTm.color)}>
            <div style={{ fontSize: "clamp(46px,15vw,62px)", marginBottom: 8 }}>🪙</div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(24px,7.5vw,32px)", letterSpacing: 5, marginBottom: 10 }}>TOSS RESULT</h2>
            <div style={{ background: winnerTm.bg, border: `2px solid ${winnerTm.border}`, borderRadius: 14, padding: "14px 12px", marginBottom: 14 }}>
              <div style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 3, marginBottom: 6, fontFamily: "'Bebas Neue',serif" }}>TOSS WINNER</div>
              <div style={{ color: winnerTm.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(26px,8vw,34px)", letterSpacing: 2 }}>
                {winnerTm.emoji} {winnerTm.label} TEAM
              </div>
              <div style={{ color: "#fff", fontSize: "clamp(10px,2.8vw,12px)", marginTop: 4 }}>{tossByName} chooses bat or bowl</div>
            </div>

            {amChooser ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={() => submitTossChoice("bat")} style={{ ...solidBtn(CLR.green, false), flex: 1, minHeight: 56 }}>🏏 BAT FIRST</button>
                <button onClick={() => submitTossChoice("bowl")} style={{ ...solidBtn(CLR.coral, false), flex: 1, minHeight: 56 }}>⚾ BOWL FIRST</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: winnerTm.color, animation: "blink 1s infinite" }} />
                <span style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)" }}>
                  Waiting for {tossByName} ({winnerTm.label}) to choose...
                </span>
              </div>
            )}
            <div style={{ color: CLR.textFaint, fontSize: "clamp(9px,2.5vw,10px)" }}>
              {winnerTm.emoji} {winnerTm.label} won toss • {loserTm.emoji} {loserTm.label} waiting
            </div>
            <Footer />
          </div>
        </div></>
    );
  }

  // ── TEAM GAME ──────────────────────────────────────────
  if (screen === "teamGame" && tmRoom?.gs) {
    const tgs = tmRoom.gs;
    const teamA = tmRoom.teamA || [];
    const teamB = tmRoom.teamB || [];
    // teamA = RED, teamB = BLUE always
    const myTmTeam = teamA.includes(MY_PID) ? "A" : "B";
    const myTmColor = myTmTeam === "A" ? TM.red : TM.blue;
    const battArr = tgs.battingTeam === "A" ? teamA : teamB;
    const bowlArr = tgs.battingTeam === "A" ? teamB : teamA;
    const battingTm = tgs.battingTeam === "A" ? TM.red : TM.blue;
    const fieldingTm = tgs.battingTeam === "A" ? TM.blue : TM.red;
    const currentBatterPid = battArr[tgs.batterIdx % battArr.length];
    const currentBowlerPid = bowlArr[tgs.bowlerIdx % bowlArr.length];
    const iAmBatter = MY_PID === currentBatterPid;
    const iAmBowler = MY_PID === currentBowlerPid;
    const iAmActive = iAmBatter || iAmBowler;
    const batterName = tmRoom.players[currentBatterPid]?.name || "Batter";
    const bowlerName = tmRoom.players[currentBowlerPid]?.name || "Bowler";
    const MAX_BALLS = tgs.overs * 6;
    const iHavePicked = (iAmBatter && tgs.batterPick !== null) || (iAmBowler && tgs.bowlerPick !== null) || myPick !== null;
    const padDisabled = !iAmActive || animating || !!revealInfo || iHavePicked;
    const urgent = timerOn && timeLeft <= 10;

    return (
      <><style>{CSS}</style><CricketBg accent={CLR.orange} />
        {burst && <Burst type={burst} />}
        {flash && <div style={{ position: "fixed", top: "16%", left: 0, right: 0, textAlign: "center", zIndex: 800, pointerEvents: "none", color: flash.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(28px,9vw,40px)", letterSpacing: 4, animation: "flashA 1.3s ease forwards", padding: "0 8px" }}>{flash.msg}</div>}

        {/* ── PLAYER LEFT MODAL — shown to remaining teammates ── */}
        {playerLeftModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}>
            <div style={{ width: "100%", maxWidth: 370, background: "linear-gradient(160deg,#1a2e1f,#0f2010)", border: "1.5px solid rgba(255,140,66,0.40)", borderRadius: 18, padding: "clamp(18px,5vw,28px)", boxShadow: "0 24px 64px rgba(0,0,0,0.60)", animation: "popIn 0.35s ease" }}>
              <div style={{ fontSize: "clamp(36px,11vw,48px)", textAlign: "center", marginBottom: 10 }}>⚠️</div>
              <h3 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.orange, fontSize: "clamp(18px,5.5vw,24px)", letterSpacing: 4, textAlign: "center", marginBottom: 6 }}>PLAYER LEFT</h3>
              <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(11px,3.2vw,13px)", textAlign: "center", marginBottom: 18, lineHeight: 1.5 }}>
                <span style={{ color: "#fff", fontWeight: 700 }}>{playerLeftModal.name}</span> has left the match.<br />
                Their turns will rotate to remaining teammates.
              </p>
              <div style={{ background: "rgba(255,140,66,0.08)", border: "1px solid rgba(255,140,66,0.22)", borderRadius: 10, padding: "10px 14px", marginBottom: 18 }}>
                <div style={{ color: CLR.textFaint, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,9px)", letterSpacing: 3, marginBottom: 6 }}>YOUR TEAM NOW</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {((playerLeftModal.team === "A" ? (playerLeftModal.room?.teamA || []) : (playerLeftModal.room?.teamB || []))
                    .filter(pid => pid !== playerLeftModal.pid)
                    .map(pid => (
                      <span key={pid} style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 20, padding: "3px 10px", color: "#fff", fontSize: "clamp(9px,2.5vw,11px)", fontWeight: 700 }}>
                        {playerLeftModal.room?.players?.[pid]?.name || "?"}
                        {pid === MY_PID ? " (you)" : ""}
                      </span>
                    )))}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={async () => {
                  // CONTINUE — remove the quitter from their team array, clear playerLeft, resume
                  const code = tmCodeRef.current;
                  const room = await loadTeam(code);
                  if (!room) return;
                  const leaverPid = playerLeftModal.pid;
                  const leaverTeam = playerLeftModal.team;
                  const newTeamA = leaverTeam === "A" ? (room.teamA || []).filter(p => p !== leaverPid) : room.teamA;
                  const newTeamB = leaverTeam === "B" ? (room.teamB || []).filter(p => p !== leaverPid) : room.teamB;
                  // Recalculate maxWkts based on new team sizes
                  const newMaxWkts = Math.max(newTeamA.length, newTeamB.length);
                  const updatedGs = { ...room.gs, maxWkts: newMaxWkts };
                  await saveTeam(code, { ...room, teamA: newTeamA, teamB: newTeamB, gs: updatedGs, playerLeft: null });
                  setTmRoom(r => r ? { ...r, teamA: newTeamA, teamB: newTeamB, gs: updatedGs, playerLeft: null } : r);
                  setPlayerLeftModal(null);
                  showFlash("✅ Match continues!", CLR.green, 2000);
                  // Restart timer if it's now our turn
                  const battArr2 = updatedGs.battingTeam === "A" ? newTeamA : newTeamB;
                  const bowlArr2 = updatedGs.battingTeam === "A" ? newTeamB : newTeamA;
                  const nb = battArr2[updatedGs.batterIdx % battArr2.length];
                  const nbl = bowlArr2[updatedGs.bowlerIdx % bowlArr2.length];
                  if (MY_PID === nb || MY_PID === nbl) startTimer();
                }} style={{ ...solidBtn(CLR.green), minHeight: 50 }}>
                  ✅ CONTINUE MATCH
                  <span style={{ fontSize: "clamp(8px,2.2vw,10px)", opacity: 0.8, fontWeight: 400, letterSpacing: 1 }}> — I'll cover their turns</span>
                </button>
                <button onClick={async () => {
                  // FORFEIT — write teamForfeit so opponents win
                  const code = tmCodeRef.current;
                  const room = await loadTeam(code);
                  if (room) await saveTeam(code, { ...room, playerLeft: null, teamForfeit: playerLeftModal.pid });
                  stopTmPoll(); stopTimer();
                  setPlayerLeftModal(null);
                  setScreen("menu"); setTmRoom(null); setTmCode("");
                  setMyPick(null); setRevealInfo(null); setAnimating(false);
                  setBurst(null); setFlash(null); setStreak(0);
                }} style={{ ...solidBtn(CLR.coral), minHeight: 44 }}>
                  ❌ END MATCH
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── OPPONENT PLAYER LEFT BANNER — persistent info for the other team ── */}
        {opponentLeftBanner && !playerLeftModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 1050,
            background: "linear-gradient(90deg, rgba(15,25,15,0.97), rgba(20,35,20,0.97))",
            borderBottom: "2px solid rgba(255,140,66,0.50)",
            padding: "10px 16px",
            display: "flex", alignItems: "center", gap: 10,
            backdropFilter: "blur(8px)",
            animation: "slideDown 0.35s ease"
          }}>
            {/* Icon + text */}
            <div style={{ fontSize: 20, flexShrink: 0 }}>⚠️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: CLR.orange, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,13px)", letterSpacing: 3, lineHeight: 1 }}>
                PLAYER LEFT
              </div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(9px,2.5vw,11px)", marginTop: 2 }}>
                <span style={{ color: "#fff", fontWeight: 700 }}>{opponentLeftBanner.name}</span>
                {" "}left · {opponentLeftBanner.team === "A" ? TM.red.emoji + " RED" : TM.blue.emoji + " BLUE"} team is deciding whether to continue…
              </div>
            </div>
            {/* Animated waiting dots */}
            <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: CLR.orange, animation: `thinkDot 1.2s ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}

        {/* Incoming emoji reactions from opponent team — float up dramatically */}
        {tmReactions.map(r => {
          const left = 15 + (parseInt(r.id, 36) % 70);
          return (
            <div key={r.id} style={{
              position: "fixed", bottom: "22%", left: `${left}%`, zIndex: 950, pointerEvents: "none",
              animation: "floatUp 2.4s ease forwards", textAlign: "center"
            }}>
              <div style={{ fontSize: "clamp(28px,9vw,36px)" }}>{r.emoji}</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(8px,2.2vw,10px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1, marginTop: 2 }}>{r.name}</div>
            </div>
          );
        })}
        {renderQuitOverlay()}
        <div style={wrap}>
          <div style={{ ...panel(430, urgent ? CLR.coral : CLR.orange), width: "100%" }}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button onClick={() => setQuitConfirm(true)} style={{ background: "rgba(255,95,87,0.12)", border: "1.5px solid rgba(255,95,87,0.28)", borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", fontWeight: 700, fontFamily: "'Bebas Neue',serif", letterSpacing: 1, minHeight: 30, touchAction: "manipulation" }}>✕ QUIT</button>
                <span style={{ color: CLR.orange, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(11px,3vw,13px)", letterSpacing: 2 }}>INN {tgs.inn}</span>
                <span style={{ color: CLR.textFaint, fontSize: "clamp(9px,2.5vw,10px)" }}>·</span>
                <span style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,10px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1 }}>{tgs.overs} OV</span>
              </div>
              {/* MY TEAM badge */}
              <div style={{ padding: "3px 10px", borderRadius: 20, background: myTmColor.bg, border: `1.5px solid ${myTmColor.border}`, color: myTmColor.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(9px,2.5vw,11px)", letterSpacing: 2 }}>
                {myTmColor.emoji} {myTmColor.label} TEAM
              </div>
            </div>

            {/* Scoreboard — RED vs BLUE */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {[TM.red, TM.blue].map(t => {
                const isTA = t.id === "red"; // red=teamA, blue=teamB
                const score = isTA ? tgs.teamAScore : tgs.teamBScore;
                const isbatting = tgs.battingTeam === (isTA ? "A" : "B");
                const isMyTeam = myTmTeam === t.id || myTmColor.id === t.id;
                return (
                  <div key={t.id} style={{
                    flex: 1, padding: "clamp(8px,2.5vw,12px)", borderRadius: 12,
                    background: isbatting ? t.bg : "rgba(255,255,255,0.06)",
                    border: `2px solid ${isbatting ? t.border : "rgba(255,255,255,0.10)"}`
                  }}>
                    <div style={{ color: t.color, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3, marginBottom: 3, fontFamily: "'Bebas Neue',serif", display: "flex", alignItems: "center", gap: 4 }}>
                      {t.emoji} {t.label}{isMyTeam ? " (YOU)" : ""}  {isbatting ? "🏏" : "⚾"}
                    </div>
                    <div style={{ color: isbatting ? t.color : "rgba(255,255,255,0.5)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(28px,8vw,36px)", lineHeight: 1 }}>{score}</div>
                    {isbatting && <div style={{ color: CLR.coral, fontSize: "clamp(10px,2.8vw,11px)", marginTop: 1 }}>/{tgs.wkts}W</div>}
                    {tgs.inn === 2 && tgs.target && isbatting && (
                      <div style={{ marginTop: 4, color: tgs.target - score <= 6 ? CLR.green : CLR.coral, fontSize: "clamp(8px,2.2vw,9px)", fontWeight: 700, fontFamily: "'Bebas Neue',serif" }}>
                        NEED {Math.max(0, tgs.target - score)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current matchup */}
            <div style={{ background: "rgba(0,0,0,0.20)", borderRadius: 11, padding: "8px 12px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: "clamp(16px,5vw,20px)" }}>🏏</span>
                <div>
                  <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>BATTING</div>
                  <div style={{ color: iAmBatter ? CLR.green : "#fff", fontWeight: 700, fontSize: "clamp(11px,3.2vw,13px)" }}>{iAmBatter ? "YOU" : batterName}</div>
                </div>
              </div>
              <span style={{ color: "rgba(255,255,255,0.25)", fontWeight: 700, fontSize: "clamp(9px,2.5vw,11px)" }}>VS</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexDirection: "row-reverse" }}>
                <span style={{ fontSize: "clamp(16px,5vw,20px)" }}>⚾</span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>BOWLING</div>
                  <div style={{ color: iAmBowler ? CLR.coral : "#fff", fontWeight: 700, fontSize: "clamp(11px,3.2vw,13px)" }}>{iAmBowler ? "YOU" : bowlerName}</div>
                </div>
              </div>
            </div>

            {/* Over progress */}
            <div style={{ display: "flex", gap: 2, marginBottom: 10 }}>
              {Array.from({ length: MAX_BALLS }, (_, i) => (
                <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < tgs.balls ? CLR.orange : "rgba(255,255,255,0.10)" }} />
              ))}
            </div>

            {/* Timer + reveal / status */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9, minHeight: 68 }}>
              {timerOn && iAmActive && !revealInfo && <TimerRing time={timeLeft} total={TURN_SECS} color={CLR.orange} size={52} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                {revealInfo && revealInfo.tmMode ? (
                  <div style={{
                    borderRadius: 12, padding: "10px 12px",
                    background: revealInfo.isOut ? "rgba(255,95,87,0.11)" : "rgba(255,140,66,0.08)",
                    border: `1.5px solid ${revealInfo.isOut ? "rgba(255,95,87,0.32)" : "rgba(255,140,66,0.28)"}`,
                    animation: "popIn 0.35s ease"
                  }}>
                    <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 6 }}>
                      {[{ l: revealInfo.batterName.slice(0, 6).toUpperCase(), n: revealInfo.batterNum, role: "🏏" },
                      { l: revealInfo.bowlerName.slice(0, 6).toUpperCase(), n: revealInfo.bowlerNum, role: "⚾" }].map(({ l, n, role }, idx) => (
                        <div key={idx} style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "clamp(6px,1.8vw,8px)", color: CLR.textFaint, letterSpacing: 2, marginBottom: 2 }}>{role} {l}</div>
                          <div style={{ fontSize: "clamp(26px,8vw,32px)", animation: `bnc 0.35s ${idx * 0.08}s ease both` }}>{EMOJI[n]}</div>
                          <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(15px,4.5vw,18px)" }}>{n}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ textAlign: "center", color: revealInfo.isOut ? CLR.coral : CLR.green, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(15px,4.5vw,18px)", letterSpacing: 2 }}>
                      {revealInfo.isOut ? "💀 OUT!" : revealInfo.rawNum === 6 ? "💥 SIX!" : revealInfo.rawNum === 4 ? "🔥 FOUR!" : revealInfo.iAmBatting ? `+${revealInfo.runs} RUNS` : `${revealInfo.runs} SCORED`}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: CLR.textDim, fontSize: "clamp(10px,2.8vw,11px)" }}>
                    {iAmActive && !iHavePicked ? (
                      <span style={{ color: iAmBatter ? CLR.green : CLR.coral }}>{iAmBatter ? "🏏 PICK TO SCORE" : "⚾ PICK TO BOWL"}</span>
                    ) : iHavePicked && !revealInfo ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: CLR.orange, animation: "blink 0.85s infinite", flexShrink: 0 }} />
                        <span>Waiting for {iAmBatter ? bowlerName : batterName}…</span>
                      </div>
                    ) : (
                      <span>👀 {batterName} <span style={{ color: CLR.textFaint }}>vs</span> {bowlerName}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* NumPad — only shown to batter and bowler */}
            {iAmActive && <NumPad onPick={submitTeamPick} disabled={padDisabled} myPick={myPick} acc={CLR.orange} urgent={urgent} />}

            {/* ── SPECTATOR SPLIT-SCREEN LIVE FEED ── */}
            {!iAmActive && (() => {
              const lastBall = Array.isArray(tgs.hist) && tgs.hist.length > 0 ? tgs.hist[tgs.hist.length - 1] : null;
              const batterHasPicked = tgs.batterPick !== null;
              const bowlerHasPicked = tgs.bowlerPick !== null;
              const isRevealing = !!(revealInfo && revealInfo.tmMode);

              // Per-batter stats from current innings
              const batterRunsThisInn = (Array.isArray(tgs.hist) ? tgs.hist : [])
                .filter(b => b.batterPid === currentBatterPid && !b.isOut)
                .reduce((s, b) => s + (b.runs || 0), 0);
              const batterBallsThisInn = (Array.isArray(tgs.hist) ? tgs.hist : [])
                .filter(b => b.batterPid === currentBatterPid).length;
              const bowlerWktsThisInn = (Array.isArray(tgs.hist) ? tgs.hist : [])
                .filter(b => b.bowlerPid === currentBowlerPid && b.isOut).length;
              const bowlerRunsThisInn = (Array.isArray(tgs.hist) ? tgs.hist : [])
                .filter(b => b.bowlerPid === currentBowlerPid && !b.isOut)
                .reduce((s, b) => s + (b.runs || 0), 0);

              const ThinkingDots = () => (
                <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.6)", animation: `thinkDot 1.2s ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              );

              return (
                <div style={{ marginTop: 8, borderRadius: 14, overflow: "hidden", border: "1.5px solid rgba(255,255,255,0.10)" }}>

                  {/* ── TOP HALF: BATTER ── */}
                  <div style={{
                    background: `linear-gradient(135deg, ${battingTm.bg}, rgba(0,0,0,0.30))`,
                    borderBottom: `2px solid ${battingTm.color}40`,
                    padding: "clamp(10px,3vw,16px) clamp(10px,3vw,14px)",
                    animation: "slideDown 0.4s ease",
                    minHeight: 90,
                    display: "flex", flexDirection: "column", gap: 8
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: "clamp(16px,5vw,22px)" }}>🏏</span>
                        <div>
                          <div style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3 }}>BATTING</div>
                          <div style={{ color: battingTm.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(13px,4vw,17px)", letterSpacing: 1, lineHeight: 1 }}>{batterName}</div>
                        </div>
                      </div>
                      {/* Live stats */}
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(18px,6vw,26px)", lineHeight: 1, fontWeight: 900 }}>{batterRunsThisInn}</div>
                          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "clamp(6px,1.8vw,8px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1 }}>{batterBallsThisInn}B</div>
                        </div>
                      </div>
                    </div>

                    {/* Pick status */}
                    {isRevealing ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, animation: "revealFlip 0.4s ease" }}>
                        <div style={{
                          flex: 1, borderRadius: 10, padding: "8px 12px",
                          background: revealInfo.isOut ? "rgba(255,95,87,0.18)" : "rgba(76,255,110,0.12)",
                          border: `1.5px solid ${revealInfo.isOut ? "rgba(255,95,87,0.4)" : "rgba(76,255,110,0.35)"}`,
                          display: "flex", alignItems: "center", justifyContent: "space-between"
                        }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "clamp(22px,7vw,30px)" }}>{EMOJI[revealInfo.batterNum]}</div>
                            <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(13px,4vw,16px)" }}>{revealInfo.batterNum}</div>
                          </div>
                          <div style={{ color: revealInfo.isOut ? CLR.coral : CLR.green, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(14px,4.5vw,18px)", letterSpacing: 2, fontWeight: 900 }}>
                            {revealInfo.isOut ? "💀 OUT!" : revealInfo.rawNum === 6 ? "💥 SIX!" : revealInfo.rawNum === 4 ? "🔥 FOUR!" : `+${revealInfo.runs}`}
                          </div>
                        </div>
                      </div>
                    ) : batterHasPicked ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 10, background: `${battingTm.color}18`, border: `1.5px solid ${battingTm.color}35` }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: CLR.green, boxShadow: `0 0 6px ${CLR.green}`, animation: "pulseRing 1s infinite" }} />
                        <span style={{ color: CLR.green, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,13px)", letterSpacing: 2 }}>LOCKED IN ✓</span>
                        <span style={{ color: "rgba(255,255,255,0.30)", fontSize: "clamp(9px,2.5vw,11px)", marginLeft: "auto" }}>waiting for bowler…</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(255,255,255,0.10)" }}>
                        <ThinkingDots />
                        <span style={{ color: "rgba(255,255,255,0.50)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,12px)", letterSpacing: 2 }}>CHOOSING…</span>
                      </div>
                    )}
                  </div>

                  {/* ── MIDDLE DIVIDER: LIVE STATUS ── */}
                  <div style={{
                    background: "rgba(0,0,0,0.50)",
                    padding: "6px 14px",
                    display: "flex", alignItems: "center", justifyContent: "space-between"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: CLR.orange, animation: "blink 1s infinite" }} />
                      <span style={{ color: CLR.orange, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3 }}>LIVE</span>
                    </div>
                    {/* Last ball chip */}
                    {lastBall && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 5,
                        padding: "2px 8px", borderRadius: 20,
                        background: lastBall.isOut ? "rgba(255,95,87,0.18)" : "rgba(76,255,110,0.12)",
                        border: `1px solid ${lastBall.isOut ? "rgba(255,95,87,0.35)" : "rgba(76,255,110,0.28)"}`,
                      }}>
                        <span style={{ color: "rgba(255,255,255,0.40)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,8px)", letterSpacing: 1 }}>LAST</span>
                        <span style={{ color: lastBall.isOut ? CLR.coral : CLR.green, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(9px,2.5vw,11px)", fontWeight: 900 }}>
                          {lastBall.isOut ? "W" : `+${lastBall.runs}`}
                        </span>
                      </div>
                    )}
                    <div style={{ color: "rgba(255,255,255,0.35)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 1 }}>
                      BALL {tgs.balls + 1} / {tgs.overs * 6}
                    </div>
                  </div>

                  {/* ── BOTTOM HALF: BOWLER ── */}
                  <div style={{
                    background: `linear-gradient(225deg, ${fieldingTm.bg}, rgba(0,0,0,0.30))`,
                    borderTop: `2px solid ${fieldingTm.color}40`,
                    padding: "clamp(10px,3vw,16px) clamp(10px,3vw,14px)",
                    animation: "slideUp 0.4s ease",
                    minHeight: 90,
                    display: "flex", flexDirection: "column", gap: 8
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: "clamp(16px,5vw,22px)" }}>⚾</span>
                        <div>
                          <div style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3 }}>BOWLING</div>
                          <div style={{ color: fieldingTm.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(13px,4vw,17px)", letterSpacing: 1, lineHeight: 1 }}>{bowlerName}</div>
                        </div>
                      </div>
                      {/* Live stats */}
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ color: bowlerWktsThisInn > 0 ? CLR.green : "rgba(255,255,255,0.55)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(18px,6vw,26px)", lineHeight: 1, fontWeight: 900 }}>{bowlerWktsThisInn}W</div>
                          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "clamp(6px,1.8vw,8px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1 }}>{bowlerRunsThisInn}R</div>
                        </div>
                      </div>
                    </div>

                    {/* Pick status */}
                    {isRevealing ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, animation: "revealFlip 0.4s ease" }}>
                        <div style={{
                          flex: 1, borderRadius: 10, padding: "8px 12px",
                          background: revealInfo.isOut ? "rgba(76,255,110,0.12)" : "rgba(255,95,87,0.10)",
                          border: `1.5px solid ${revealInfo.isOut ? "rgba(76,255,110,0.35)" : "rgba(255,95,87,0.25)"}`,
                          display: "flex", alignItems: "center", justifyContent: "space-between"
                        }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "clamp(22px,7vw,30px)" }}>{EMOJI[revealInfo.bowlerNum]}</div>
                            <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(13px,4vw,16px)" }}>{revealInfo.bowlerNum}</div>
                          </div>
                          <div style={{ color: revealInfo.isOut ? CLR.green : "rgba(255,255,255,0.50)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(14px,4.5vw,18px)", letterSpacing: 2 }}>
                            {revealInfo.isOut ? "🎯 GOT HIM!" : "MISSED"}
                          </div>
                        </div>
                      </div>
                    ) : bowlerHasPicked ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 10, background: `${fieldingTm.color}18`, border: `1.5px solid ${fieldingTm.color}35` }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: CLR.green, boxShadow: `0 0 6px ${CLR.green}`, animation: "pulseRing 1s infinite" }} />
                        <span style={{ color: CLR.green, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,13px)", letterSpacing: 2 }}>LOCKED IN ✓</span>
                        <span style={{ color: "rgba(255,255,255,0.30)", fontSize: "clamp(9px,2.5vw,11px)", marginLeft: "auto" }}>waiting for batter…</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(255,255,255,0.10)" }}>
                        <ThinkingDots />
                        <span style={{ color: "rgba(255,255,255,0.50)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,12px)", letterSpacing: 2 }}>CHOOSING…</span>
                      </div>
                    )}
                  </div>

                  {/* ── SPECTATOR LABEL ── */}
                  <div style={{ background: "rgba(0,0,0,0.35)", padding: "5px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <span style={{ fontSize: 10 }}>📺</span>
                    <span style={{ color: "rgba(255,255,255,0.28)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3 }}>
                      SPECTATING · {myTmColor.emoji} {myTmColor.label} · YOUR TURN COMES NEXT
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Ball history — when inn1 just ended (hist=[]) show inn1hist so last ball is visible */}
            <div style={{ marginTop: 10, minHeight: 26 }}>
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
                {(() => {
                  const displayHist = (Array.isArray(tgs.hist) && tgs.hist.length > 0)
                    ? tgs.hist
                    : (Array.isArray(tgs.inn1hist) && tgs.inn1hist.length > 0 ? tgs.inn1hist : []);
                  return displayHist.slice(-18).map((e, i) => <BallDot key={i} entry={e} />);
                })()}
              </div>
            </div>

            {/* Wicket pips */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5, marginTop: 8 }}>
              {Array.from({ length: tgs.maxWkts }, (_, i) => (
                <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < tgs.wkts ? CLR.coral : "rgba(255,255,255,0.15)", boxShadow: i < tgs.wkts ? `0 0 6px ${CLR.coral}` : "none" }} />
              ))}
              <span style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", marginLeft: 4, letterSpacing: 2, fontFamily: "'Bebas Neue',serif" }}>WICKETS</span>
            </div>

            {/* Emoji reactions — available to everyone including spectators */}
            <div style={{ marginTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
              <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 3, textAlign: "center", marginBottom: 6, fontFamily: "'Bebas Neue',serif" }}>REACT</div>
              <div style={{ display: "flex", justifyContent: "center", gap: "clamp(6px,2vw,10px)" }}>
                {["👏", "🔥", "💀", "😂", "😱", "🎯"].map(emoji => (
                  <button key={emoji} onClick={() => sendReaction(emoji)}
                    style={{
                      width: "clamp(38px,10vw,46px)", height: "clamp(38px,10vw,46px)", borderRadius: 10,
                      border: "1.5px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)",
                      fontSize: "clamp(18px,5.5vw,22px)", cursor: "pointer", touchAction: "manipulation",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "transform 0.1s", WebkitTapHighlightColor: "transparent"
                    }}
                    onTouchStart={e => e.currentTarget.style.transform = "scale(0.88)"}
                    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}>
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <Footer />
          </div>
        </div></>
    );
  }

  // ── TEAM BREAK ─────────────────────────────────────────
  if (screen === "teamBreak" && tmRoom?.gs) {
    const tgs = tmRoom.gs;
    return (
      <><style>{CSS}</style><CricketBg accent={CLR.orange} />
        {showPromo && <PromoPopup onClose={() => { setShowPromo(false); setAdDone(true); }} />}
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={panel(430, CLR.orange)}>
            <div style={{ fontSize: "clamp(44px,14vw,60px)", marginBottom: 10 }}>🔄</div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", color: CLR.cream, fontSize: "clamp(22px,7vw,28px)", letterSpacing: 5, marginBottom: 16 }}>INNINGS BREAK</h2>
            <div style={{ background: `${CLR.orange}15`, border: `2px solid ${CLR.orange}40`, borderRadius: 14, padding: "clamp(14px,4vw,22px)", marginBottom: 12 }}>
              <div style={{ color: CLR.textDim, fontSize: "clamp(8px,2.2vw,9px)", letterSpacing: 4, marginBottom: 6, fontFamily: "'Bebas Neue',serif" }}>TARGET TO WIN</div>
              <div style={{ fontFamily: "'Bebas Neue',serif", color: CLR.orange, fontSize: "clamp(52px,18vw,70px)", lineHeight: 1 }}>{tgs.target}</div>
              <div style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,11px)", marginTop: 6 }}>
                {tgs.battingTeam === "A" ? TM.red.emoji + " RED" : TM.blue.emoji + " BLUE"} TEAM BATS NEXT
              </div>
            </div>
            {/* Cricket tip */}
            {(() => {
              const tip = CRICKET_TIPS[tgs.ver % CRICKET_TIPS.length];
              return (
                <div style={{ textAlign: "left", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ color: CLR.orange, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, marginBottom: 5 }}>🏏 {tip.title.toUpperCase()}</div>
                  <p style={{ color: "rgba(255,255,255,0.70)", fontSize: "clamp(11px,3.2vw,13px)", lineHeight: 1.6, margin: 0 }}>{tip.body}</p>
                </div>
              );
            })()}
            {/* Promo Banner */}
            <PromoBanner />
            {adDone ? (
              <button onClick={async () => {
                const code = tmCodeRef.current;
                // Write my ready flag
                await updateTeam(code, { [`inn2Ready/${MY_PID}`]: true });
                setInn2ReadyCount(c => c + 1);
                // Check if ALL active players are ready
                const r2 = await loadTeam(code);
                if (!r2?.gs) return;
                if (r2.gs.phase !== "break") { setScreen("teamGame"); return; }
                const activePids = Object.keys(r2.players || {}).filter(pid => r2.players[pid].active !== false);
                const readyPids = Object.keys(r2.inn2Ready || {}).filter(pid => r2.inn2Ready[pid]);
                const allReady = activePids.every(pid => readyPids.includes(pid));
                if (allReady) {
                  setInn2Starting(true);
                  const newGs = { ...r2.gs, phase: "picking", ver: r2.gs.ver + 1 };
                  await saveTeam(code, { ...r2, gs: newGs, lastBall: null, inn2Ready: null });
                  setTmRoom({ ...r2, gs: newGs });
                  setScreen("teamGame");
                  setInn2Starting(false);
                  const ba = newGs.battingTeam === "A" ? r2.teamA : r2.teamB;
                  const bo = newGs.battingTeam === "A" ? r2.teamB : r2.teamA;
                  const nb = ba[newGs.batterIdx % ba.length];
                  const nbl = bo[newGs.bowlerIdx % bo.length];
                  if (MY_PID === nb || MY_PID === nbl) startTimer();
                }
              }} disabled={inn2ReadyCount > 0} style={{ ...solidBtn(CLR.orange), animation: "popIn 0.3s ease", opacity: inn2ReadyCount > 0 ? 0.6 : 1 }}>
                {inn2ReadyCount > 0 ? "⏳ Waiting for others…" : "🏏 READY FOR 2ND INNINGS →"}
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", padding: "12px 0", color: "rgba(255,255,255,0.40)", fontSize: "clamp(10px,2.8vw,12px)" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: CLR.orange, animation: "blink 1s infinite" }} />
                Please watch the ad, then 2nd innings button will appear…
              </div>
            )}
            <Footer />
          </div>
        </div></>
    );
  }

  // ── TEAM RESULT ────────────────────────────────────────
  if (screen === "teamResult" && tmRoom?.gs && tmRoom.teamA && !inn2Starting) {
    const tgs = tmRoom.gs;
    const isRedTeam = (tmRoom.teamA || []).includes(MY_PID); // teamA = RED
    const myTmCol = isRedTeam ? TM.red : TM.blue;
    const oppTmCol = isRedTeam ? TM.blue : TM.red;
    const myWinner = isRedTeam ? "A" : "B";
    const won = tgs.winner === myWinner;
    const isDraw = tgs.winner === "draw";
    const rc = isDraw ? CLR.draw : won ? CLR.win : CLR.lose;
    const myScore = isRedTeam ? tgs.teamAScore : tgs.teamBScore;
    const oppScore = isRedTeam ? tgs.teamBScore : tgs.teamAScore;
    const myNames = (isRedTeam ? tmRoom.teamA : tmRoom.teamB).map(pid => tmRoom.players[pid]?.name || "?").join(", ");
    const oppNames = (isRedTeam ? tmRoom.teamB : tmRoom.teamA).map(pid => tmRoom.players[pid]?.name || "?").join(", ");

    // ── MAN OF THE MATCH ──────────────────────────────────
    // Score = runs scored + (wickets × 15) - economy penalty
    // Gives equal weight to good bowling performances
    const allHist = [...(Array.isArray(tgs.inn1hist) ? tgs.inn1hist : []), ...(Array.isArray(tgs.hist) ? tgs.hist : [])];
    const runsPerPlayer = {};
    const wicketsPerPlayer = {};
    const runsConcededPerPlayer = {};
    const ballsBowledPerPlayer = {};
    allHist.forEach(ball => {
      if (ball.batterPid) {
        if (!ball.isOut) runsPerPlayer[ball.batterPid] = (runsPerPlayer[ball.batterPid] || 0) + (ball.runs || 0);
      }
      if (ball.bowlerPid) {
        if (ball.isOut) wicketsPerPlayer[ball.bowlerPid] = (wicketsPerPlayer[ball.bowlerPid] || 0) + 1;
        runsConcededPerPlayer[ball.bowlerPid] = (runsConcededPerPlayer[ball.bowlerPid] || 0) + (ball.runs || 0);
        ballsBowledPerPlayer[ball.bowlerPid] = (ballsBowledPerPlayer[ball.bowlerPid] || 0) + 1;
      }
    });
    // MOTM score = runs + (wickets × 15) + economy bonus (lower economy = better)
    const motmScore = (pid) => {
      const runs = runsPerPlayer[pid] || 0;
      const wkts = wicketsPerPlayer[pid] || 0;
      const balls = ballsBowledPerPlayer[pid] || 0;
      const econ = balls > 0 ? ((runsConcededPerPlayer[pid] || 0) / balls) * 6 : 6;
      const econBonus = Math.max(0, (6 - econ) * 3); // bonus for low economy
      return runs + (wkts * 15) + econBonus;
    };
    const winTeamPids = isDraw ? [...(tmRoom.teamA||[]), ...(tmRoom.teamB||[])] : (tgs.winner === "A" ? tmRoom.teamA : tmRoom.teamB);
    let motmPid = null, motmBestScore = -1;
    winTeamPids.forEach(pid => {
      const s = motmScore(pid);
      if (s > motmBestScore) { motmBestScore = s; motmPid = pid; }
    });
    const motmRuns = runsPerPlayer[motmPid] || 0;
    const motmWkts = wicketsPerPlayer[motmPid] || 0;
    const motmName = motmPid ? (tmRoom.players[motmPid]?.name || "?") : null;
    const motmTeam = motmPid ? ((tmRoom.teamA || []).includes(motmPid) ? TM.red : TM.blue) : null;

    // ── SCORE TABLE DATA ──────────────────────────────────
    // Build per-player batting AND bowling stats for each innings
    const buildInningsTable = (hist, teamA, teamB) => {
      const batting = {};
      const bowling = {};
      [...teamA, ...teamB].forEach(pid => {
        batting[pid] = { runs: 0, balls: 0, outs: 0, fours: 0, sixes: 0 };
        bowling[pid] = { runs: 0, balls: 0, wkts: 0 };
      });
      hist.forEach(ball => {
        if (ball.batterPid) {
          if (!batting[ball.batterPid]) batting[ball.batterPid] = { runs: 0, balls: 0, outs: 0, fours: 0, sixes: 0 };
          batting[ball.batterPid].balls += 1;
          if (!ball.isOut) {
            batting[ball.batterPid].runs += (ball.runs || 0);
            if (ball.runs === 4) batting[ball.batterPid].fours += 1;
            if (ball.runs === 6) batting[ball.batterPid].sixes += 1;
          } else batting[ball.batterPid].outs += 1;
        }
        if (ball.bowlerPid) {
          if (!bowling[ball.bowlerPid]) bowling[ball.bowlerPid] = { runs: 0, balls: 0, wkts: 0 };
          bowling[ball.bowlerPid].balls += 1;
          bowling[ball.bowlerPid].runs += (ball.runs || 0);
          if (ball.isOut) bowling[ball.bowlerPid].wkts += 1;
        }
      });
      return { batting, bowling };
    };
    const inn1Table = buildInningsTable(tgs.inn1hist || [], tmRoom.teamA, tmRoom.teamB);
    const inn2Table = buildInningsTable(tgs.hist || [], tmRoom.teamA, tmRoom.teamB);
    const inn1Scores = inn1Table.batting;
    const inn2Scores = inn2Table.batting;
    const inn1BowlScores = inn1Table.bowling;
    const inn2BowlScores = inn2Table.bowling;
    const inn1BattingTeam = tgs.inn1hist?.length > 0
      ? (tgs.inn1hist[0].batterPid && tmRoom.teamA.includes(tgs.inn1hist[0].batterPid) ? "A" : "B")
      : null;
    const inn2BattingTeam = inn1BattingTeam === "A" ? "B" : "A";

    const ScoreTable = ({ label, hist, battingTeam, teamA, teamB, scores, bowlScores }) => {
      const battArr = battingTeam === "A" ? teamA : teamB;
      const bowlArr = battingTeam === "A" ? teamB : teamA;
      const battTm = battingTeam === "A" ? TM.red : TM.blue;
      const bowlTm = battingTeam === "A" ? TM.blue : TM.red;
      const totalRuns = battingTeam === "A" ? tgs.teamAScore : tgs.teamBScore;
      // For 2nd innings, use hist directly; for 1st innings, use inn1hist
      const relevantHist = hist;
      const wkts = relevantHist.filter(b => b.isOut).length;
      const balls = relevantHist.length;
      const overs = `${Math.floor(balls / 6)}.${balls % 6}`;

      return (
        <div style={{ marginBottom: 14, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ color: battTm.color, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,13px)", letterSpacing: 2 }}>
              {battTm.emoji} {battTm.label} INNINGS {label}
            </div>
            <div style={{ color: CLR.textDim, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,3vw,12px)", letterSpacing: 1 }}>
              {totalRuns}/{wkts} ({overs} ov)
            </div>
          </div>
          <div style={{ background: "rgba(0,0,0,0.22)", borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
            {/* Batting Header: BATTER | R | B | 4s | 6s | SR */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 28px 28px 28px 28px 36px", gap: 3, padding: "5px 10px", background: "rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.09)" }}>
              {[["BATTER", "left"], ["R", "center"], ["B", "center"], ["4s", "center"], ["6s", "center"], ["SR", "center"]].map(([h, align]) => (
                <div key={h} style={{ color: "rgba(255,255,255,0.50)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,9px)", letterSpacing: 2, textAlign: align }}>{h}</div>
              ))}
            </div>
            {/* Batting Rows */}
            {battArr.map(pid => {
              const ps = scores[pid] || { runs: 0, balls: 0, outs: 0, fours: 0, sixes: 0 };
              const sr = ps.balls > 0 ? ((ps.runs / ps.balls) * 100).toFixed(0) : "—";
              const isOut = ps.outs > 0;
              const isMOTM = pid === motmPid;
              return (
                <div key={pid} style={{ display: "grid", gridTemplateColumns: "1fr 28px 28px 28px 28px 36px", gap: 3, padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.05)", background: isMOTM ? "rgba(245,197,24,0.08)" : "transparent" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: isOut ? CLR.coral : CLR.green, boxShadow: isOut ? `0 0 4px ${CLR.coral}` : `0 0 4px ${CLR.green}` }} />
                    <span style={{ color: "#ffffff", fontSize: "clamp(9px,2.5vw,11px)", fontWeight: 700, letterSpacing: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tmRoom.players[pid]?.name || "?"}</span>
                    {isOut && <span style={{ background: "rgba(255,95,87,0.20)", border: "1px solid rgba(255,95,87,0.45)", color: CLR.coral, fontSize: "clamp(6px,1.6vw,7px)", fontFamily: "'Bebas Neue',serif", letterSpacing: 1, padding: "1px 4px", borderRadius: 3, flexShrink: 0 }}>OUT</span>}
                    {isMOTM && <span style={{ color: CLR.gold, fontSize: "clamp(8px,2.2vw,10px)", flexShrink: 0 }}>⭐</span>}
                  </div>
                  {/* R */}
                  <div style={{ color: ps.runs >= 6 ? CLR.gold : "#f0f0f0", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(11px,3vw,13px)", textAlign: "center", fontWeight: 900 }}>{ps.runs}</div>
                  {/* B */}
                  <div style={{ color: "rgba(255,255,255,0.70)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,2.8vw,12px)", textAlign: "center" }}>{ps.balls}</div>
                  {/* 4s — sky tint */}
                  <div style={{ color: ps.fours > 0 ? CLR.sky : "rgba(255,255,255,0.35)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,2.8vw,12px)", textAlign: "center", fontWeight: ps.fours > 0 ? 700 : 400 }}>{ps.fours}</div>
                  {/* 6s — gold tint */}
                  <div style={{ color: ps.sixes > 0 ? CLR.gold : "rgba(255,255,255,0.35)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,2.8vw,12px)", textAlign: "center", fontWeight: ps.sixes > 0 ? 700 : 400 }}>{ps.sixes}</div>
                  {/* SR */}
                  <div style={{ color: CLR.sky, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(9px,2.5vw,11px)", textAlign: "center" }}>{sr}</div>
                </div>
              );
            })}
            {/* Bowling section: BOWLER | W | R | OV | ECON */}
            <div style={{ padding: "6px 10px 8px", borderTop: "1.5px solid rgba(255,255,255,0.09)", background: "rgba(0,0,0,0.15)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 28px 28px 36px 44px", gap: 3, padding: "3px 0 5px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {[["BOWLER", "left"], ["W", "center"], ["R", "center"], ["OV", "center"], ["ECON", "center"]].map(([h, align]) => (
                  <div key={h} style={{ color: "rgba(255,255,255,0.45)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(7px,2vw,8px)", letterSpacing: 2, textAlign: align }}>{h}</div>
                ))}
              </div>
              {bowlArr.map(pid => {
                const bs = bowlScores[pid] || { runs: 0, balls: 0, wkts: 0 };
                if (bs.balls === 0) return null;
                const ovsNum = bs.balls / 6;
                const ovsStr = `${Math.floor(bs.balls / 6)}.${bs.balls % 6}`;
                const econ = ovsNum > 0 ? (bs.runs / ovsNum).toFixed(1) : "—";
                // Economy colour: green ≤6, orange ≤9, coral >9
                const econColor = ovsNum === 0 ? "rgba(255,255,255,0.55)"
                  : bs.runs / ovsNum <= 6 ? CLR.green
                    : bs.runs / ovsNum <= 9 ? CLR.orange
                      : CLR.coral;
                return (
                  <div key={pid} style={{ display: "grid", gridTemplateColumns: "1fr 28px 28px 36px 44px", gap: 3, padding: "5px 0", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ color: "#ffffff", fontSize: "clamp(9px,2.5vw,11px)", fontWeight: 700, letterSpacing: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tmRoom.players[pid]?.name || "?"}</span>
                    {/* W */}
                    <span style={{ color: bs.wkts > 0 ? CLR.green : "rgba(255,255,255,0.55)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(11px,3vw,13px)", textAlign: "center", fontWeight: 900 }}>{bs.wkts}</span>
                    {/* R */}
                    <span style={{ color: "rgba(255,255,255,0.70)", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(10px,2.8vw,12px)", textAlign: "center" }}>{bs.runs}</span>
                    {/* OV */}
                    <span style={{ color: CLR.sky, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(9px,2.5vw,11px)", textAlign: "center" }}>{ovsStr}</span>
                    {/* ECON — colour-coded */}
                    <span style={{ color: econColor, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(9px,2.5vw,11px)", textAlign: "center", fontWeight: 700 }}>{econ}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    };

    return (
      <><style>{CSS}</style><CricketBg accent={rc} />
        {showPromo && <PromoPopup onClose={() => { setShowPromo(false); startMenuLockAfterAd(); }} />}
        {/* Rematch request banner — shown to non-requester players, sits above result without hiding it */}
        {rematchReq && rematchReq.requestedBy !== MY_PID && !rematchReq.responses?.[MY_PID] && (
          <RematchBanner
            requesterName={tmRoom.players?.[rematchReq.requestedBy]?.name || "Someone"}
            onYes={async () => {
              setRematchReq(null);
              await updateTeam(tmCodeRef.current, { [`rematchRequest/responses/${MY_PID}`]: "yes" });
            }}
            onNo={async () => {
              setRematchReq(null);
              // Write both my response AND expiredAt flag so requester's poll knows timer ran out
              await updateTeam(tmCodeRef.current, {
                [`rematchRequest/responses/${MY_PID}`]: "no",
                "rematchRequest/expiredAt": true,
              });
            }}
          />
        )}
        {/* Requester's live response tracker */}
        {rematchStatus === "waiting" && rematchReq?.requestedBy === MY_PID && (
          <RematchWaiting
            responses={rematchReq.responses}
            players={tmRoom.players}
            onCancel={async () => {
              setRematchReq(null); setRematchStatus(null);
              await updateTeam(tmCodeRef.current, { rematchRequest: null });
            }}
          />
        )}
        {/* Declined notification — auto-dismisses */}
        {rematchStatus === "declined" && (
          <div style={{
            position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
            zIndex: 1100, padding: "12px 20px", borderRadius: 14,
            background: "linear-gradient(135deg,#2d1a1a,#1a0f0f)",
            border: "1.5px solid rgba(255,95,87,0.45)", color: CLR.coral,
            fontFamily: "'Bebas Neue',serif", fontSize: "clamp(11px,3.5vw,14px)", letterSpacing: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)", animation: "slideDown 0.3s ease",
            display: "flex", alignItems: "center", gap: 10,
            width: "calc(100% - 32px)", maxWidth: 380
          }}>
            <span style={{ flex: 1 }}>😔 No one accepted the rematch</span>
            <button onClick={() => setRematchStatus(null)} style={{
              background: "none", border: "none", color: "rgba(255,255,255,0.4)",
              cursor: "pointer", fontSize: 16, padding: "0 4px", touchAction: "manipulation"
            }}>✕</button>
          </div>
        )}
        <div style={scrollWrap}>
          <div style={{ ...panel(430, rc), textAlign: "center", width: "100%" }}>
            <div style={{ fontSize: "clamp(52px,16vw,70px)", marginBottom: 8, animation: "popIn 0.6s ease" }}>{isDraw ? "🤝" : won ? "🏆" : "💀"}</div>
            <h2 style={{ fontFamily: "'Bebas Neue',serif", fontSize: "clamp(30px,10vw,44px)", color: rc, letterSpacing: "clamp(3px,1.2vw,6px)", marginBottom: 5 }}>
              {isDraw ? "TIE GAME!" : won ? "TEAM WIN!" : "TEAM LOSS"}
            </h2>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 16, padding: "3px 12px", borderRadius: 20, background: `${CLR.orange}18`, border: `1.5px solid ${CLR.orange}35`, color: CLR.orange, fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, fontFamily: "'Bebas Neue',serif" }}>
              🏟️ {tgs.overs} OVERS · TEAM MATCH
            </div>

            {/* Scores */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[{ t: myTmCol, s: myScore, names: myNames, mine: true }, { t: oppTmCol, s: oppScore, names: oppNames, mine: false }].map(({ t, s, names, mine }) => (
                <div key={t.id} style={{ flex: 1, padding: "clamp(10px,3vw,14px) 8px", background: "rgba(0,0,0,0.20)", border: `1.5px solid ${t.border}`, borderRadius: 12 }}>
                  <div style={{ color: t.color, fontSize: "clamp(7px,2vw,8px)", letterSpacing: 2, marginBottom: 2, fontFamily: "'Bebas Neue',serif" }}>{t.emoji} {t.label}{mine ? " (YOU)" : ""}</div>
                  <div style={{ color: mine && won && !isDraw ? CLR.win : !mine && !won && !isDraw ? CLR.lose : "#fff", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(36px,11vw,48px)", lineHeight: 1 }}>{s}</div>
                  <div style={{ color: CLR.textFaint, fontSize: "clamp(7px,2vw,8px)", marginTop: 4, lineHeight: 1.4 }}>{names}</div>
                </div>
              ))}
            </div>

            {/* Man of the Match */}
            {motmName && (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 12, background: "rgba(245,197,24,0.10)", border: "1.5px solid rgba(245,197,24,0.30)", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: "clamp(24px,7vw,32px)", flexShrink: 0 }}>⭐</div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ color: CLR.gold, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, marginBottom: 2 }}>MAN OF THE MATCH</div>
                  <div style={{ color: "#fff", fontFamily: "'Bebas Neue',serif", fontSize: "clamp(16px,5vw,22px)", letterSpacing: 1, lineHeight: 1.1 }}>
                    {motmName}
                    {motmTeam && <span style={{ color: motmTeam.color, fontSize: "clamp(10px,3vw,13px)", marginLeft: 6 }}>{motmTeam.emoji} {motmTeam.label}</span>}
                  </div>
                  <div style={{ color: CLR.textDim, fontSize: "clamp(9px,2.5vw,11px)", marginTop: 1 }}>
                    {motmRuns} runs{motmWkts > 0 ? ` · ${motmWkts} wicket${motmWkts > 1 ? "s" : ""}` : ""}
                  </div>
                </div>
              </div>
            )}

            {/* Score Tables — innings breakdown */}
            {inn1BattingTeam && (
              <div style={{ textAlign: "left", marginBottom: 4 }}>
                <div style={{ color: CLR.textFaint, fontFamily: "'Bebas Neue',serif", fontSize: "clamp(8px,2.2vw,10px)", letterSpacing: 3, marginBottom: 8 }}>📊 SCORECARD</div>
                <ScoreTable label="1" hist={tgs.inn1hist || []} battingTeam={inn1BattingTeam} teamA={tmRoom.teamA} teamB={tmRoom.teamB} scores={inn1Scores} bowlScores={inn1BowlScores} />
                <ScoreTable label="2" hist={tgs.hist || []} battingTeam={inn2BattingTeam} teamA={tmRoom.teamA} teamB={tmRoom.teamB} scores={inn2Scores} bowlScores={inn2BowlScores} />
              </div>
            )}

            {/* Promo Banner */}
            <PromoBanner />

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => runNavOnce(async () => {
                // Send a rematch REQUEST — non-blocking, others get a banner to accept/decline
                const code = tmCodeRef.current;
                const room = await loadTeam(code);
                if (!room) { setTimeout(() => setScreen("teamSetup"), 0); return; }
                const allActivePids = Object.keys(room.players || {}).filter(pid => room.players[pid].active !== false);
                // Build initial responses — requester is auto-yes, everyone else is pending (null)
                const initResponses = {};
                allActivePids.forEach(pid => { initResponses[pid] = pid === MY_PID ? "yes" : null; });
                const req = { requestedBy: MY_PID, requestedAt: Date.now(), responses: initResponses };
                await updateTeam(code, { rematchRequest: req });
                setRematchReq(req);
                setRematchStatus("waiting");
              })} style={solidBtn(CLR.orange)}>🔁 PLAY AGAIN</button>
              <button onClick={() => {
                // PREMIUM TEAM SCORECARD — vertically stacked for clarity
                const c = document.createElement("canvas");
                const players = tmRoom.players;

                const teamA = tmRoom.teamA;
                const teamB = tmRoom.teamB;

                // Pre-calculate stats for both innings
                const getStats = (hist) => {
                  const b = {}; const bw = {};
                  [...teamA, ...teamB].forEach(p => { b[p] = { r: 0, b: 0, o: 0, f: 0, s: 0 }; bw[p] = { r: 0, b: 0, w: 0 }; });
                  hist.forEach(e => {
                    if (e.batterPid) { b[e.batterPid].b++; if (!e.isOut) { b[e.batterPid].r += e.runs; if (e.runs === 4) b[e.batterPid].f++; if (e.runs === 6) b[e.batterPid].s++; } else b[e.batterPid].o++; }
                    if (e.bowlerPid) { bw[e.bowlerPid].b++; bw[e.bowlerPid].r += e.runs; if (e.isOut) bw[e.bowlerPid].w++; }
                  });
                  return { b, bw };
                };
                const s1 = getStats(tgs.inn1hist || []);
                const s2 = getStats(tgs.hist || []);
                const r1 = tgs.inn1hist?.filter(e => e.isOut).length || 0;
                const r2 = tgs.hist?.filter(e => e.isOut).length || 0;

                // Dynamic Height Calculation
                const rowH = 24;
                const innH = (label, battArr, bowlArr, stats) => {
                  let h = 40; // label + score
                  h += 22; // header
                  h += battArr.length * rowH;
                  h += 22; // bowl header
                  const activeBowlers = bowlArr.filter(p => stats.bw[p].b > 0).length;
                  h += activeBowlers * rowH;
                  return h + 25; // padding
                };
                const h1 = inn1BattingTeam ? innH("1", (inn1BattingTeam === "A" ? teamA : teamB), (inn1BattingTeam === "A" ? teamB : teamA), s1) : 0;
                const h2 = inn2BattingTeam ? innH("2", (inn2BattingTeam === "A" ? teamA : teamB), (inn2BattingTeam === "A" ? teamB : teamA), s2) : 0;
                const H = 410 + (motmName ? 85 : 0) + h1 + h2 + 60;
                c.width = 600; c.height = H;
                const ctx = c.getContext("2d");

                // 1. BACKGROUND
                const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
                bgGrad.addColorStop(0, "#1a4a28"); bgGrad.addColorStop(0.4, "#0f2d1a"); bgGrad.addColorStop(1, "#0d2415");
                ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, 600, H);
                // Subtle horizontal lawn stripes
                ctx.fillStyle = "rgba(255,255,255,0.015)";
                for (let i = 0; i < H; i += 60) ctx.fillRect(0, i, 600, 30);

                let y = 65;
                // 2. HEADER
                ctx.shadowBlur = 30; ctx.shadowColor = rc;
                ctx.font = "80px serif"; ctx.textAlign = "center";
                ctx.fillText(isDraw ? "🤝" : won ? "🏆" : "💀", 300, y);
                ctx.shadowBlur = 0; y += 70;

                ctx.font = "900 62px Arial"; ctx.fillStyle = rc;
                ctx.fillText(isDraw ? "TIE GAME!" : won ? "VICTORY!" : "TEAM LOSS", 300, y); y += 35;
                ctx.font = "700 13px Arial"; ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.letterSpacing = "4px";
                ctx.fillText(`${tgs.overs} OVERS · TEAM MATCH`, 300, y); ctx.letterSpacing = "0px"; y += 45;

                // 3. SCORE BOXES
                const drawScoreBox = (x, y, w, h, team, score, label, isYou) => {
                  ctx.strokeStyle = team.border; ctx.lineWidth = 2;
                  ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.beginPath(); ctx.roundRect(x, y, w, h, 16); ctx.fill(); ctx.stroke();
                  ctx.fillStyle = team.color; ctx.font = "bold 11px Arial"; ctx.textAlign = "center";
                  ctx.fillText(`${team.label}${isYou ? " [YOU]" : ""}`, x + w / 2, y + 22);
                  ctx.fillStyle = "#fff"; ctx.font = "900 52px Arial";
                  ctx.fillText(String(score), x + w / 2, y + 74);
                  ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.font = "11px Arial";
                  ctx.fillText("rs", x + w / 2, y + 92);
                };
                drawScoreBox(30, y, 260, 110, myTmCol, myScore, myTmCol.label, true);
                drawScoreBox(310, y, 260, 110, oppTmCol, oppScore, oppTmCol.label, false);
                y += 145;

                // 4. MAN OF THE MATCH
                if (motmName) {
                  ctx.fillStyle = "rgba(245,197,24,0.08)"; ctx.strokeStyle = "rgba(245,197,24,0.4)"; ctx.lineWidth = 1.5;
                  ctx.beginPath(); ctx.roundRect(30, y, 540, 80, 14); ctx.fill(); ctx.stroke();
                  ctx.font = "40px Arial"; ctx.textAlign = "left";
                  ctx.fillText("⭐", 48, y + 54);
                  ctx.font = "bold 11px Arial"; ctx.fillStyle = CLR.gold; ctx.letterSpacing = "2px";
                  ctx.fillText("MAN OF THE MATCH", 95, y + 28); ctx.letterSpacing = "0px";
                  ctx.font = "bold 26px Arial"; ctx.fillStyle = "#fff";
                  ctx.fillText(motmName, 95, y + 56);
                  // team dot
                  ctx.fillStyle = motmTeam.color; ctx.beginPath(); ctx.arc(95 + ctx.measureText(motmName).width + 15, y + 46, 6, 0, Math.PI * 2); ctx.fill();
                  ctx.font = "700 12px Arial"; ctx.fillStyle = "rgba(255,255,255,0.5)";
                  ctx.fillText(`${motmRuns} runs · ${motmWkts} wicket${motmWkts !== 1 ? "s" : ""}`, 95, y + 72);
                  y += 115;
                }

                // 5. SCORECARD HEADER
                ctx.font = "bold 12px Arial"; ctx.fillStyle = "rgba(255,255,255,0.3)"; ctx.textAlign = "left"; ctx.letterSpacing = "3px";
                ctx.fillText("📊 SCORECARD", 30, y); ctx.letterSpacing = "0px"; y += 22;

                const drawInning = (x, startY, label, teamId, hist, stats) => {
                  const tm = teamId === "A" ? TM.red : TM.blue;
                  const bArr = teamId === "A" ? teamA : teamB;
                  const bwArr = teamId === "A" ? teamB : teamA;
                  const score = teamId === "A" ? (label === "1" ? (tgs.inn1hist?.length ? tgs.teamAScore : 0) : tgs.teamAScore) : (label === "1" ? (tgs.inn1hist?.length ? tgs.teamBScore : 0) : tgs.teamBScore);
                  const wkts = hist.filter(e => e.isOut).length;
                  const ovs = `${Math.floor(hist.length / 6)}.${hist.length % 6}`;

                  let curY = startY;
                  // Inning bar
                  ctx.fillStyle = tm.color; ctx.beginPath(); ctx.arc(x + 10, curY - 5, 5, 0, Math.PI * 2); ctx.fill();
                  ctx.font = "bold 15px Arial"; ctx.fillStyle = tm.color;
                  ctx.fillText(`${tm.label} INNINGS ${label}`, x + 25, curY);
                  ctx.textAlign = "right"; ctx.font = "bold 14px Arial"; ctx.fillStyle = "rgba(255,255,255,0.7)";
                  ctx.fillText(`${score}/${wkts} [${ovs} OV]`, 570, curY); ctx.textAlign = "left";
                  curY += 22;

                  // TABLE HEADER
                  ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.beginPath(); ctx.roundRect(30, curY, 540, 22, 4); ctx.fill();
                  ctx.font = "bold 9px Arial"; ctx.fillStyle = "rgba(255,255,255,0.4)";
                  ctx.fillText("BATTER", 40, curY + 14);
                  ctx.textAlign = "right";
                  [["R", 360], ["B", 400], ["4s", 440], ["6s", 480], ["SR", 540]].forEach(([h, tx]) => ctx.fillText(h, tx, curY + 14));
                  ctx.textAlign = "left"; curY += 24;

                  // BATTING ROWS
                  bArr.forEach((pid, i) => {
                    const s = stats.b[pid];
                    const sr = s.b > 0 ? ((s.r / s.b) * 100).toFixed(0) : "0";
                    if (i % 2 === 0) { ctx.fillStyle = "rgba(255,255,255,0.03)"; ctx.fillRect(30, curY - 2, 540, rowH); }
                    ctx.fillStyle = s.o > 0 ? "#ff5f57" : "#fff"; ctx.font = "bold 12px Arial";
                    ctx.beginPath(); ctx.arc(42, curY + 8, 3, 0, Math.PI * 2); ctx.fill();
                    ctx.fillText(players[pid]?.name || "?", 55, curY + 14);
                    if (s.o > 0) {
                      ctx.fillStyle = "rgba(255,95,87,0.2)"; ctx.beginPath(); ctx.roundRect(55 + ctx.measureText(players[pid]?.name).width + 8, curY + 2, 32, 16, 4); ctx.fill();
                      ctx.fillStyle = "#ff5f57"; ctx.font = "bold 8px Arial"; ctx.fillText("OUT", 55 + ctx.measureText(players[pid]?.name).width + 16, curY + 13);
                    }
                    if (pid === motmPid) ctx.fillText("⭐", 55 + ctx.measureText(players[pid]?.name).width + (s.o > 0 ? 46 : 8), curY + 14);
                    ctx.textAlign = "right"; ctx.font = "bold 13px Arial"; ctx.fillStyle = s.r >= 10 ? CLR.gold : "#fff";
                    ctx.fillText(String(s.r), 360, curY + 14);
                    ctx.font = "11px Arial"; ctx.fillStyle = "rgba(255,255,255,0.6)";
                    [[s.b, 400], [s.f, 440], [s.s, 480]].forEach(([v, tx]) => ctx.fillText(String(v), tx, curY + 14));
                    ctx.fillStyle = CLR.sky; ctx.fillText(sr, 540, curY + 14);
                    ctx.textAlign = "left"; curY += rowH;
                  });
                  curY += 8;

                  // BOWLING
                  ctx.fillStyle = "rgba(0,0,0,0.1)"; ctx.beginPath(); ctx.roundRect(30, curY, 540, 22, 4); ctx.fill();
                  ctx.font = "bold 9px Arial"; ctx.fillStyle = "rgba(255,255,255,0.4)";
                  ctx.fillText("BOWLER", 40, curY + 14);
                  ctx.textAlign = "right";
                  [["W", 360], ["R", 400], ["OV", 460], ["ECON", 540]].forEach(([h, tx]) => ctx.fillText(h, tx, curY + 14));
                  ctx.textAlign = "left"; curY += 24;

                  bwArr.filter(p => stats.bw[p].b > 0).forEach(pid => {
                    const s = stats.bw[pid];
                    const ov = `${Math.floor(s.b / 6)}.${s.b % 6}`;
                    const eco = s.b > 0 ? ((s.r / s.b) * 6).toFixed(1) : "0.0";
                    ctx.font = "bold 12px Arial"; ctx.fillStyle = "#fff";
                    ctx.fillText(players[pid]?.name || "?", 55, curY + 14);
                    ctx.textAlign = "right"; ctx.font = "bold 13px Arial"; ctx.fillStyle = s.w > 0 ? CLR.green : "#fff";
                    ctx.fillText(String(s.w), 360, curY + 14);
                    ctx.font = "11px Arial"; ctx.fillStyle = "rgba(255,255,255,0.6)";
                    [[s.r, 400], [ov, 460]].forEach(([v, tx]) => ctx.fillText(String(v), tx, curY + 14));
                    ctx.fillStyle = eco <= 6 ? CLR.green : eco <= 9 ? CLR.orange : CLR.coral;
                    ctx.fillText(eco, 540, curY + 14);
                    ctx.textAlign = "left"; curY += rowH;
                  });
                  return curY + 20;
                };

                if (inn1BattingTeam) y = drawInning(30, y, "1", inn1BattingTeam, tgs.inn1hist || [], s1);
                if (inn2BattingTeam) y = drawInning(30, y, "2", inn2BattingTeam, tgs.hist || [], s2);

                // FOOTER
                ctx.fillStyle = "rgba(255,255,255,0.15)"; ctx.font = "11px Arial"; ctx.textAlign = "center";
                ctx.fillText("hand-cricket-striveon-games.vercel.app · Built by Striveon", 300, H - 25);

                c.toBlob(blob => {
                  shareScorecardBlob(blob, "team-cricket-scorecard.png", "Team Cricket Scorecard");
                }, "image/png");
              }} style={ghostBtn()}>📸 SCREENSHOT RESULT</button>
              <button onClick={() => { if (!menuLocked) runNavOnce(() => resetToMenu()); }} disabled={menuLocked} style={{ ...ghostBtn(), opacity: menuLocked ? 0.4 : 1, cursor: menuLocked ? "not-allowed" : "pointer" }}>{menuLocked ? "← MAIN MENU (wait…)" : "← MAIN MENU"}</button>
            </div>
            <Footer />
          </div>
        </div></>
    );
  }

  return null;
}
